import { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Menu as MenuIcon,
  Brain,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  GitBranch,
  List,
  MoreVertical,
  Phone,
  Plus,
  RotateCcw,
  Check,
  ScrollText,
  Send,
  TrendingUp,
  Video,
  X,
} from "lucide-react";
import { applyLabComment } from "../data/applyLabComment";
import {
  FLOW6_CANON,
  FLOW6_REPLIES,
  FLOW6_STEPS,
  FLOW6_TITLE,
  activitiesSentence,
  activitiesSummary,
  activityReplies,
  blockageShort,
  buildFlow6Snapshot,
  difficultyReplies,
  durationReplies as flow6DurationReplies,
  emptySession,
  flow6DisplayIndex,
  labelForActivity,
  labelForDifficulty,
  labelForTime,
  sessionMinutes,
} from "../data/flow6Activities";
import {
  FLOW4_REPLIES,
  FLOW4_STEPS,
  FLOW4_TITLE,
  buildFlow4Snapshot,
  dayReplies,
  daysSentence,
  emptyWeek,
  flow4DisplayIndex,
  labelForDay,
  orderedDays,
} from "../data/flow4Planning";
import {
  FLOW7_REPLIES,
  FLOW7_STEPS,
  FLOW7_TITLE,
  buildFlow7Snapshot,
  flow7DisplayIndex,
} from "../data/flow7Parent";
import {
  BASE_AVAILABILITY,
  FLOW2_REPLIES,
  FLOW2_STEPS,
  FLOW2_TITLE,
  buildFlow2Snapshot,
  cleanTutorName,
  defaultAvailability,
  pickTutorName,
  slotId,
} from "../data/flow2Welcome";
import {
  DURATION_OPTIONS,
  FLOW3_CANON,
  FLOW3_STEPS,
  FLOW3_TITLE,
  SUBJECT_OPTIONS,
  TYPE_OPTIONS,
  buildFlow3Snapshot,
  dueReply,
  durationLabel,
  durationMinutes,
  durationReplies,
  emptyObjective,
  flow3DisplayIndex,
  RHYTHM_MESSAGE,
  labelForReply,
  mentionReply,
  parseSteps,
  stepsReplies,
  subjectReplies,
  typeReplies,
} from "../data/flow3Objectives";
import { FLOW5_REPLIES, FLOW5_STEPS, FLOW5_TITLE, buildFlow5Snapshot, flow5DisplayIndex, timingFor, tutorRows } from "../data/flow5Sessions";
import {
  EXTRACT_ROWS,
  FLOW8_REPLIES,
  FLOW8_STEPS,
  FLOW8_TITLE,
  MARGIN_ROWS,
  buildFlow8Snapshot,
  flow8DisplayIndex,
  helpSubject,
  repliesFor,
  rowsFor,
  tableFor,
} from "../data/flow8PhotoHelp";
import {
  FLOW9_REPLIES,
  FLOW9_STEPS,
  FLOW9_TITLE,
  buildFlow9Snapshot,
  buildStudyPlans,
  deadlineRows,
  flow9DisplayIndex,
  itemsFor,
  mergeAgenda,
  photoRows,
  planTotals,
} from "../data/flow9Agenda";
import { dayAgendaUrl, matchAgenda, taskAgendaUrl, weekAgendaUrl } from "../data/renderAgenda";
import {
  formulaStepsUrl,
  matchHelpItem,
  modelFormulaUrl,
  phraseExerciseUrl,
  pythagoreExerciseUrl,
  pythagoreFormulaUrl,
  pythagoreSummaryUrl,
  sampleExerciseUrl,
  visualSummaryUrl,
} from "../data/renderHelpSheet";
import { getFlowMessage, nextFlowMessageKey } from "../data/flowMessages";
import { emptyRevisions, FIELD_CTX, loadRevisions, saveRevisions } from "../data/labStore";
import LabCopyStudio, { LabPromptsPanel } from "./LabCopyStudio";
import LabFlowTree from "./LabFlowTree";
import { sendWebReminder } from "../data/webReminders";
import { ARCHIVED_COMMANDS, FEATURES, N1_MENU, isArchivedFlow } from "../config/features";
import InviteChildCard from "./live/InviteChildCard";
import PlanExampleCard from "./live/PlanExampleCard";
import PlanFormCard from "./live/PlanFormCard";
import PlanCard from "./live/PlanCard";
import HelpAskCard from "./live/HelpAskCard";
import HelpCard from "./live/HelpCard";
import WeeklyReportCard from "./live/WeeklyReportCard";
import SimClockPanel from "./live/SimClockPanel";
import { liveApi, matchIntent, setSimForfait, simApi, simClock, simNextReminder, simReminders, simRestore, simSmsToday, simSnapshot, simStats } from "../live/engagementApi";

const RESUME_KEY = "sp.lab.resume.v1";
import WeekPlannerCard from "./live/WeekPlannerCard";
import CaptureCard from "./live/CaptureCard";
import CaptureReviewCard from "./live/CaptureReviewCard";
import ScheduleProposalCard from "./live/ScheduleProposalCard";
import MoveSessionCard from "./live/MoveSessionCard";
import AiHelpTurnCard from "./live/AiHelpTurnCard";
import LoopMap from "./live/LoopMap";
import DemoControls from "./live/DemoControls";
import { MVP_FLOWS } from "../data/flowMvp";
import { FLOW15_STEPS, FLOW15_TITLE, FLOW16_STEPS, FLOW16_TITLE, FLOW17_STEPS, FLOW17_TITLE } from "../data/flowWeek";
import { adminToken, loadPublished, startLabSync } from "../data/labSync";
import PublishPanel from "./live/PublishPanel";
import { SideMenu } from "./live/SideMenu";
import { SessionTimerCard } from "./live/SessionTimerCard";
import { InfoListCard } from "./live/InfoListCard";
import { FeedbackDialog } from "./live/FeedbackDialog";
import { loadSession as readSession } from "../data/session";

const TYPE_LABEL = { exam: "Examen", assignment: "Devoir", practice: "Pratique", plan: "Plan", other: "Travail" };
const shortDay = (iso) => {
  if (!iso) return "Sans date";
  const date = new Date(`${iso}T12:00:00`);
  const otherMonth = date.getMonth() !== simClock.now().getMonth();
  const text = date.toLocaleDateString("fr-CA", { weekday: "short", day: "numeric", ...(otherMonth ? { month: "short" } : {}) });
  return text.charAt(0).toUpperCase() + text.slice(1);
};
// French elision: « d’Émile », « de Luca ».
const ofName = (name) => (/^[aeiouyhàâäéèêëîïôöûü]/i.test(name) ? `d’${name}` : `de ${name}`);
// Parent messages shown as one bubble per paragraph.
const SPLIT_KEYS = new Set(["p1.welcome"]);

const dueRows = (rows) => rows.map((row) => ({
  left: shortDay(row.dueDate),
  right: `${TYPE_LABEL[row.type] || "Travail"} · ${row.subject || row.title}`,
  sub: row.subject && row.title !== (TYPE_LABEL[row.type] || "Travail") ? row.title : "",
}));
const sessionRows = (rows, titles = {}) => [...rows]
  .sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`))
  .map((row) => ({ left: `${shortDay(row.date)} · ${row.time}`, right: row.planTitle || titles[row.planId] || "Séance", sub: row.status === "done" ? "✓ faite" : "" }));

const OPEN_PLAN_STATUSES = ["pending_acceptance", "change_requested", "active"];
import { FLOW11_STEPS, FLOW11_TITLE, FLOW12_STEPS, FLOW12_TITLE } from "../data/flowLot3";
import { FLOW13_STEPS, FLOW13_TITLE, FLOW14_STEPS, FLOW14_TITLE } from "../data/flowHome";
import { PARENT_STEP_TITLES } from "../data/flow1Parent";
import { FLOW10_STEPS, FLOW10_TITLE } from "../data/flow10Propose";
import { apiFetch, loadSession, saveSession } from "../data/session";
import { findFlow, findNode, flowsByAudience, loadFlowBlueprint, saveFlowBlueprint, spineNodes } from "../data/flowBlueprint";
import {
  BuildCard,
  GoalsCard,
  NextGoalsCard,
  ParentSummaryCard,
  ReadyPlanCard,
  SubjectBarsCard,
  TaskListCard,
  WeekPlanCard,
} from "./LabFlowCards";

function nowStamp() {
  const d = new Date();
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

const initialDash = {
  flowId: 2,
  flowStep: 0,
  status: "Accueil",
  sessionsDone: 0,
  sessionsTotal: 0,
  minutes: 0,
  regularity: 0,
  subject: "—",
  view: "eleve",
  planReady: false,
  objective: emptyObjective(),
  session: emptySession(),
  week: emptyWeek(),
};

const TOC = [
  { id: 2, title: FLOW2_TITLE, steps: FLOW2_STEPS },
  { id: 3, title: FLOW3_TITLE, steps: FLOW3_STEPS },
  { id: 4, title: FLOW4_TITLE, steps: FLOW4_STEPS },
  { id: 5, title: FLOW5_TITLE, steps: FLOW5_STEPS },
  { id: 6, title: FLOW6_TITLE, steps: FLOW6_STEPS },
  { id: 7, title: FLOW7_TITLE, steps: FLOW7_STEPS },
  { id: 8, title: FLOW8_TITLE, steps: FLOW8_STEPS },
  { id: 9, title: FLOW9_TITLE, steps: FLOW9_STEPS },
];

function flowTitle(flowId) {
  if (flowId === 1) return "Flow 1 — Onboarding parent";
  if (flowId === 13) return FLOW13_TITLE;
  if (flowId === 14) return FLOW14_TITLE;
  if (flowId === 15) return FLOW15_TITLE;
  if (flowId === 16) return FLOW16_TITLE;
  if (flowId === 17) return FLOW17_TITLE;
  if (MVP_FLOWS[flowId]) return MVP_FLOWS[flowId].title;
  if (flowId === 10) return FLOW10_TITLE;
  if (flowId === 11) return FLOW11_TITLE;
  if (flowId === 12) return FLOW12_TITLE;
  if (flowId === 9) return FLOW9_TITLE;
  if (flowId === 8) return FLOW8_TITLE;
  if (flowId === 7) return FLOW7_TITLE;
  if (flowId === 6) return FLOW6_TITLE;
  if (flowId === 5) return FLOW5_TITLE;
  if (flowId === 4) return FLOW4_TITLE;
  if (flowId === 3) return FLOW3_TITLE;
  return FLOW2_TITLE;
}

const LAB_MENU = [
  ["/accueil", "Accueil"],
  ["/objectifs", "Définir des objectifs"],
  ["/dispos", "Disponibilité"],
  ["/liste", "Liste des objectifs en cours"],
  ["/plan", "Plan de la semaine"],
  ["/debut", "Accompagnement à la tâche"],
  ["/suivi", "Suivi des activités"],
  ["/bilan", "Bilan parent"],
  ["/photo", "Aide photo"],
  ["/agenda", "Agenda et remises"],
].map(([command, label]) => `${command} — ${label}`).join("\n");

function sectionLabel(flowId) {
  return flowTitle(flowId);
}

function InfoCard({ testId, title, subtitle, rows, listLabel, list, time }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="self-start max-w-[92%] overflow-hidden rounded-2xl rounded-tl-sm border border-[#C6EBD7] bg-white shadow-sm"
      data-testid={testId}
    >
      {(title || subtitle) && (
        <div className="bg-[#E9FBF0] px-3 py-2">
          {title && <div className="text-sm font-semibold text-[#122118]">{title}</div>}
          {subtitle && <div className="text-[11px] text-[#4a5f53]">{subtitle}</div>}
        </div>
      )}
      <dl className="space-y-1.5 px-3 py-2.5 text-[12px] text-[#122118]">
        {rows.map(([label, value], index) => (
          <div key={`${label}-${index}`} className="flex gap-2">
            <dt className="w-[5.5rem] shrink-0 text-[#829C8D]">{label}</dt>
            <dd className="font-semibold">{value}</dd>
          </div>
        ))}
        {list?.length ? (
          <div>
            <dt className="text-[#829C8D]">{listLabel}</dt>
            <dd className="mt-1 space-y-0.5 font-semibold">
              {list.map((item) => (
                <div key={item}>– {item}</div>
              ))}
            </dd>
          </div>
        ) : null}
      </dl>
      <div className="px-3 pb-2 text-right text-[10px] text-gray-500">{time}</div>
    </motion.div>
  );
}

function AvailabilityCard({ time, selected, onToggle, note }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="self-start max-w-[92%] overflow-hidden rounded-2xl rounded-tl-sm border border-[#C6EBD7] bg-white shadow-sm"
      data-testid="lab-availability"
    >
      <div className="bg-[#E9FBF0] px-3 py-2 text-sm font-semibold text-[#122118]">
        Disponibilités de base
      </div>
      <div className="space-y-2 px-3 py-2">
        {BASE_AVAILABILITY.map((day) => (
          <div key={day.id}>
            <div className="text-[11px] font-semibold text-[#075E54]">{day.label}</div>
            {day.slots.map((slot) => {
              const id = slotId(day.id, slot);
              const checked = selected.includes(id);
              return (
                <label
                  key={id}
                  className="flex items-center gap-2 py-0.5 text-[12px] text-[#122118]"
                >
                  <input
                    type="checkbox"
                    data-testid={`lab-avail-${id}`}
                    checked={checked}
                    onChange={() => onToggle(id)}
                    className="h-3.5 w-3.5 rounded border-gray-300 text-[#25D366] focus:ring-[#25D366]"
                  />
                  {slot}
                </label>
              );
            })}
          </div>
        ))}
      </div>
      {note ? <p className="px-3 pb-1 text-[11px] text-[#4a5f53]">Indisponible : {note}</p> : null}
      <div className="px-3 pb-2 text-right text-[10px] text-gray-500">{time}</div>
    </motion.div>
  );
}

function TutorNameField({ time, active, onSubmit }) {
  const [value, setValue] = useState("");
  return (
    <motion.form
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      onSubmit={(event) => {
        event.preventDefault();
        if (!active) return;
        onSubmit(value);
      }}
      className="self-start w-[92%] max-w-[92%] rounded-2xl rounded-tl-sm border border-[#C6EBD7] bg-white p-3 shadow-sm"
      data-testid="lab-tutor-name"
    >
      <input
        id="lab-tutor-name-input"
        data-testid="lab-tutor-name-input"
        aria-label="Prénom"
        value={value}
        disabled={!active}
        onChange={(event) => setValue(event.target.value)}
        placeholder="Écris un prénom"
        className="w-full rounded-full border border-gray-200 px-3 py-2 text-[#122118] outline-none focus:border-[#25D366]"
        style={{ fontSize: 16 }}
      />
      <button
        type="submit"
        data-testid="lab-tutor-name-save"
        disabled={!active || !value.trim()}
        className="mt-2 rounded-full bg-[#25D366] px-4 py-2 text-sm font-semibold text-[#0A2914] disabled:opacity-50"
      >
        C’est mon nom
      </button>
      <div className="mt-1 text-right text-[10px] text-gray-500">{time}</div>
    </motion.form>
  );
}

function flow2DisplayIndex(flowStep) {
  if (flowStep === 90) return 0;
  if (flowStep === 91) return 2;
  if (flowStep >= 4) return FLOW2_STEPS.length - 1;
  return Math.min(Math.max(flowStep, 0), FLOW2_STEPS.length - 1);
}

/**
 * flowStep:
 * 0 welcome (buttons)
 * 1 role shown (button Super j'ai compris)
 * 2 ready (buttons)
 * 3 availability
 * 4 tutor name
 * 5 baptism, then flow 3
 * 90 postponed
 * 91 asking question (free text then back to ready)
 */
const DynamicTestLab = ({
  open,
  onClose,
  studentName = "Émile",
  parentName = "Sophie",
  variant = "lab",
  initialRole = "eleve",
  onLogout,
  onOpenSettings,
  availabilitySeed = null,
  scheduleNote = "",
  onAvailabilityCommit,
  onSnapshot,
  onImmersive,
  tutorName = "",
  onTutorName,
  accountToken = "",
  accountForfait = 1,
  accountOnboarded = false,
  accountId = "",
  studentId = "",
  hasStudent = true,
  onStudentInvited,
}) => {
  const seedAvailability = () =>
    Array.isArray(availabilitySeed) && availabilitySeed.length
      ? availabilitySeed
      : defaultAvailability();
  const appMode = variant === "app";
  const [showArchived, setShowArchived] = useState(false);
  const levelOne = appMode || !showArchived;
  // The simulator lists every flow (hidden ones carry a badge); real accounts only see active ones.
  const visibleFlow = (flow) => !appMode || !isArchivedFlow(flow.id);
  const [messages, setMessages] = useState([]);
  const [quickReplies, setQuickReplies] = useState([]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [dash, setDash] = useState(initialDash);
  const [phonePanel, setPhonePanel] = useState("");
  const [immersive, setImmersive] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const [frameBox, setFrameBox] = useState({ top: 0, left: 0, width: "100vw", height: "100dvh" });
  useEffect(() => {
    if (typeof onSnapshot !== "function") return undefined;
    if (!dash.objective?.subject && !dash.planReady && !dash.sessionsDone) return undefined;
    const timer = setTimeout(() => {
      onSnapshot({
        objective: dash.objective,
        week: dash.week,
        session: dash.session,
        planReady: dash.planReady,
        sessionsDone: dash.sessionsDone,
        minutes: dash.minutes,
      });
    }, 700);
    return () => clearTimeout(timer);
  }, [
    dash.objective,
    dash.week,
    dash.session,
    dash.planReady,
    dash.sessionsDone,
    dash.minutes,
    onSnapshot,
  ]);
  const [role, setRole] = useState("eleve");
  const [parentMessages, setParentMessages] = useState([]);
  const [parentReplies, setParentReplies] = useState([]);
  const parentStarted = useRef(false);
  const parentChildRef = useRef(null);
  const parentKidsRef = useRef([]);
  const parentRunRef = useRef(0);
  const linkHandledRef = useRef(false);
  const aiHelpRef = useRef(null);
  const [loopMapOpen, setLoopMapOpen] = useState(false);
  const [simForfait, setSimForfaitState] = useState(3);
  const studentRunRef = useRef(0);
  const apiCacheRef = useRef({ key: "", api: null });
  const [log, setLog] = useState([]);
  const [tocOpen, setTocOpen] = useState(false);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [treeOpen, setTreeOpen] = useState(false);
  const [blueprint, setBlueprint] = useState(loadFlowBlueprint);
  const [wire, setWire] = useState(null);
  const [pendingKeys, setPendingKeys] = useState([]);
  const [revisions, setRevisions] = useState(() => (variant === "app" ? emptyRevisions() : loadRevisions()));
  const lot3DoneRef = useRef({});
  const freeChatResumeRef = useRef([]);
  const onboardedRef = useRef(accountOnboarded);
  const availFromMenuRef = useRef(false);
  useEffect(() => {
    if (accountOnboarded) onboardedRef.current = true;
  }, [accountOnboarded]);
  const [clockNow, setClockNow] = useState(() => simClock.now());
  const smsRef = useRef({ day: "", count: 0 });
  const [labStatus, setLabStatus] = useState("");
  const [followLive, setFollowLive] = useState(true);
  const [liveKey, setLiveKey] = useState("f2.welcome");
  const [targetKey, setTargetKey] = useState("f2.welcome");
  const chatRef = useRef(null);
  const captureChatRef = useRef(null);
  const captureDraftRef = useRef(null);
  const captureKeepRef = useRef([]);
  const [sideMenuOpen, setSideMenuOpen] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const studentActiveRef = useRef(false);
  const parentStateRef = useRef({ kids: null, open: false });
  const childPickedRef = useRef(false);
  const scrollMarkRef = useRef({ last: null, anchor: null, fresh: true, userScrolled: false });
  const scrollToAnchor = () => {
    const el = chatRef.current;
    if (!el) return;
    const { anchor } = scrollMarkRef.current;
    const bottom = el.scrollHeight - el.clientHeight;
    el.scrollTop = anchor && el.contains(anchor) ? Math.min(bottom, anchor.offsetTop - 8) : bottom;
  };
  useEffect(() => {
    const tapped = (event) => {
      if (event.target?.closest?.('[data-testid="lab-quick-replies"]')) captureChatRef.current = null;
      scrollMarkRef.current.fresh = true;
      scrollMarkRef.current.userScrolled = false;
    };
    const scrolled = (event) => {
      if (chatRef.current?.contains(event.target)) scrollMarkRef.current.userScrolled = true;
    };
    const taps = ["click", "keydown"];
    const moves = ["wheel", "touchmove"];
    taps.forEach((name) => window.addEventListener(name, tapped, true));
    moves.forEach((name) => window.addEventListener(name, scrolled, { capture: true, passive: true }));
    return () => {
      taps.forEach((name) => window.removeEventListener(name, tapped, true));
      moves.forEach((name) => window.removeEventListener(name, scrolled, { capture: true }));
    };
  }, []);
  const photoRef = useRef(null);
  const objectiveCaptureRef = useRef(false);
  const ficheShownRef = useRef(false);
  const helpItemRef = useRef("marge");
  const gptImageRef = useRef("");
  const gptHistoryRef = useRef([]);
  const gptPendingRef = useRef("");
  const gptGenRef = useRef(0);
  const agendaItemsRef = useRef([]);
  const agendaModeRef = useRef(false);
  const timers = useRef([]);
  const revisionsRef = useRef(revisions);
  revisionsRef.current = revisions;
  const blueprintRef = useRef(blueprint);
  blueprintRef.current = blueprint;

  const clearTimers = () => {
    parentRunRef.current += 1;
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const later = (fn, ms) => {
    const id = setTimeout(fn, ms);
    timers.current.push(id);
  };

  const pushLog = (text) =>
    setLog((l) => [{ id: Date.now() + Math.random(), text }, ...l]);

  const persist = (recipe) => {
    const raw = recipe(revisionsRef.current);
    const next = {
      overrides: raw?.overrides && typeof raw.overrides === "object" ? raw.overrides : {},
      promptOverrides:
        raw?.promptOverrides && typeof raw.promptOverrides === "object" ? raw.promptOverrides : {},
      integrations: Array.isArray(raw?.integrations) ? raw.integrations : [],
      comments: raw?.comments && typeof raw.comments === "object" ? raw.comments : {},
    };
    revisionsRef.current = next;
    saveRevisions(next);
    setRevisions(next);
    return next;
  };

  const personalizeCopy = (text, extra = {}) => {
    const tutor = extra.tutorName || "";
    let next = String(text || "")
      .replaceAll("{enfant}", extra.child || studentName)
      .replaceAll("{student}", extra.child || studentName)
      .replaceAll("{parent}", parentName)
      .replaceAll("{tuteur}", tutor)
      .replaceAll("{tutor}", tutor)
      .replaceAll("Salut Émile", `Salut ${studentName}`);
    if (tutor && extra.messageKey === "f2.baptism") {
      next = next.replace(/^[^,\n]+,\s*c'est moi/i, `${tutor}, c'est moi`);
    }
    return next;
  };

  const withRevision = (items) => {
    const overrides = revisionsRef.current.overrides;
    return items.map((item) => {
      if (!item.messageKey) return item;
      const sourceText = item.sourceText || item.text;
      const override = overrides[item.messageKey];
      const revised = typeof override === "string";
      return {
        ...item,
        sourceText,
        text: personalizeCopy(revised ? override : sourceText, item),
        revised: revised && !appMode,
      };
    });
  };

  const addAgent = (key, replies = [], ctx = {}) => {
    const text = getFlowMessage(key).text({ studentName, ...ctx });
    setTyping(true);
    later(() => {
      setTyping(false);
      const [message] = withRevision([
        {
          id: `a-${Date.now()}-${Math.random()}`,
          sender: "agent",
          messageKey: key,
          text,
          tutorName: ctx.tutorName || "",
          time: nowStamp(),
        },
      ]);
      setMessages((m) => [...m, message]);
      setQuickReplies(replies);
      setLiveKey(key);
    }, 700);
  };

  const addUser = (text) => {
    setMessages((m) => [
      ...m,
      {
        id: `u-${Date.now()}-${Math.random()}`,
        sender: "user",
        text,
        time: nowStamp(),
      },
    ]);
    setQuickReplies([]);
    pushLog(`Application web (${studentName}) : « ${text} »`);
  };

  const stamp = (items) => {
    const time = nowStamp();
    return items.map((item, index) => ({
      ...item,
      id: `s-${index}-${item.kind || item.sender}`,
      time,
    }));
  };

  const markSection = (flowId, append) => {
    const label = sectionLabel(flowId);
    setMessages((current) => {
      const base = append ? current : [];
      const last = base[base.length - 1];
      if (last?.kind === "section" && last.text === label) return base;
      return [
        ...base,
        {
          id: `section-${flowId}-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
          sender: "system",
          kind: "section",
          text: label,
          time: nowStamp(),
        },
      ];
    });
  };

  const startFlow2 = () => {
    clearTimers();
    setTocOpen(false);
    markSection(2, false);
    setQuickReplies([]);
    setInput("");
    setTyping(false);
    setDash(initialDash);
    setLog([
      {
        id: Date.now(),
        text: "Flow 2 — Accueil dans l'application démarré (simulation, aucune BD).",
      },
    ]);
    later(() => {
      addAgent("f2.welcome", FLOW2_REPLIES.welcome);
      setDash((d) => ({
        ...d,
        flowId: 2,
        flowStep: 0,
        status: FLOW2_STEPS[0],
      }));
    }, 200);
  };

  const beginFlow3 = (append) => {
    clearTimers();
    setTocOpen(false);
    setQuickReplies([]);
    setInput("");
    setTyping(false);
    markSection(3, !!append);
    setDash((d) => ({
      ...(append ? d : initialDash),
      flowId: 3,
      flowStep: 0,
      status: FLOW3_STEPS[0],
      objective: emptyObjective(),
    }));
    pushLog("Flow 3 — Collecte des objectifs démarré (simulation, aucune BD).");
    later(() => {
      addAgent("f3.intro", []);
      later(() => addAgent("f3.rhythm", mentionReply()), 700);
    }, append ? 450 : 200);
  };

  const offerTutorName = () => {
    setQuickReplies([]);
    setDash((current) => ({
      ...current,
      flowId: 2,
      flowStep: 4,
      status: FLOW2_STEPS[4],
      nameDraft: false,
    }));
    later(() => addAgent("f2.name", FLOW2_REPLIES.name), 400);
  };

  const showTutorNameField = () => {
    setDash((current) => ({ ...current, flowId: 2, flowStep: 4, nameDraft: true, status: FLOW2_STEPS[4] }));
    later(() => {
      addAgent("f2.nameOther", []);
      later(() => {
        setMessages((current) => [
          ...current,
          { id: `name-field-${Date.now()}`, sender: "agent", kind: "tutorName", time: nowStamp() },
        ]);
        setQuickReplies([]);
      }, 900);
    }, 200);
  };

  const acceptTutorName = (raw) => {
    const name = cleanTutorName(raw);
    const typed = String(raw || "").trim();
    if (!name) {
      addUser(typed || "…");
      later(() => {
        addAgent("f2.nameInvalid", []);
        later(() => {
          setMessages((current) => [
            ...current,
            { id: `name-field-${Date.now()}`, sender: "agent", kind: "tutorName", time: nowStamp() },
          ]);
          setQuickReplies([]);
        }, 900);
      }, 400);
      return;
    }
    addUser(name);
    if (typeof onTutorName === "function") onTutorName(name);
    setDash((current) => ({
      ...current,
      flowId: 2,
      flowStep: 5,
      status: "Baptême",
      tutorName: name,
      nameDraft: false,
    }));
    const baptismReplies = levelOne
      ? [{ id: "f2-name-go", label: "Voir mon espace", primary: true }]
      : FLOW2_REPLIES.baptism;
    later(() => addAgent("f2.baptism", baptismReplies, { tutorName: name }), 400);
  };

  const pushKind = (kind, extra = {}) => {
    setMessages((current) => [
      ...current,
      { id: `${kind}-${Date.now()}`, sender: "agent", kind, time: nowStamp(), ...extra },
    ]);
  };

  const beginFlow4 = (append) => {
    clearTimers();
    setTocOpen(false);
    setQuickReplies([]);
    setInput("");
    setTyping(false);
    markSection(4, !!append);
    setDash((d) => ({
      ...(append ? d : initialDash),
      flowId: 4,
      flowStep: 0,
      status: FLOW4_STEPS[0],
      week: emptyWeek(),
    }));
    pushLog("Flow 4 — Planification hebdomadaire démarré (simulation, aucune BD).");
    later(() => {
      addAgent("f4.goals", []);
      later(() => {
        pushKind("goals");
        later(() => addAgent("f4.confirm", FLOW4_REPLIES.go), 350);
      }, 800);
    }, append ? 450 : 200);
  };

  const beginFlow6 = (append) => {
    clearTimers();
    setTocOpen(false);
    setQuickReplies([]);
    setInput("");
    setTyping(false);
    markSection(6, !!append);
    setDash((d) => ({
      ...(append ? d : initialDash),
      flowId: 6,
      flowStep: 0,
      status: FLOW6_STEPS[0],
      session: emptySession(),
    }));
    pushLog("Flow 6 — Suivi des activités démarré (simulation, aucune BD).");
    later(() => {
      addAgent("f6.ask", FLOW6_REPLIES.ask);
    }, append ? 450 : 200);
  };

  const beginFlow7 = (append) => {
    clearTimers();
    setTocOpen(false);
    setQuickReplies([]);
    setInput("");
    setTyping(false);
    markSection(7, !!append);
    sendWebReminder("bilan");
    setDash((d) => ({
      ...(append ? d : initialDash),
      flowId: 7,
      flowStep: 0,
      status: FLOW7_STEPS[0],
      planReady: true,
      sessionsDone: 5,
      sessionsTotal: 6,
      minutes: 275,
      regularity: 83,
      subject: "Maths",
    }));
    pushLog("Flow 7 — Bilan parent démarré (simulation, aucune BD).");
    later(() => {
      addAgent("f7.hello", []);
      later(() => {
        pushKind("parentSummary");
        later(() => addAgent("f7.weekly", FLOW7_REPLIES.subjects), 350);
      }, 800);
    }, append ? 450 : 200);
  };

  const beginFlow8 = (append) => {
    clearTimers();
    setTocOpen(false);
    setQuickReplies([]);
    setInput("");
    setTyping(false);
    markSection(8, !!append);
    setDash((d) => ({
      ...(append ? d : initialDash),
      flowId: 8,
      flowStep: 0,
      status: FLOW8_STEPS[0],
      subject: "Maths",
    }));
    pushLog("Flow 8 — Aide photo démarré (simulation, aucune BD).");
    ficheShownRef.current = false;
    helpItemRef.current = "marge";
    gptGenRef.current += 1;
    gptImageRef.current = "";
    gptHistoryRef.current = [];
    agendaModeRef.current = false;
    later(() => addAgent("f8.ask", FLOW8_REPLIES.ask), append ? 450 : 200);
  };

  const beginFlow9 = (append) => {
    clearTimers();
    setTocOpen(false);
    setQuickReplies([]);
    setInput("");
    setTyping(false);
    markSection(9, !!append);
    agendaItemsRef.current = [];
    agendaModeRef.current = true;
    setDash((d) => ({
      ...(append ? d : initialDash),
      flowId: 9,
      flowStep: 0,
      status: FLOW9_STEPS[0],
      subject: "—",
      planReady: false,
      minutes: 0,
      sessionsDone: 0,
      sessionsTotal: 0,
    }));
    pushLog("Flow 9 — Agenda et remises démarré (simulation, aucune BD).");
    later(() => addAgent("f9.ask", FLOW9_REPLIES.ask), append ? 450 : 200);
  };

  const beginFlow5 = (append) => {
    clearTimers();
    setTocOpen(false);
    setQuickReplies([]);
    setInput("");
    setTyping(false);
    markSection(5, !!append);
    setDash((d) => ({
      ...(append ? d : initialDash),
      flowId: 5,
      flowStep: 0,
      status: FLOW5_STEPS[0],
      subject: "Maths – Fonctions",
      session: { ...emptySession(), task: "Maths – Fonctions" },
    }));
    pushLog("Flow 5 — Accompagnement à la tâche démarré (simulation, aucune BD).");
    sendWebReminder("activite");
    later(() => {
      addAgent("f5.remind", []);
      later(() => addAgent("f5.askStart", FLOW5_REPLIES.startNow), 400);
    }, append ? 450 : 200);
  };

  const prepareTutorSend = (patch) => {
    const session = { ...emptySession(), ...dash.session, ...patch };
    setDash((d) => ({
      ...d,
      flowId: 5,
      flowStep: 5,
      status: FLOW5_STEPS[4],
      planReady: true,
      subject: session.task || "Maths",
      minutes: sessionMinutes(session.duration),
      sessionsTotal: Math.max(d.sessionsTotal, 1),
      session,
    }));
    later(() => {
      addAgent("f5.send", []);
      later(() => {
        setMessages((current) => [
          ...current,
          {
            id: `tutor-${Date.now()}`,
            sender: "agent",
            kind: "tutorNote",
            rows: tutorRows({
              task: session.task,
              started: session.started,
              ended: session.ended,
              pause: session.pause,
              hadPause: session.hadPause,
              duration: session.duration,
              rating: session.difficulty,
              note: session.blockage,
            }),
            time: nowStamp(),
          },
        ]);
        setQuickReplies(FLOW5_REPLIES.send);
      }, 900);
    }, 200);
  };

  const openFromToc = (flowId, stepIndex) => {
    clearTimers();
    setWire(null);
    setTyping(false);
    setInput("");
    setTocOpen(false);
    const snap =
      flowId === 9
        ? buildFlow9Snapshot(stepIndex)
        : flowId === 8
        ? buildFlow8Snapshot(stepIndex)
        : flowId === 7
          ? buildFlow7Snapshot(stepIndex, studentName)
          : flowId === 6
          ? buildFlow6Snapshot(stepIndex)
          : flowId === 5
            ? buildFlow5Snapshot(stepIndex)
            : flowId === 4
              ? buildFlow4Snapshot(stepIndex)
              : flowId === 3
                ? buildFlow3Snapshot(stepIndex, studentName)
                : buildFlow2Snapshot(stepIndex, studentName);
    const shown = stamp(
      withRevision([
        { sender: "system", kind: "section", text: sectionLabel(flowId) },
        ...snap.messages,
      ])
    );
    ficheShownRef.current = shown.some((item) => item.kind === "fiche");
    if (flowId === 8) helpItemRef.current = "marge";
    if (flowId === 9) {
      agendaItemsRef.current = snap.items || [];
      agendaModeRef.current = true;
    } else {
      agendaModeRef.current = false;
    }
    setMessages(shown);
    const lastAgent = [...shown].reverse().find((item) => item.messageKey);
    if (lastAgent) setLiveKey(lastAgent.messageKey);
    setQuickReplies(snap.quickReplies);
    const base = {
      ...initialDash,
      flowId,
      flowStep: snap.flowStep,
      status: snap.status,
      planReady: Boolean(snap.planReady),
      objective: snap.objective || emptyObjective(),
      session: snap.session || emptySession(),
      week: snap.week || emptyWeek(),
    };
    if (flowId === 9) {
      const plans = buildStudyPlans(snap.items || []);
      const totals = planTotals(plans);
      const planned = snap.flowStep >= 3;
      setDash({
        ...base,
        subject: planned ? totals.label : "—",
        minutes: planned ? totals.minutes : 0,
        sessionsDone: 0,
        sessionsTotal: planned ? totals.sessions : 0,
        planReady: snap.flowStep >= 5,
      });
    } else if (flowId === 8) {
      const helped = snap.flowStep >= 1;
      const closed = snap.flowStep >= 8;
      setDash({
        ...base,
        subject: helped ? "Maths" : "—",
        minutes: snap.flowStep >= 6 ? 8 : 0,
        sessionsDone: closed ? 1 : 0,
        sessionsTotal: closed ? 1 : 0,
        regularity: closed ? 100 : 0,
        planReady: snap.flowStep >= 6,
        status: closed ? "Aide photo · comprise" : snap.status,
      });
    } else if (flowId === 7) {
      setDash({
        ...base,
        sessionsDone: 5,
        sessionsTotal: 6,
        minutes: 275,
        regularity: 83,
        subject: "Maths",
        planReady: true,
      });
    } else if (flowId === 6) {
      const recorded = Boolean(snap.planReady);
      setDash({
        ...base,
        sessionsDone: recorded ? 1 : 0,
        sessionsTotal: recorded ? 1 : 0,
        subject: "Maths",
        minutes: snap.session?.duration ? sessionMinutes(snap.session.duration) : 0,
        regularity: recorded ? 100 : 0,
      });
    } else if (flowId === 5) {
      const started = snap.flowStep >= 1;
      const done = snap.flowStep >= 6;
      setDash({
        ...base,
        sessionsDone: done ? 1 : 0,
        sessionsTotal: started ? 1 : 0,
        subject: "Maths",
        minutes: sessionMinutes(snap.session?.duration),
        regularity: done ? 100 : 0,
        planReady: done,
        session: { ...emptySession(), ...snap.session },
      });
    } else if (flowId === 4) {
      const ready = snap.flowStep >= 2;
      const active = snap.flowStep >= 6;
      setDash({
        ...base,
        sessionsDone: 0,
        sessionsTotal: ready ? 6 : 0,
        subject: ready ? "4 matières" : "—",
        minutes: ready ? 330 : 0,
        regularity: active ? 100 : 0,
        planReady: ready,
      });
    } else if (flowId === 3) {
      setDash({
        ...base,
        sessionsTotal: snap.planReady ? 1 : 0,
        subject: snap.objective?.subject || "—",
        minutes: snap.objective ? durationMinutes(snap.objective.duration) : 0,
      });
    } else {
      setDash({ ...base, sessionsTotal: snap.planReady ? 32 : 0 });
    }
    pushLog(`Sommaire : ${flowTitle(flowId)} · ${snap.status}`);
  };

  // Simulator only: reopen on the same role, flow and message, with the conversation so far.
  const restoreLab = () => {
    let saved = null;
    try {
      saved = JSON.parse(localStorage.getItem(RESUME_KEY) || "null");
    } catch {
      saved = null;
    }
    if (!saved?.dash || !(saved.messages?.length || saved.parentMessages?.length)) return false;
    simRestore(saved.sim);
    parentChildRef.current = saved.parentChild || null;
    parentStarted.current = Boolean(saved.parentStarted);
    studentActiveRef.current = Boolean(saved.studentActive);
    onboardedRef.current = Boolean(saved.onboarded);
    lot3DoneRef.current = saved.lot3 || {};
    setRole(saved.role === "parent" ? "parent" : "eleve");
    setMessages(saved.messages || []);
    setQuickReplies(saved.quickReplies || []);
    setParentMessages(saved.parentMessages || []);
    setParentReplies(saved.parentReplies || []);
    setDash(saved.dash);
    if (saved.liveKey) setLiveKey(saved.liveKey);
    setClockNow(simClock.now());
    pushLog(`Reprise : ${flowTitle(saved.dash.flowId)} · ${saved.role === "parent" ? "Parent" : "Élève"}`);
    return true;
  };

  useEffect(() => {
    if (!open || appMode) return undefined;
    const timer = setTimeout(() => {
      try {
        localStorage.setItem(RESUME_KEY, JSON.stringify({
          role, messages, quickReplies, parentMessages, parentReplies, dash, liveKey,
          parentChild: parentChildRef.current,
          parentStarted: parentStarted.current,
          studentActive: studentActiveRef.current,
          onboarded: onboardedRef.current,
          lot3: lot3DoneRef.current,
          sim: simSnapshot(),
        }));
      } catch {
        /* storage full (photos): keep the previous snapshot */
      }
    }, 600);
    return () => clearTimeout(timer);
  }, [open, appMode, role, messages, quickReplies, parentMessages, parentReplies, dash, liveKey]);

  useEffect(() => {
    if (!open) return;
    if (variant !== "app" && restoreLab()) {
      // Resumed from the last visit.
    } else if (variant === "app" && initialRole === "parent") {
      setRole("parent");
      setDash((current) => ({ ...current, view: "parent" }));
      if (levelOne) {
        parentStarted.current = true;
        setParentMessages([]);
        parentWelcome();
      } else {
        startParentOnboarding();
      }
    } else {
      setRole("eleve");
      setDash((current) => ({ ...current, view: "eleve" }));
      if (variant === "app" && levelOne && (cleanTutorName(tutorName) || onboardedRef.current || /[?&](seance|plan)=/.test(window.location.search))) {
        // Returning student: onboarding already done, straight to the activities.
        setMessages([
          { id: `section-home-${Date.now()}`, sender: "system", kind: "section", text: "Accueil", time: nowStamp() },
        ]);
        sayStudent("e.welcomeBack");
        later(() => showStudentHome(), 900);
      } else {
        startFlow2();
      }
    }
    const prev = document.body.style.overflow;
    if (variant !== "app") document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
      clearTimers();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, studentName, variant, initialRole]);

  useEffect(() => {
    const el = chatRef.current;
    if (!el) return;
    // Everything that arrived since the user's last tap stays readable from its first line.
    const mark = scrollMarkRef.current;
    const items = [...el.children].filter((node) => !node.hasAttribute("data-chat-extra"));
    const seen = items.indexOf(mark.last);
    const added = seen === -1 ? items.slice(-1) : items.slice(seen + 1);
    if (added.length && (mark.fresh || !mark.anchor || !el.contains(mark.anchor))) {
      mark.anchor = added[0];
      mark.fresh = false;
    }
    mark.last = items[items.length - 1] || null;
    if (mark.userScrolled) return undefined;
    const scroll = () => scrollToAnchor();
    scroll();
    const id = requestAnimationFrame(scroll);
    const timer = window.setTimeout(scroll, 280);
    return () => {
      cancelAnimationFrame(id);
      window.clearTimeout(timer);
    };
  }, [messages, parentMessages, typing, quickReplies, parentReplies, immersive, frameBox]);

  useEffect(() => {
    if (followLive) setTargetKey(liveKey);
  }, [followLive, liveKey]);

  useEffect(() => {
    const query = window.matchMedia("(max-width: 767px)");
    const apply = () => setNarrow(query.matches);
    apply();
    query.addEventListener("change", apply);
    return () => query.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const native = appMode && immersive && narrow;
    if (!native) return undefined;
    const root = document.documentElement;
    const body = document.body;
    const previous = {
      rootOverflow: root.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyWidth: body.style.width,
      bodyTop: body.style.top,
    };
    const scrollY = window.scrollY;
    root.style.overflow = "hidden";
    body.style.overflow = "hidden";
    body.style.position = "fixed";
    body.style.width = "100%";
    body.style.top = `-${scrollY}px`;
    const apply = () => {
      const viewport = window.visualViewport;
      const next = {
        top: viewport ? viewport.offsetTop : 0,
        left: viewport ? viewport.offsetLeft : 0,
        width: viewport ? viewport.width : window.innerWidth,
        height: viewport ? viewport.height : window.innerHeight,
      };
      setFrameBox((current) =>
        current.top === next.top &&
        current.left === next.left &&
        current.width === next.width &&
        current.height === next.height
          ? current
          : next
      );
      scrollToAnchor();
      document.documentElement.scrollLeft = 0;
      document.body.scrollLeft = 0;
    };
    apply();
    const viewport = window.visualViewport;
    viewport?.addEventListener("resize", apply);
    viewport?.addEventListener("scroll", apply);
    window.addEventListener("orientationchange", apply);
    return () => {
      root.style.overflow = previous.rootOverflow;
      body.style.overflow = previous.bodyOverflow;
      body.style.position = previous.bodyPosition;
      body.style.width = previous.bodyWidth;
      body.style.top = previous.bodyTop;
      window.scrollTo(0, scrollY);
      viewport?.removeEventListener("resize", apply);
      viewport?.removeEventListener("scroll", apply);
      window.removeEventListener("orientationchange", apply);
    };
  }, [appMode, immersive, narrow]);

  useEffect(() => {
    if (!phonePanel) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") setPhonePanel("");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [phonePanel]);

  useEffect(() => {
    const onRevisions = (event) => {
      const next = event.detail;
      if (!next || typeof next !== "object") return;
      revisionsRef.current = next;
      setRevisions(next);
    };
    const onBlueprint = (event) => {
      const next = event.detail;
      if (!next || !Array.isArray(next.flows)) return;
      blueprintRef.current = next;
      setBlueprint(next);
    };
    window.addEventListener("sp-lab-revisions", onRevisions);
    window.addEventListener("sp-lab-blueprint", onBlueprint);
    return () => {
      window.removeEventListener("sp-lab-revisions", onRevisions);
      window.removeEventListener("sp-lab-blueprint", onBlueprint);
    };
  }, []);

  useEffect(() => {
    if (appMode) {
      loadPublished()
        .then((overrides) => {
          const next = { ...emptyRevisions(), overrides };
          revisionsRef.current = next;
          setRevisions(next);
          setMessages((current) => withRevision(current));
          setParentMessages((current) => withRevision(current));
        })
        .catch(() => {});
      return undefined;
    }
    const token = adminToken();
    if (!open || !token) return undefined;
    return startLabSync(token, setLabStatus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appMode, open]);

  if (!open) return null;

  const playNode = (flowId, nodeId, mode = "jump") => {
    if (dash.flowId === 12 && flowId !== 12) resetClock();
    if (flowId === 11 || flowId === 12) {
      clearTimers();
      setTyping(false);
      setTocOpen(false);
      const parentSide = flowId === 12 ? role === "parent" : nodeId === "s11-1" || nodeId === "s11-3";
      setRole(parentSide ? "parent" : "eleve");
      setDash((current) => ({ ...current, view: parentSide ? "parent" : "eleve" }));
      if (flowId === 11) return parentSide ? parentHelpView() : studentHelp();
      if (nodeId === "s12-0") return advanceClock("saturday");
      if (nodeId === "s12-1") return advanceClock("monday");
      return showReport(parentSide ? "parent" : "eleve");
    }
    if (flowId === 14) {
      clearTimers();
      setTyping(false);
      setTocOpen(false);
      setRole("eleve");
      lot3DoneRef.current.eleve = false;
      return showStudentHome();
    }
    if (MVP_FLOWS[flowId]) {
      clearTimers();
      setTyping(false);
      setTocOpen(false);
      const parentCapture = flowId === 18 && role === "parent";
      if (!parentCapture) setRole("eleve");
      setDash((current) => ({ ...current, view: parentCapture ? "parent" : "eleve" }));
      if (flowId === 18 || flowId === 19) return startCapture(parentCapture);
      if (flowId === 20) return startSchedule();
      if (flowId === 21) {
        if (nodeId === "s21-1") return askAssistant("GET_NEXT_SESSION");
        studentSection(21);
        mvpDash(21, 0);
        return sayStudent("q.menu", {}, quickAsk);
      }
      if (flowId === 23) return studentAiHelp();
      if (flowId === 24) return studentCheckin();
      const next = simStats().activePlans[0];
      return next ? openSession("", next.id) : showStudentHome();
    }
    if (flowId === 15 || flowId === 16 || flowId === 17) {
      clearTimers();
      setTyping(false);
      setTocOpen(false);
      setRole("eleve");
      setDash((current) => ({ ...current, view: "eleve" }));
      if (flowId === 15) return studentSelfPlan();
      if (flowId === 16) return studentWeekPlan();
      if (nodeId === "s17-2") {
        const open = simStats().activePlans[0];
        return open ? openSession("", open.id) : showStudentHome();
      }
      studentSection(17);
      weekDash(17, nodeId === "s17-1" ? 1 : 0);
      return advanceClock("next");
    }
    if ((flowId === 1 || flowId === 10 || flowId === 13) && levelOne && PARENT_V0_NODES[nodeId]) {
      if (flowId === 13) lot3DoneRef.current.parent = false;
      clearTimers();
      setTyping(false);
      setTocOpen(false);
      setWire({ flowId, nodeId });
      setRole("parent");
      setParentReplies([]);
      PARENT_V0_NODES[nodeId]();
      return;
    }
    const flow = findFlow(blueprintRef.current, flowId);
    const node = findNode(blueprintRef.current, flowId, nodeId);
    if (!node) return;
    clearTimers();
    setTyping(false);
    setWire({ flowId, nodeId });
    setTocOpen(false);
    const fillNames = (value) =>
      String(value || "")
        .replaceAll("{parent}", parentName)
        .replaceAll("{student}", studentName)
        .replaceAll("Salut Émile", `Salut ${studentName}`);
    let text = node.message.trim();
    if (node.messageKey) {
      const override = revisionsRef.current.overrides[node.messageKey];
      if (typeof override === "string") text = override;
      else {
        try {
          text = getFlowMessage(node.messageKey).text({
            studentName,
            tutorName: dash.tutorName || cleanTutorName(tutorName) || "Léo",
            subject: "cette matière",
            minutes: 25,
            item: "marge",
          });
        } catch {
          text = text || "Écris le message de cette étape.";
        }
      }
    }
    if (!text) text = "Écris le message de cette étape.";
    const time = nowStamp();
    const relance = node.relance?.message?.trim();
    const relanceKey = node.id === "s3-0" ? "f3.rhythm" : undefined;
    const relanceOverride =
      relanceKey && typeof revisionsRef.current.overrides[relanceKey] === "string"
        ? revisionsRef.current.overrides[relanceKey]
        : "";
    const relanceText = relanceOverride || relance;
    const bubbles = [
      {
        id: `wire-${nodeId}-${Date.now()}`,
        sender: "agent",
        messageKey: node.messageKey || undefined,
        text: fillNames(text),
        sourceText: text,
        time,
      },
      ...(relanceText
        ? [
            {
              id: `relance-${nodeId}-${Date.now()}`,
              sender: "agent",
              messageKey: relanceKey,
              text: fillNames(
                relanceKey || !node.relance?.when ? relanceText : `${node.relance.when}\n${relanceText}`
              ),
              time,
            },
          ]
        : []),
      ...(node.id === "s7-0"
        ? [{ id: `psum-${Date.now()}`, sender: "agent", kind: "parentSummary", time }]
        : []),
    ];
    const replies = node.replies.map((reply, index) => ({
      id: `wire:${reply.id}`,
      label: fillNames(reply.label || "Réponse"),
      primary: index === 0,
      wireTo: reply.to,
      wireFlow:
        blueprintRef.current.flows.find((item) => item.nodes.some((candidate) => candidate.id === reply.to))?.id ||
        flowId,
    }));
    if (flow.audience === "parent") {
      parentStarted.current = true;
      setRole("parent");
      const section = {
        id: `section-${flowId}-${Date.now()}`,
        sender: "system",
        kind: "section",
        text: flow.title,
        time,
      };
      const entering = mode !== "continue" || dash.flowId !== flowId;
      setParentMessages((current) =>
        mode === "continue" ? [...current, ...(entering ? [section] : []), ...bubbles] : [section, ...bubbles]
      );
      setParentReplies(replies);
      if (relanceKey) setLiveKey(relanceKey);
      else if (node.messageKey) setLiveKey(node.messageKey);
      setDash((current) => ({
        ...current,
        view: "parent",
        flowId,
        status: node.title,
        flowStep: node.scriptIndex ?? current.flowStep,
      }));
      pushLog(`Flow ${flowId} · ${node.title}`);
      if (flowId === 7 && (node.id === "s7-3" || node.messageKey === "f7.alert")) sendWebReminder("alerte");
      else if (flowId === 7) sendWebReminder("bilan");
      return;
    }
    setMessages([
      {
        id: `section-${flowId}-${Date.now()}`,
        sender: "system",
        kind: "section",
        text: sectionLabel(flowId),
        time,
      },
      ...bubbles.filter((item) => item.kind !== "parentSummary"),
    ]);
    setQuickReplies(replies);
    if (relanceKey) setLiveKey(relanceKey);
    else if (node.messageKey) setLiveKey(node.messageKey);
    setDash((current) => ({
      ...current,
      flowId,
      status: node.title,
      flowStep: node.scriptIndex ?? current.flowStep,
    }));
    pushLog(`Fil · ${node.title}`);
  };

  const startParentFlow = (flowId) => {
    const flow = findFlow(blueprintRef.current, flowId);
    const first = spineNodes(flow)[0];
    if (first) playNode(flow.id, first.id, "jump");
  };

  const n1MenuText = () =>
    N1_MENU.filter(([, , audience]) => !audience || audience === (role === "parent" ? "parent" : "student"))
      .map(([command, label, , tag]) => `${command} — ${label}${tag === "soon" ? " (bientôt)" : ""}`)
      .join("\n");

  const agentNote = (text, target = "eleve") => {
    const note = { id: `n1-${Date.now()}-${Math.random()}`, sender: "agent", text, time: nowStamp() };
    if (target === "parent") setParentMessages((current) => [...current, note]);
    else setMessages((current) => [...current, note]);
  };

  // ---------- Lot 2 — engagements in the chat ----------
  const apiKey = `${appMode}|${accountToken}|${role}|${parentName}|${studentName}`;
  if (apiCacheRef.current.key !== apiKey) {
    apiCacheRef.current = {
      key: apiKey,
      api: appMode && accountToken ? liveApi(accountToken) : simApi({ parentName, studentName, role }),
    };
  }
  const engagementApi = apiCacheRef.current.api;
  const viewerId = appMode ? accountId : role === "parent" ? "sim-parent" : "sim-student";
  const selfStudentId = appMode ? accountId : "sim-student";

  const sayStudent = (key, ctx = {}, replies = []) => {
    const text = getFlowMessage(key).text({ parentName, studentName, ...ctx });
    setTyping(true);
    later(() => {
      setTyping(false);
      const [message] = withRevision([
        { id: `sa-${Date.now()}-${Math.random()}`, sender: "agent", messageKey: key, text, time: nowStamp() },
      ]);
      setMessages((current) => [...current, message]);
      setQuickReplies(replies);
      setLiveKey(key);
    }, 650);
  };

  const pushStudentCard = (card, delay = 900) =>
    later(() => {
      setMessages((current) => [
        ...current,
        { id: `sc-${Date.now()}-${Math.random()}`, sender: "agent", time: nowStamp(), ...card },
      ]);
    }, delay);

  const selfPlanReply = { id: "e-self", label: "Créer mon propre plan d’étude" };
  const studentMenuReply = { id: "e-menu", label: "Menu" };
  const studentEndReplies = [studentMenuReply, { id: "e-end", label: "Terminer la session" }];

  const showStudentHome = async () => {
    const run = ++studentRunRef.current;
    setQuickReplies([]);
    setWire(null);
    markOnboarded();
    studentSection(14);
    setDash((current) => ({ ...current, view: "eleve", flowId: 14, flowStep: 0, status: FLOW14_STEPS[0] }));
    const params = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : null;
    const seance = params?.get("seance") || "";
    const planLink = params?.get("plan") || "";
    if (appMode && !linkHandledRef.current && (seance || planLink)) {
      linkHandledRef.current = true;
      window.history.replaceState(null, "", window.location.pathname);
      openSession(seance, planLink);
      return;
    }
    if (appMode) {
      const rows = await engagementApi.remindersToday().catch(() => []);
      const seenKey = `sp.sms.seen.${viewerId}`;
      const seen = (localStorage.getItem(seenKey) || "").split(",");
      const fresh = rows.filter((row) => !seen.includes(row.id));
      if (fresh.length) localStorage.setItem(seenKey, [...seen, ...fresh.map((row) => row.id)].slice(-40).join(","));
      fresh.forEach((row) => simNotice("eleve", "SMS", row.text));
    }
    let plans = [];
    try {
      plans = await engagementApi.listPlans(selfStudentId);
    } catch {
      plans = [];
    }
    if (run !== studentRunRef.current) return;
    const pending = plans.filter((plan) => plan.status === "pending_acceptance");
    const active = plans.filter((plan) => plan.status === "active");
    studentActiveRef.current = active.length > 0;
    const finished = plans.filter((plan) => plan.status === "completed").slice(0, 2);
    if (!pending.length && !active.length) {
      if (finished.length) {
        sayStudent("e.doneList");
        finished.forEach((plan, index) => pushStudentCard({ kind: "planCard", plan }, 1000 + index * 200));
        later(() => sayStudent("e.noneAfterDone", {}, studentLot3Replies()), 1700);
        return;
      }
      sayStudent("e.none", {}, studentLot3Replies());
      return;
    }
    let delay = 0;
    pending.forEach((plan) => {
      later(() => sayStudent("e.pending", { creator: plan.creatorName }), delay);
      pushStudentCard({ kind: "planCard", plan }, delay + 1000);
      delay += 1600;
    });
    let shown = 0;
    if (active.length) {
      const { cards, start, running } = await priorityCards(selfStudentId);
      if (run !== studentRunRef.current) return;
      shown = cards.length;
      const replies = studentLot3Replies().filter((reply) => reply.id !== "q-now");
      later(() => sayStudent("e.active", {}, start ? [start, ...replies] : replies), delay);
      cards.forEach((card, index) => pushStudentCard(card, delay + 1000 + index * 700));
      const runningPlan = running && active.find((plan) => plan.id === running.planId);
      if (runningPlan) pushStudentCard({ kind: "sessionTimer", plan: runningPlan, minutes: runningPlan.suggestedMinutes || 20, timerKey: running.id }, delay + 1000 + cards.length * 700);
    }
    if (!lot3DoneRef.current.eleve) {
      lot3DoneRef.current.eleve = true;
      later(() => lot3OnOpen("eleve"), delay + 1800 + shown * 700);
    }
    if (active.length) {
      const week = await engagementApi.weekPlan(selfStudentId).catch(() => null);
      if (run !== studentRunRef.current) return;
      if (!shown && week?.plans.some((plan) => plan.toPlace > 0)) later(() => studentWeekPlan(), delay + 2600);
    }
  };

  const STUDENT_FLOW_STEPS = { 15: FLOW15_STEPS, 16: FLOW16_STEPS, 17: FLOW17_STEPS };
  const weekDash = (flowId, flowStep) => {
    setWire(null);
    setDash((current) => ({ ...current, view: "eleve", flowId, flowStep, status: STUDENT_FLOW_STEPS[flowId][flowStep] }));
  };

  const studentWeekPlan = async () => {
    setQuickReplies([]);
    studentSection(16);
    weekDash(16, 0);
    const week = await engagementApi.weekPlan(selfStudentId).catch(() => null);
    const placed = week && week.sessions.length > 0 && week.plans.every((plan) => plan.toPlace === 0);
    if (placed || !week?.plans.length) {
      sayStudent(placed ? "w.alreadyPlanned" : "w.planWeek");
      pushStudentCard({ kind: "weekPlan" }, 1000);
      return;
    }
    // Flow 4 style: a proposed week first; the manual card is behind « Modifier ».
    sayStudent(week.availability?.kind ? "w.proposeAuto" : "s.askAvail");
    pushStudentCard({ kind: "schedule", remembered: week.availability || null }, 900);
  };
  const manualWeekPlan = () => {
    sayStudent("w.planWeek");
    pushStudentCard({ kind: "weekPlan" }, 900);
  };

  const onWeekDone = (all) => {
    weekDash(16, 1);
    sayStudent(all ? "w.weekDone" : "w.weekPartial", {}, studentEndReplies);
  };

  // Flow 17: the reminder link opens the planned session (or the priority plan).
  const openSession = async (sessionId, planId) => {
    setQuickReplies([]);
    studentSection(17);
    weekDash(17, 2);
    const week = await engagementApi.weekPlan(selfStudentId).catch(() => ({ sessions: [] }));
    const session = sessionId ? week.sessions.find((item) => item.id === sessionId) : null;
    if (session?.status === "started") return beginSession(session.id, session.planId);
    const plan = session ? null : (await engagementApi.listPlans(selfStudentId).catch(() => [])).find((item) => item.id === planId && item.status === "active");
    if (!session && !plan) return showStudentHome();
    return sessionChoice(session, plan);
  };

  const showRemindersFired = (rows) => {
    rows.forEach((row, index) => {
      later(() => {
        weekDash(17, row.n === 1 ? 0 : 1);
        simNotice("eleve", "SMS", row.text);
        setQuickReplies([{ id: `w-open:${row.sessionId || ""}:${row.planId}`, label: "Ouvrir le lien du texto", primary: true }]);
      }, 200 + index * 300);
    });
  };

  const creatorLimitReached = async (studentId) => {
    if (familyLevel >= 2) return false;
    const me = engagementApi.simulated ? (role === "parent" ? "sim-parent" : "sim-student") : accountId;
    const plans = await engagementApi.listPlans(studentId).catch(() => []);
    return plans.some((plan) => OPEN_PLAN_STATUSES.includes(plan.status) && plan.createdBy === me);
  };

  const studentSelfPlan = async () => {
    setQuickReplies([]);
    studentSection(15);
    weekDash(15, 0);
    if (await creatorLimitReached(selfStudentId)) {
      sayStudent("e.limitReached", {}, [{ id: "e-plans", label: "Voir les plans" }, studentMenuReply]);
      return;
    }
    const note = familyLevel === 1 ? 900 : 0;
    if (note) sayStudent("e.essentielNote");
    later(() => sayStudent("e.selfIntro"), note);
    pushStudentCard({ kind: "planForm", self: true }, 1100 + note);
  };

  const onStudentPlanChange = (plan, event, extra = {}) => {
    const ctx = { creator: plan.creatorName, done: plan.progress?.done, target: plan.progress?.target };
    if (event === "accepted") {
      studentActiveRef.current = true;
      sayStudent("e.accepted", ctx, studentLot3Replies());
      later(() => studentWeekPlan(), 1600);
    }
    else if (event === "checkin") {
      sayStudent(extra.needsHelp ? "e.checkinHelp" : "e.checkin", ctx);
      later(() => afterCheckin(), 1500);
    }
    else if (event === "completed") sayStudent("e.completed", ctx, [selfPlanReply]);
    else if (event === "change_requested") sayStudent("e.changeSent", ctx);
    else if (event === "declined") sayStudent("e.declined", ctx, [selfPlanReply]);
    else if (event === "cancelled") sayStudent("e.declined", ctx, [selfPlanReply]);
  };

  const pickChildThen = async (then, next) => {
    if (childPickedRef.current && parentChildRef.current) {
      childPickedRef.current = false;
      return parentChildRef.current;
    }
    let current;
    const kids = await engagementApi.listChildren().catch(() => []);
    parentKidsRef.current = kids;
    if (!kids.length) {
      addParent("p1.needChild", parentHomeReplies(), { child: "" });
      return null;
    }
    if (kids.length > 1) {
      addParent(
        "p1.chooseChild",
        kids.map((item) => v0Reply(`pick-${item.id}`, item.name || item.email, false, { childId: item.id, then, next })),
        { child: "" }
      );
      return null;
    }
    [current] = kids;
    parentChildRef.current = current;
    return current;
  };

  const parentPropose = async (next = "home") => {
    setParentReplies([]);
    const target = await pickChildThen("propose", next);
    if (!target) return;
    parentSection(10);
    flow10Dash(0);
    if (await creatorLimitReached(target.id)) {
      addParent("p1.limitReached", [v0Reply("home-follow", "Voir les plans"), v0Reply("home-menu", "Menu")], { child: target.name });
      return;
    }
    addParent("p1.proposeIntro", [], { child: target.name });
    pushParentCard({ kind: "planForm", childName: target.name, childId: target.id, next }, 1100);
  };

  // Conversations: the last sessions declared, newest first.
  const showHistory = async (parentSide) => {
    if (parentSide) setParentReplies([]);
    else setQuickReplies([]);
    const target = parentSide ? await pickChildThen("history") : { id: selfStudentId };
    if (!target) return undefined;
    const plans = await engagementApi.listPlans(target.id).catch(() => []);
    const lists = await Promise.all(plans.map((plan) => engagementApi.listCheckins(plan.id).then((rows) => rows.map((row) => ({ ...row, title: plan.title }))).catch(() => [])));
    const rows = lists.flat().sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))).slice(0, 10);
    const feel = { easy: "facile", ok: "correct", hard: "difficile" };
    const list = rows.map((row) => `• ${fmtDay(String(row.createdAt).slice(0, 10))} — ${row.title}${row.actualMinutes ? ` · ${row.actualMinutes} min` : ""}${feel[row.difficulty] ? ` · ${feel[row.difficulty]}` : ""}${row.needsHelp ? " · aide demandée" : ""}`).join("\n");
    if (parentSide) return addParent(rows.length ? "p1.history" : "p1.noHistory", parentHomeReplies(), { child: target.name, list });
    return sayStudent(rows.length ? "e.history" : "e.noHistory", { list }, studentLot3Replies());
  };

  const pickSideMenu = (id, label) => {
    if (id === "settings") return onOpenSettings?.();
    if (id === "feedback") return setFeedbackOpen(true);
    if (role === "parent") {
      setParentReplies([]);
      setParentMessages((current) => [...current, { id: `pu-${Date.now()}`, sender: "user", text: label, time: nowStamp() }]);
      const parentActions = {
        home: showParentHome, follow: parentFollowView, plan: () => parentPropose("home"), capture: () => startCapture(true),
        week: parentWeekView, deadlines: parentDeadlinesView, report: () => showReport("parent"), history: () => showHistory(true),
        help: parentHelpView, invite: () => handleParentV0(v0Reply("home-invite", label)), end: () => handleParentV0(v0Reply("home-end", label)),
      };
      return parentActions[id]?.();
    }
    if (id === "end") return handleQuickReply("e-end");
    addUser(label);
    const studentActions = {
      home: showStudentHome, start: () => askAssistant("START_20_MIN_SESSION"), help: studentAiHelp, plans: studentPlans,
      create: studentSelfPlan, capture: () => startCapture(false), week: studentWeekPlan,
      deadlines: () => askAssistant("LIST_UPCOMING_DEADLINES"), report: () => showReport("eleve"), history: () => showHistory(false),
    };
    return studentActions[id]?.();
  };

  const parentDeadlinesView = async () => {
    setParentReplies([]);
    const target = await pickChildThen("deadlines");
    if (!target) return;
    const res = await engagementApi.assistant(target.id, { intent: "LIST_UPCOMING_DEADLINES" }).catch(() => ({ data: {} }));
    const rows = res.data?.deadlines || [];
    if (!rows.length) return addParent("p1.noDeadlines", [v0Reply("home-capture", "Capturer des échéances", true), v0Reply("home-menu", "Menu")], { child: target.name });
    addParent("p1.deadlines", parentHomeReplies(), { child: target.name });
    return pushParentCard({ kind: "infoList", list: "deadlines", title: `Échéances de ${target.name}`, subtitle: "Devoirs et examens", rows: dueRows(rows) }, 700);
  };

  const parentWeekView = async () => {
    setParentReplies([]);
    const target = await pickChildThen("week");
    if (!target) return;
    const week = await engagementApi.weekPlan(target.id).catch(() => ({ sessions: [], plans: [] }));
    const titles = Object.fromEntries((week.plans || []).map((plan) => [plan.id, plan.title]));
    const rows = [...(week.sessions || [])].sort((a, b) => `${a.date}${a.time}`.localeCompare(`${b.date}${b.time}`));
    if (!rows.length) return addParent("p1.noWeek", parentHomeReplies(), { child: target.name });
    addParent("p1.week", parentHomeReplies(), { child: target.name });
    return pushParentCard({ kind: "infoList", list: "sessions", title: `Semaine de ${target.name}`, subtitle: "Séances prévues", rows: sessionRows(rows, titles) }, 700);
  };

  const parentFollowView = async () => {
    setParentReplies([]);
    const target = await pickChildThen("follow");
    if (!target) return;
    let plans = [];
    try {
      plans = await engagementApi.listPlans(target.id);
    } catch {
      plans = [];
    }
    const shown = plans.filter((plan) => !["declined", "cancelled", "expired"].includes(plan.status)).slice(0, 3);
    if (!shown.length) {
      addParent("p1.followEmpty", parentHomeReplies(), { child: target.name });
      return;
    }
    addParent("p1.followIntro", [], { child: target.name });
    shown.forEach((plan, index) => pushParentCard({ kind: "planCard", plan }, 1000 + index * 200));
    later(() => setParentReplies(parentHomeReplies()), 1400 + shown.length * 200);
  };

  const submitPlanForm = (msg) => async (body) => {
    if (msg.self) {
      const plan = await engagementApi.createPlan({ ...body, studentId: selfStudentId });
      studentActiveRef.current = true;
      weekDash(15, 2);
      sayStudent("e.selfCreated");
      pushStudentCard({ kind: "planCard", plan }, 1000);
      later(() => studentWeekPlan(), 2200);
      return;
    }
    const plan = msg.initial
      ? await engagementApi.editPlan(msg.initial.id, body)
      : await engagementApi.createPlan({ ...body, studentId: msg.childId });
    parentStateRef.current = { ...parentStateRef.current, open: true };
    addParent(msg.initial ? "p1.revised" : "p1.proposed", [], { child: msg.childName });
    pushParentCard({ kind: "planCard", plan }, 1000);
    later(() => {
      if (msg.next === "follow") return parentFollow();
      parentSection(13);
      home13Dash();
      return setParentReplies(parentHomeReplies());
    }, 1900);
  };

  const onParentPlanChange = (plan, event) => {
    if (event === "cancelled") addParent("p1.cancelled", parentHomeReplies(), { child: plan.studentName });
  };

  const editPlanFromCard = (plan) => {
    pushParentCard({ kind: "planForm", initial: plan, childName: plan.studentName, childId: plan.studentId, next: "home" }, 100);
  };

  // ---------- Lot 3 — help (Flow 11) and weekly review (Flow 12) ----------
  const flowDash = (flowId, flowStep, view) => {
    setWire(null);
    setDash((current) => ({ ...current, view, flowId, flowStep, status: (flowId === 11 ? FLOW11_STEPS : FLOW12_STEPS)[flowStep] }));
  };
  // 2 suggestions for the likely next step, plus Menu (opens ☰). Everything else is in the menu.
  const studentLot3Replies = () => (studentActiveRef.current
    ? [{ id: "q-now", label: "Je commence" }, { id: "e-horaire", label: "Planifier ma semaine" }, studentMenuReply]
    : [selfPlanReply, studentMenuReply]);
  const captureReply = { id: "e-capture", label: "Capturer mes travaux" };
  const STUDENT_MENU_COMMANDS = { "e-plans": "plans", "e-dispos": "dispos", "e-horaire": "horaire", "e-objectif": "objectifs", "e-echeance": "echeances" };

  const studentPlans = async () => {
    setQuickReplies([]);
    studentSection(14);
    const plans = await engagementApi.listPlans(selfStudentId).catch(() => []);
    const active = plans.filter((plan) => plan.status === "active");
    studentActiveRef.current = active.length > 0;
    if (!active.length) return sayStudent("h.noActive", {}, studentLot3Replies());
    sayStudent("e.active", {}, studentLot3Replies());
    active.forEach((plan, index) => pushStudentCard({ kind: "planCard", plan }, 1000 + index * 200));
    return undefined;
  };

  const studentSection = (flowId) => {
    const text = flowTitle(flowId);
    setMessages((current) => {
      const last = [...current].reverse().find((item) => item.kind === "section");
      if (last?.text === text) return current;
      return [...current, { id: `section-${flowId}-${Date.now()}`, sender: "system", kind: "section", text, time: nowStamp() }];
    });
  };

  const studentHelp = async () => {
    setQuickReplies([]);
    studentSection(11);
    flowDash(11, 0, "eleve");
    const plans = await engagementApi.listPlans(selfStudentId).catch(() => []);
    const active = plans.filter((plan) => plan.status === "active");
    if (!active.length) return sayStudent("h.noActive", {}, [selfPlanReply]);
    sayStudent("h.askIntro");
    pushStudentCard({ kind: "helpAsk", plans: active }, 1000);
    return undefined;
  };

  // ---------- Flows 18-23: capture -> verify -> schedule -> next actions -> séance -> AI help ----------
  const familyLevel = appMode ? Number(accountForfait) || 1 : simForfait;
  const mvpDash = (flowId, flowStep, view = "eleve") => {
    setWire(null);
    setDash((current) => ({ ...current, view, flowId, flowStep, status: MVP_FLOWS[flowId].steps[flowStep] }));
  };
  const sideSay = (parentSide, key, ctx = {}, replies = []) => (parentSide ? addParent(key, replies, ctx) : sayStudent(key, ctx, replies));
  const sideCard = (parentSide, card, delay = 900) => (parentSide ? pushParentCard(card, delay) : pushStudentCard(card, delay));
  const sideSection = (parentSide, flowId) => (parentSide ? parentSection(flowId) : studentSection(flowId));
  const quickAsk = [{ id: "q-next", label: "Ma prochaine séance" }, studentMenuReply];
  const QUICK_INTENTS = { "q-plans": "LIST_ACTIVE_PLANS", "q-deadlines": "LIST_UPCOMING_DEADLINES", "q-next": "GET_NEXT_SESSION", "q-week": "GET_WEEK_SCHEDULE", "q-now": "START_20_MIN_SESSION", "q-move": "RESCHEDULE_SESSION" };
  const fmtDay = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" });

  const startCapture = async (parentSide = role === "parent") => {
    if (parentSide) setParentReplies([]);
    else setQuickReplies([]);
    sideSection(parentSide, 18);
    mvpDash(18, 0, parentSide ? "parent" : "eleve");
    if (familyLevel < 2) return sideSay(parentSide, parentSide ? "c.lockedParent" : "c.locked", {}, parentSide ? parentHomeReplies() : [selfPlanReply, ...studentLot3Replies()]);
    const child = parentSide ? await pickChildThen("capture") : null;
    if (parentSide && !child) return undefined;
    sideSay(parentSide, parentSide ? "c.introParent" : "c.intro", { child: child?.name });
    captureChatRef.current = { parentSide, studentId: parentSide ? child.id : selfStudentId, text: "", rounds: 0 };
    sideCard(parentSide, { kind: "capture", parentSide, studentId: parentSide ? child.id : selfStudentId, childName: child?.name || "" });
    return undefined;
  };

  // The AI asks up to 3 short questions (date, subject) before the review card.
  const captureRound = async (ctx, text, image) => {
    const view = ctx.parentSide ? "parent" : "eleve";
    mvpDash(18, 1, view);
    ctx.text = [ctx.text, text].filter(Boolean).join("\n");
    ctx.rounds = (ctx.rounds || 0) + 1;
    if (image) ctx.image = image;
    const draft = await engagementApi.extractCapture({
      studentId: ctx.studentId, text: ctx.text, image: ctx.image, audience: ctx.parentSide ? "parent" : "student",
    });
    const incomplete = !draft.items.length || draft.items.some((item) => !item.dueDate);
    if (incomplete && draft.question && ctx.rounds <= 3) {
      captureChatRef.current = ctx;
      sideSay(ctx.parentSide, "c.ask", { question: draft.question });
      return draft;
    }
    captureChatRef.current = null;
    return showCaptureReview(ctx, draft);
  };

  const submitCapture = (msg) => async ({ text, image }) => {
    const ctx = captureChatRef.current?.studentId === msg.studentId ? captureChatRef.current : { parentSide: msg.parentSide, studentId: msg.studentId };
    return captureRound(ctx, text, image);
  };

  const showCaptureReview = (msg, draft) => {
    const view = msg.parentSide ? "parent" : "eleve";
    sideSection(msg.parentSide, 19);
    mvpDash(19, 0, view);
    if (!draft.items.length) {
      sideSay(msg.parentSide, msg.parentSide ? "c.noneParent" : "c.none");
      return sideCard(msg.parentSide, { kind: "captureReview", parentSide: msg.parentSide, draft, studentId: msg.studentId });
    }
    if (captureKeepRef.current.length) {
      draft = { ...draft, items: [...captureKeepRef.current, ...draft.items.map((item) => ({ ...item, key: `${item.key}b${Date.now() % 1000}` }))] };
      captureKeepRef.current = [];
    }
    captureDraftRef.current = { draft, parentSide: msg.parentSide, studentId: msg.studentId };
    sideSay(msg.parentSide, msg.parentSide ? "c.foundParent" : "c.found", { count: draft.items.length });
    const sorted = [...draft.items].sort((a, b) => String(a.dueDate || "9").localeCompare(String(b.dueDate || "9")));
    sideCard(msg.parentSide, { kind: "infoList", list: "deadlines", title: "Échéances", subtitle: "Devoirs et examens", rows: dueRows(sorted) }, 800);
    const actions = [["cap-build", "Construire les plans", true], ["cap-edit", "Vérifier ou modifier"], ["cap-more", "Ajouter une autre photo"]];
    later(() => sideSay(
      msg.parentSide,
      msg.parentSide ? "c.buildAskParent" : "c.buildAsk",
      {},
      msg.parentSide ? actions.map(([id, label, primary]) => v0Reply(id, label, Boolean(primary))) : actions.map(([id, label, primary]) => ({ id, label, primary: Boolean(primary) })),
    ), 1500);
    return undefined;
  };

  const captureAction = (id) => {
    const pending = captureDraftRef.current;
    if (!pending) return undefined;
    if (id === "cap-build") {
      captureDraftRef.current = null;
      return confirmCaptureItems(pending)(pending.draft.items).catch((problem) => sideSay(pending.parentSide, pending.parentSide ? "c.errorParent" : "c.error", { error: problem.message }));
    }
    if (id === "cap-edit") return sideCard(pending.parentSide, { kind: "captureReview", ...pending }, 300);
    captureKeepRef.current = pending.draft.items;
    captureDraftRef.current = null;
    return startCapture(pending.parentSide);
  };

  const confirmCaptureItems = (msg) => async (items) => {
    const result = await engagementApi.confirmCapture(msg.draft.id, items);
    mvpDash(19, 1, msg.parentSide ? "parent" : "eleve");
    const names = [...result.created.map((plan) => `« ${plan.title} »`), ...result.attached.map((plan) => `« ${plan.title} » (complété)`)];
    const summary = names.length ? `${names.length} plan${names.length > 1 ? "s" : ""} : ${names.join(", ")}.` : "Aucun plan enregistré.";
    sideSay(msg.parentSide, msg.parentSide ? "c.savedParent" : "c.saved", { summary, student: studentName }, msg.parentSide ? parentHomeReplies() : []);
    if (result.failed.length) sideSay(msg.parentSide, "c.partial", { failed: result.failed.map((row) => `« ${row.title} » (${row.reason})`).join(", ") });
    if (!msg.parentSide && names.length) later(() => startSchedule(), 1800);
  };

  const cancelCapture = (msg) => () => {
    captureChatRef.current = null;
    engagementApi.cancelCapture(msg.draft.id).catch(() => null);
    sideSay(msg.parentSide, "c.cancelled", {}, msg.parentSide ? parentHomeReplies() : studentLot3Replies());
  };

  const startSchedule = () => {
    setQuickReplies([]);
    studentSection(20);
    mvpDash(20, 0);
    sayStudent("s.askAvail");
    pushStudentCard({ kind: "schedule" }, 900);
  };
  const onProposal = (data) => {
    mvpDash(20, 1);
    if (!data.slots.length && !data.unplaced.length) return sayStudent("s.nothing", {}, quickAsk);
    sayStudent("s.proposal", { count: data.slots.length });
    if (data.unplaced.length) sayStudent("s.tight", { list: data.unplaced.map((row) => `« ${row.title} » : ${row.count} séance(s) non placée(s)`).join(", ") });
    return undefined;
  };
  const onScheduleConfirmed = (data) => {
    mvpDash(20, 2);
    sayStudent("s.confirmed", { count: data.created }, quickAsk);
  };

  const askAssistant = async (intent, text = "") => {
    setQuickReplies([]);
    studentSection(21);
    mvpDash(21, 0);
    const res = await engagementApi.assistant(selfStudentId, { intent, text }).catch(() => ({ intent: "UNKNOWN", data: {} }));
    mvpDash(21, 1);
    const { data } = res;
    if (res.intent === "LIST_ACTIVE_PLANS") {
      if (!data.plans?.length) return sayStudent("q.noPlans", {}, [selfPlanReply, captureReply]);
      sayStudent("q.plans", {}, quickAsk);
      return pushStudentCard({ kind: "infoList", list: "plans", title: "Plans d’étude", subtitle: "Séances faites cette semaine", rows: data.plans.map((plan) => ({ left: `${plan.doneThisWeek}/${plan.perWeek}`, right: plan.title, sub: plan.dueDate ? `Échéance ${shortDay(plan.dueDate).toLowerCase()}` : "Chaque semaine" })) }, 700);
    }
    if (res.intent === "LIST_UPCOMING_DEADLINES") {
      if (!data.deadlines?.length) return sayStudent("q.noDeadlines", {}, [captureReply, studentMenuReply]);
      sayStudent("q.deadlines", {}, quickAsk);
      return pushStudentCard({ kind: "infoList", list: "deadlines", title: "Échéances", subtitle: "Devoirs et examens", rows: dueRows(data.deadlines) }, 700);
    }
    if (res.intent === "GET_NEXT_SESSION" || res.intent === "RESCHEDULE_SESSION") {
      const next = data.session;
      if (!next) return sayStudent("q.noNext", {}, [{ id: "e-horaire", label: "Planifier ma semaine" }, ...quickAsk]);
      if (res.intent === "RESCHEDULE_SESSION") return moveSession(next);
      return sayStudent("q.next", { title: next.planTitle, when: `${fmtDay(next.date)} à ${next.time}` }, [{ id: `x-open:${next.id}:${next.planId}`, label: "Ouvrir la séance", primary: true }, { id: `x-move:${next.id}`, label: "Reporter" }, studentMenuReply]);
    }
    if (res.intent === "GET_WEEK_SCHEDULE") {
      if (!data.sessions?.length) return sayStudent("q.noNext", {}, [{ id: "e-horaire", label: "Planifier ma semaine" }]);
      sayStudent("q.week", {}, [studentMenuReply]);
      return pushStudentCard({ kind: "infoList", list: "sessions", title: "Ma semaine", subtitle: "Prochaines séances", rows: sessionRows(data.sessions) }, 700);
    }
    if (res.intent === "START_20_MIN_SESSION") {
      if (data.session) return sayStudent("q.now", { title: data.session.planTitle, minutes: data.session.suggestedMinutes }, [{ id: `x-go:${data.session.id}:${data.session.planId}`, label: "Commencer", primary: true }]);
      if (data.plan) return sayStudent("q.now", { title: data.plan.title, minutes: data.plan.suggestedMinutes }, [{ id: `x-now:${data.plan.id}`, label: "Commencer", primary: true }]);
      return sayStudent("q.nothing", {}, [selfPlanReply, captureReply]);
    }
    if (res.intent === "CAPTURE_SCHOOLWORK") return startCapture(false);
    if (res.intent === "REQUEST_AI_HELP") return studentAiHelp();
    if (res.intent === "REQUEST_HUMAN_HELP") return studentHelp();
    return sayStudent("q.unknown", {}, quickAsk);
  };

  const todayKey = () => {
    const now = appMode ? new Date() : simClock.now();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  };

  // Home by priority: next séance, deadlines (never plans), week schedule, plans in progress — simple lists.
  const priorityCards = async (studentId, childName = "") => {
    const [due, week, nextRes] = await Promise.all([
      engagementApi.assistant(studentId, { intent: "LIST_UPCOMING_DEADLINES" }).catch(() => ({ data: {} })),
      engagementApi.weekPlan(studentId).catch(() => ({ plans: [], sessions: [] })),
      engagementApi.assistant(studentId, { intent: "GET_NEXT_SESSION" }).catch(() => ({ data: {} })),
    ]);
    const of = childName ? ` ${ofName(childName)}` : "";
    const today = todayKey();
    const plans = (week.plans || []).filter((plan) => !plan.status || plan.status === "active");
    const titles = Object.fromEntries(plans.map((plan) => [plan.id, plan.title]));
    const deadlines = (due.data?.deadlines || []).filter((row) => row.type !== "plan").slice(0, 5);
    const planned = (week.sessions || []).filter((row) => ["planned", "started"].includes(row.status) && row.date >= today);
    const next = nextRes.data?.session || null;
    const cards = [];
    if (next) cards.push({ kind: "infoList", list: "next", title: `Prochaine séance d’étude${of}`, rows: [{ left: `${shortDay(next.date)} · ${next.time}`, right: next.planTitle || titles[next.planId] || "Séance" }] });
    if (deadlines.length) cards.push({ kind: "infoList", list: "deadlines", title: `Échéances${of}`, subtitle: "Devoirs et examens", rows: dueRows(deadlines) });
    if (planned.length) cards.push({ kind: "infoList", list: "sessions", title: `Horaire de la semaine${of}`, subtitle: "Séances prévues", rows: sessionRows(planned, titles) });
    if (plans.length) cards.push({ kind: "infoList", list: "plans", title: `Plans d’étude en cours${of}`, subtitle: "Séances faites cette semaine", rows: plans.map((plan) => ({ left: `${plan.doneThisWeek || 0}/${plan.perWeek || 0}`, right: plan.title })) });
    const start = next
      ? { id: next.date === today ? `x-go:${next.id}:${next.planId}` : `x-now:${next.planId}`, label: "Débuter", primary: true }
      : plans[0] ? { id: `x-now:${plans[0].id}`, label: "Débuter", primary: true } : null;
    return { cards, start, running: next?.status === "started" && localStorage.getItem(`sp.timer.${next.id}`) ? next : null };
  };

  // Flow 24: simple check-in — upcoming, to do, planned, then start the next séance.
  const studentCheckin = async () => {
    setQuickReplies([]);
    studentSection(24);
    mvpDash(24, 0);
    const [due, week, nextRes] = await Promise.all([
      engagementApi.assistant(selfStudentId, { intent: "LIST_UPCOMING_DEADLINES" }).catch(() => ({ data: {} })),
      engagementApi.weekPlan(selfStudentId).catch(() => ({ plans: [], sessions: [] })),
      engagementApi.assistant(selfStudentId, { intent: "START_20_MIN_SESSION" }).catch(() => ({ data: {} })),
    ]);
    const deadlines = (due.data?.deadlines || []).slice(0, 3);
    const plans = (week.plans || []).filter((plan) => !plan.status || plan.status === "active");
    const titles = Object.fromEntries(plans.map((plan) => [plan.id, plan.title]));
    const nowKey = todayKey();
    const planned = (week.sessions || []).filter((row) => ["planned", "started"].includes(row.status) && row.date >= nowKey);
    const left = plans.reduce((sum, plan) => sum + Math.max(0, (plan.perWeek || 0) - (plan.doneThisWeek || 0)), 0);
    if (deadlines.length) {
      sayStudent("k.upcoming");
      pushStudentCard({ kind: "infoList", list: "deadlines", title: "Ce qui s’en vient", subtitle: "Les 3 prochaines échéances", rows: dueRows(deadlines) }, 1000);
    } else sayStudent("k.noUpcoming");
    later(() => {
      mvpDash(24, 1);
      if (!plans.length) return sayStudent("k.noTodo");
      sayStudent("k.todo", { left });
      return pushStudentCard({ kind: "infoList", list: "plans", title: "Ce que tu as à faire", subtitle: "Séances faites cette semaine", rows: plans.map((plan) => ({ left: `${plan.doneThisWeek || 0}/${plan.perWeek || 0}`, right: plan.title, sub: plan.dueDate ? `Échéance ${shortDay(plan.dueDate).toLowerCase()}` : "Chaque semaine" })) }, 1000);
    }, 2600);
    later(() => {
      mvpDash(24, 2);
      if (!planned.length) return sayStudent("k.noPlanned");
      sayStudent("k.planned");
      return pushStudentCard({ kind: "infoList", list: "sessions", title: "Ce qui est prévu", subtitle: "Mes prochaines séances", rows: sessionRows(planned, titles) }, 1000);
    }, 5200);
    later(async () => {
      mvpDash(24, 3);
      const laterReply = { id: "k-later", label: "Plus tard" };
      const next = (await engagementApi.assistant(selfStudentId, { intent: "GET_NEXT_SESSION" }).catch(() => ({ data: {} }))).data?.session;
      if (next) {
        const go = next.date === nowKey ? `x-go:${next.id}:${next.planId}` : `x-now:${next.planId}`;
        return sayStudent("k.start", { title: next.planTitle || titles[next.planId] || "ta séance", when: `${fmtDay(next.date)} à ${next.time}` }, [{ id: go, label: "Commencer maintenant", primary: true }, laterReply, studentMenuReply]);
      }
      const plan = nextRes.data?.plan;
      if (plan) return sayStudent("k.startPlan", { title: plan.title, minutes: plan.suggestedMinutes }, [{ id: `x-now:${plan.id}`, label: "Commencer maintenant", primary: true }, laterReply, studentMenuReply]);
      return sayStudent("k.nothing", {}, [selfPlanReply, captureReply, studentMenuReply]);
    }, 7800);
  };

  // Flow 22: the séance itself (opened from a reminder, a question or the schedule).
  const sessionChoice = (session, plan) => {
    studentSection(22);
    mvpDash(22, 0);
    const title = session?.planTitle || plan?.title || "ta séance";
    const replies = session
      ? [
          { id: `x-go:${session.id}:${session.planId}`, label: "Je commence", primary: true },
          { id: `x-snooze:${session.id}`, label: "Dans 20 min" },
          { id: `x-move:${session.id}`, label: "Reporter" },
          { id: "q-week", label: "Voir mon horaire" },
        ]
      : [{ id: `x-now:${plan.id}`, label: "Je commence", primary: true }, { id: "q-week", label: "Voir mon horaire" }];
    sayStudent("x.choice", { title, minutes: session?.suggestedMinutes || plan?.suggestedMinutes }, replies);
  };

  const beginSession = async (sessionId, planId) => {
    setQuickReplies([]);
    try {
      const res = sessionId ? await engagementApi.startSession(sessionId) : await engagementApi.startNow(planId);
      mvpDash(22, 1);
      const minutes = res.plan?.suggestedMinutes || 20;
      sayStudent("x.start", { title: res.plan?.title, minutes }, [{ id: "e-help", label: "Je bloque" }, { id: "q-week", label: "Voir mon horaire" }]);
      pushStudentCard({ kind: "sessionTimer", plan: res.plan, minutes, timerKey: res.session?.id || sessionId || `now-${planId}-${Date.now()}` }, 1000);
    } catch (problem) {
      sayStudent("c.error", { error: problem.message }, quickAsk);
    }
  };

  const snoozeSession = async (sessionId) => {
    setQuickReplies([]);
    try {
      const week = await engagementApi.snoozeSession(sessionId);
      const row = week.sessions.find((item) => item.id === sessionId);
      sayStudent("x.snooze", { time: row?.time || "" }, studentLot3Replies());
    } catch (problem) {
      sayStudent("c.error", { error: problem.message }, [{ id: `x-move:${sessionId}`, label: "Reporter" }]);
    }
  };

  const moveSession = async (session) => {
    setQuickReplies([]);
    studentSection(22);
    mvpDash(22, 0);
    let target = session;
    if (typeof session === "string") {
      const week = await engagementApi.weekPlan(selfStudentId).catch(() => ({ sessions: [] }));
      target = week.sessions.find((item) => item.id === session);
    }
    if (!target) return sayStudent("q.noNext", {}, quickAsk);
    sayStudent("x.move");
    pushStudentCard({ kind: "moveSession", session: target }, 800);
    return undefined;
  };

  const afterCheckin = async () => {
    const week = await engagementApi.weekPlan(selfStudentId).catch(() => null);
    if (!week) return;
    const res = await engagementApi.assistant(selfStudentId, { intent: "GET_NEXT_SESSION" }).catch(() => null);
    const next = res?.data?.session;
    studentSection(22);
    mvpDash(22, 2);
    const left = week.plans.reduce((sum, plan) => sum + Math.max(0, plan.perWeek - plan.doneThisWeek), 0);
    sayStudent("x.after", { left, next: next ? `${fmtDay(next.date)} à ${next.time} (« ${next.planTitle} »)` : "" }, quickAsk);
  };

  // Flow 23: AI "Je bloque" (Famille), else Flow 11 human help.
  const studentAiHelp = async () => {
    setQuickReplies([]);
    if (familyLevel < 2) {
      studentSection(23);
      sayStudent("a.locked");
      return later(() => studentHelp(), 1400);
    }
    const plans = await engagementApi.listPlans(selfStudentId).catch(() => []);
    const active = plans.filter((plan) => plan.status === "active");
    studentSection(23);
    mvpDash(23, 0);
    if (!active.length) return sayStudent("h.noActive", {}, [selfPlanReply]);
    sayStudent("a.intro");
    pushStudentCard({ kind: "aiHelpAsk", plan: active[0] }, 900);
    return undefined;
  };
  const submitAiHelp = (msg) => async ({ text, image }) => {
    const res = await engagementApi.aiHelpStart({ planId: msg.plan.id, text, image });
    aiHelpRef.current = res.session.id;
    mvpDash(23, 1);
    pushStudentCard({ kind: "aiHelpTurn", turn: res.turn, triesLeft: res.triesLeft }, 400);
  };
  const onAiHelpAction = async (id) => {
    const sessionId = aiHelpRef.current;
    if (id === "again") {
      try {
        const res = await engagementApi.aiHelpTurn(sessionId, { again: true });
        pushStudentCard({ kind: "aiHelpTurn", turn: res.turn, triesLeft: res.triesLeft }, 400);
      } catch {
        sayStudent("a.limit", {}, [{ id: "a-human", label: "Besoin d’un tuteur" }, { id: "a-back", label: "Retour à mon plan" }]);
      }
      return;
    }
    await engagementApi.aiHelpOutcome(sessionId, id);
    mvpDash(23, 2);
    if (id === "resolved") sayStudent("a.resolved", {}, quickAsk);
    if (id === "needs_practice") sayStudent("a.practice", {}, quickAsk);
    if (id === "human_help_requested") {
      sayStudent("a.human", {}, studentLot3Replies());
      flowDash(11, 1, "eleve");
    }
  };

  const studentHelpUpdates = async () => {
    const items = await engagementApi.listHelp("all").catch(() => []);
    const weekAgo = simClock.now().getTime() - 7 * 86400000;
    const seenKey = `sp.help.seen.${viewerId}`;
    const seen = localStorage.getItem(seenKey) || "";
    const fresh = items.filter(
      (item) =>
        item.thread.some((event) => event.role === "parent") &&
        new Date(item.updatedAt).getTime() >= weekAgo &&
        item.updatedAt > seen
    );
    if (fresh.length) localStorage.setItem(seenKey, fresh.reduce((max, item) => (item.updatedAt > max ? item.updatedAt : max), seen));
    if (!fresh.length) return;
    studentSection(11);
    flowDash(11, 2, "eleve");
    sayStudent("h.studentUpdate", {}, studentLot3Replies());
    fresh.slice(0, 3).forEach((item, index) => pushStudentCard({ kind: "helpCard", request: item }, 900 + index * 200));
  };

  const parentHelpView = async (quiet = false) => {
    const items = await engagementApi.listHelp().catch(() => []);
    if (!items.length) {
      if (!quiet) addParent("h.parentNone", parentHomeReplies(), { child: parentChildRef.current?.name || "" });
      return false;
    }
    parentSection(11);
    flowDash(11, 1, "parent");
    addParent("h.parentAlert", [], { child: items[0].studentName });
    items.slice(0, 3).forEach((item, index) => pushParentCard({ kind: "helpCard", request: item }, 1000 + index * 200));
    later(() => setParentReplies(parentHomeReplies()), 1500);
    return true;
  };

  const onHelpChange = (viewer) => (item, kind) => {
    if (viewer === "parent") {
      flowDash(11, kind === "resolve" ? 3 : 2, "parent");
      addParent("h.parentDone", parentHomeReplies(), { child: item.studentName });
      if (!appMode) pushStudentCard({ kind: "helpCard", request: item }, 600);
      return;
    }
    if (!appMode) pushParentCard({ kind: "helpCard", request: item }, 600);
  };

  const showReport = async (viewer, pendingWeek = "") => {
    const say = viewer === "parent" ? (key) => addParent(key, [], {}) : (key) => sayStudent(key, {}, studentEndReplies);
    if (viewer === "parent") parentSection(12);
    else studentSection(12);
    flowDash(12, pendingWeek ? 1 : 2, viewer);
    const reports = await engagementApi.weeklyReport(pendingWeek).catch(() => []);
    say(pendingWeek ? "r.pending" : "r.onDemand");
    const push = viewer === "parent" ? pushParentCard : pushStudentCard;
    reports.forEach((report, index) => push({ kind: "reportCard", report }, 1000 + index * 200));
    if (pendingWeek) engagementApi.markReportSeen(pendingWeek).catch(() => {});
    if (viewer === "parent") later(() => setParentReplies([v0Reply("home-menu", "Menu"), v0Reply("home-end", "Terminer la session")]), 1500);
  };

  const lot3OnOpen = async (viewer) => {
    const askedReport = typeof window !== "undefined" && new URLSearchParams(window.location.search).get("bilan") === "1";
    const week = await engagementApi.pendingReport().catch(() => null);
    if (week || askedReport) await showReport(viewer, week || "");
    if (viewer === "parent") await parentHelpView(true);
    else await studentHelpUpdates();
  };

  // ---------- Simulated clock (simulator only) ----------
  const simNotice = (target, channel, text) => {
    const note = { id: `sim-${Date.now()}-${Math.random()}`, sender: "system", kind: "simNotice", channel, text, time: simClock.now().toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" }) };
    if (target === "parent") setParentMessages((current) => [...current, note]);
    else setMessages((current) => [...current, note]);
  };
  const advanceClock = (id) => {
    const now = simClock.now();
    const target = new Date(now);
    if (id === "next") {
      const next = simNextReminder();
      if (!next) {
        simNotice("eleve", "Info", "Aucun rappel prévu : planifie d’abord une séance (Flow 16).");
        return;
      }
      target.setTime(next.getTime());
    }
    if (id === "plus15") target.setMinutes(now.getMinutes() + 15);
    if (id === "plus60") target.setHours(now.getHours() + 1);
    if (id === "tomorrow") target.setDate(now.getDate() + 1), target.setHours(16, 0, 0, 0);
    if (id === "saturday") target.setDate(now.getDate() + (((6 - now.getDay() + 7) % 7) || 7)), target.setHours(12, 0, 0, 0);
    if (id === "monday") target.setDate(now.getDate() + (((1 - now.getDay() + 7) % 7) || 7)), target.setHours(8, 0, 0, 0);
    simClock.advance(target.getTime() - now.getTime());
    setClockNow(simClock.now());
    showRemindersFired(simReminders(now, simClock.now()));
    if (id === "saturday") {
      flowDash(12, 0, role === "parent" ? "parent" : "eleve");
      simNotice("parent", "Courriel", "Courriel simulé : « Votre bilan Plan Étude est prêt » · bouton Voir le bilan");
      simNotice("eleve", "Courriel", "Courriel simulé : « Ton bilan est prêt » · bouton Voir le bilan");
    }
    if (id === "monday") {
      engagementApi.pendingReport().then((week) => {
        if (!week) return simNotice(role === "parent" ? "parent" : "eleve", "Info", "Aucun engagement : pas de bilan à afficher.");
        showReport("parent", week);
        return showReport("eleve", week);
      });
    }
  };
  const resetClock = () => {
    simClock.reset();
    smsRef.current = { day: "", count: 0 };
    setClockNow(simClock.now());
  };

  // ---------- Flow 1 — parent welcome V0 ----------
  const v0Reply = (id, label, primary = false, extra = {}) => ({ id: `pv0-${id}`, label, primary, v0: true, ...extra });
  // 2 suggestions for the likely next step, plus Menu (opens ☰). Everything else is in the menu.
  const parentHomeReplies = () => {
    const { kids, open } = parentStateRef.current;
    const menu = v0Reply("home-menu", "Menu");
    if (kids === 0) return [v0Reply("home-invite", "Inviter un enfant", true), menu];
    if (open) return [v0Reply("home-follow", "Voir le suivi des plans"), v0Reply("home-week", "Voir l’horaire de la semaine"), menu];
    return [v0Reply("home-plan", "Proposer un plan d’étude"), v0Reply("home-capture", "Capturer des échéances"), menu];
  };
  const refreshParentState = async () => {
    const kids = await engagementApi.listChildren().catch(() => []);
    const lists = await Promise.all(kids.map((kid) => engagementApi.listPlans(kid.id).catch(() => [])));
    parentStateRef.current = { kids: kids.length, open: lists.flat().some((plan) => OPEN_PLAN_STATUSES.includes(plan.status)) };
  };

  const parentStepDash = (flowStep, status) => {
    setWire(null);
    setDash((current) => ({ ...current, view: "parent", flowId: 1, flowStep, status }));
  };

  const flow10Dash = (flowStep) => {
    setWire(null);
    setDash((current) => ({ ...current, view: "parent", flowId: 10, flowStep, status: FLOW10_STEPS[flowStep] }));
  };

  const home13Dash = (flowStep = 0) => {
    setWire(null);
    setDash((current) => ({ ...current, view: "parent", flowId: 13, flowStep, status: FLOW13_STEPS[flowStep] }));
  };

  // Shows the flow title in the parent chat when a new flow starts.
  const parentSection = (flowId) => {
    const text = flowTitle(flowId);
    setParentMessages((current) => {
      const last = [...current].reverse().find((item) => item.kind === "section");
      if (last?.text === text) return current;
      return [...current, { id: `section-${flowId}-${Date.now()}`, sender: "system", kind: "section", text, time: nowStamp() }];
    });
  };

  const addParent = (key, replies = [], ctx = {}) => {
    const childName =
      ctx.child !== undefined ? ctx.child : parentChildRef.current?.name || (hasStudent ? studentName : "");
    const text = getFlowMessage(key).text({ parentName, studentName, ...ctx, child: childName });
    setTyping(true);
    later(() => {
      setTyping(false);
      const [message] = withRevision([
        {
          id: `pa-${Date.now()}-${Math.random()}`,
          sender: "agent",
          messageKey: key,
          text,
          child: childName,
          time: nowStamp(),
        },
      ]);
      const parts = SPLIT_KEYS.has(key) ? message.text.split(/\n\s*\n/).filter((part) => part.trim()) : [message.text];
      parts.forEach((part, index) =>
        later(() => {
          setTyping(index < parts.length - 1);
          setParentMessages((current) => [...current, index ? { ...message, id: `${message.id}-${index}`, messageKey: undefined, text: part } : { ...message, text: part }]);
          if (index === parts.length - 1) {
            setParentReplies(replies);
            setLiveKey(key);
          }
        }, index * 1100)
      );
    }, 650);
  };

  const pushParentCard = (card, delay = 1100) =>
    later(() => {
      setParentMessages((current) => [
        ...current,
        { id: `pc-${Date.now()}-${Math.random()}`, sender: "agent", time: nowStamp(), ...card },
      ]);
    }, delay);

  const loadChildren = async () => {
    if (appMode && accountToken) {
      try {
        const data = await apiFetch("/api/family", accountToken);
        return (data.students || []).filter((item) => item.status !== "removed");
      } catch {
        return [];
      }
    }
    return hasStudent ? [{ id: "sim-student", name: studentName, status: "active", simulated: true }] : [];
  };

  const parentWelcome = async () => {
    // One welcome per start: a second call (StrictMode, fast re-render) cancels the first.
    const run = ++parentRunRef.current;
    parentChildRef.current = null;
    const kids = await loadChildren();
    if (run !== parentRunRef.current) return;
    parentKidsRef.current = kids;
    const activeKids = kids.filter((kid) => kid.status === "active");
    if (appMode && onboardedRef.current) {
      if (activeKids.length === 1) parentChildRef.current = activeKids[0];
      else if (kids.length === 1) parentChildRef.current = kids[0];
      parentHome();
      return;
    }
    if (appMode && activeKids.length) {
      const plans = await engagementApi.listPlans(activeKids[0].id).catch(() => []);
      if (run !== parentRunRef.current) return;
      if (plans.length) {
        if (activeKids.length === 1) parentChildRef.current = activeKids[0];
        parentHome();
        return;
      }
    }
    parentStepDash(0, PARENT_STEP_TITLES[0]);
    parentSection(1);
    addParent("p1.welcome", [v0Reply("ok", "Compris", true), v0Reply("example", "Voir un exemple")], {
      child: kids.length === 1 ? kids[0].name : "",
      count: kids.length,
    });
  };

  const parentInvite = (kid, next) => {
    const another = next === "home" && parentKidsRef.current.length > 0;
    addParent(another ? "p1.inviteAnother" : "p1.inviteFirst", [], { child: kid?.name || "" });
    pushParentCard({ kind: "inviteChild", childName: kid?.name || "", childEmail: kid?.email || "", next }, 1300);
  };

  const parentChildState = (kid) => {
    parentChildRef.current = kid;
    const followUp = [
      v0Reply("resend", "Renvoyer l’invitation"),
      v0Reply("fix-email", "Corriger le courriel"),
      v0Reply("child-next", "Continuer", true),
    ];
    if (kid.status === "active") {
      addParent("p1.childActive", [], { child: kid.name });
      later(() => parentFirstPlan(true), 1500);
    } else if (kid.status === "pending" || kid.status === "waiting") {
      addParent("p1.childPending", followUp, { child: kid.name, invitedAt: kid.invitedAt, email: kid.email });
    } else if (kid.status === "expired") {
      addParent("p1.childExpired", followUp, { child: kid.name });
    } else if (kid.status === "deactivated") {
      addParent("p1.childDeactivated", [], { child: kid.name });
      later(() => parentFirstPlan(), 1500);
    } else {
      parentInvite(kid, "plan");
    }
  };

  const parentChildStep = async () => {
    const run = ++parentRunRef.current;
    parentStepDash(1, PARENT_STEP_TITLES[1]);
    const kids = await loadChildren();
    if (run !== parentRunRef.current) return;
    parentKidsRef.current = kids;
    if (!kids.length) {
      parentInvite(null, "plan");
      return;
    }
    if (kids.length > 1) {
      addParent(
        "p1.chooseChild",
        kids.map((kid) => v0Reply(`child-${kid.id}`, kid.name || kid.email, false, { childId: kid.id })),
        { child: "" }
      );
      return;
    }
    parentChildState(kids[0]);
  };

  const parentFirstPlan = (active = parentChildRef.current?.status === "active") => {
    parentStepDash(2, PARENT_STEP_TITLES[2]);
    if (FEATURES.engagements) {
      addParent("p1.firstPlan", [v0Reply("plan-now", "Proposer maintenant", true), v0Reply("plan-later", "Plus tard")], { active });
      return;
    }
    addParent("p1.firstPlanSoon");
    later(() => parentFollow(), 1700);
  };

  const parentFollow = () => {
    parentSection(1);
    parentStepDash(3, PARENT_STEP_TITLES[3]);
    addParent("p1.follow", [v0Reply("follow-ok", "C’est compris", true)]);
  };

  const markOnboarded = () => {
    if (!appMode || onboardedRef.current || !accountToken) return;
    onboardedRef.current = true;
    const saved = loadSession();
    if (saved?.token === accountToken) saveSession({ ...saved, onboarded: true });
    apiFetch("/api/me/onboarded", accountToken, { method: "POST", body: "{}" }).catch(() => {});
  };

  const parentHome = async () => {
    markOnboarded();
    parentSection(13);
    home13Dash();
    await refreshParentState();
    const kids = await engagementApi.listChildren().catch(() => []);
    const kid = kids.find((item) => item.id === parentChildRef.current?.id) || kids[0];
    const { cards } = kid ? await priorityCards(kid.id, kid.name) : { cards: [] };
    if (cards.length) {
      addParent("p1.priorities", [], { child: kid.name });
      cards.forEach((card, index) => pushParentCard(card, 1300 + index * 700));
      later(() => addParent("p1.home", parentHomeReplies()), 1300 + cards.length * 700);
    } else addParent("p1.home", parentHomeReplies());
    if (!lot3DoneRef.current.parent) {
      lot3DoneRef.current.parent = true;
      later(() => lot3OnOpen("parent"), 2600 + cards.length * 700);
    }
  };

  const resendInvitation = async () => {
    const kid = parentChildRef.current;
    if (!kid) return parentFirstPlan();
    if (!appMode || kid.simulated || !accountToken) {
      addParent("p1.resent", [], { child: kid.name });
      later(() => agentNote("Simulation : aucun courriel envoyé.", "parent"), 900);
      later(() => parentFirstPlan(), 1700);
      return undefined;
    }
    try {
      const member = await apiFetch(`/api/family/students/${kid.id}/resend`, accountToken, { method: "POST" });
      addParent("p1.resent", [], { child: kid.name });
      if (!member.delivered && member.confirmPath) {
        pushParentCard({ kind: "localLink", href: member.confirmPath }, 900);
      }
      later(() => parentFirstPlan(), 1800);
    } catch (problem) {
      agentNote(problem.message || "Envoi impossible.", "parent");
      later(() => parentFirstPlan(), 1200);
    }
    return undefined;
  };

  const handleParentV0 = (reply) => {
    const id = reply.id.slice(4);
    if (id.startsWith("cap-")) return captureAction(id);
    if (id === "ok") return parentChildStep();
    if (id === "example") {
      pushParentCard({ kind: "planExample" }, 300);
      later(() => addParent("p1.exampleAfter", [v0Reply("ok", "Compris", true)]), 900);
      return undefined;
    }
    if (id.startsWith("pick-") && reply.childId) {
      const chosen = parentKidsRef.current.find((item) => item.id === reply.childId);
      if (chosen) {
        parentChildRef.current = chosen;
        childPickedRef.current = true;
      }
      const next = { follow: parentFollowView, capture: () => startCapture(true), deadlines: parentDeadlinesView, week: parentWeekView, history: () => showHistory(true) }[reply.then];
      return next ? next() : parentPropose(reply.next || "home");
    }
    if (id.startsWith("child-") && reply.childId) {
      const kid = parentKidsRef.current.find((item) => item.id === reply.childId);
      if (kid) parentChildState(kid);
      return undefined;
    }
    if (id === "resend") return resendInvitation();
    if (id === "fix-email") {
      const kid = parentChildRef.current;
      if (!kid) return parentFirstPlan();
      pushParentCard({ kind: "inviteChild", childName: kid.name || "", childEmail: kid.email || "", memberId: kid.simulated ? "" : kid.id, next: "plan" }, 300);
      return undefined;
    }
    if (id === "child-next") return parentFirstPlan();
    if (id === "plan-later") return parentFollow();
    if (id === "plan-now") return parentPropose("follow");
    if (id === "follow-ok") return parentHome();
    if (id === "home-plan") {
      if (!FEATURES.engagements) return addParent("p1.soon", parentHomeReplies());
      return parentPropose("home");
    }
    if (id === "home-follow") return parentFollowView();
    if (id === "home-deadlines") return parentDeadlinesView();
    if (id === "home-week") return parentWeekView();
    if (id === "home-capture") return startCapture(true);
    if (id === "free-continue") {
      const resume = freeChatResumeRef.current;
      return resume.length ? setParentReplies(resume) : parentHome();
    }
    if (id === "home-help") return parentHelpView();
    if (id === "home-report") return showReport("parent");
    if (id === "home-invite") {
      return loadChildren().then((kids) => {
        parentKidsRef.current = kids;
        parentInvite(null, "home");
      });
    }
    if (id === "home-menu") {
      setSideMenuOpen(true);
      setParentReplies(parentHomeReplies());
      return undefined;
    }
    if (id === "home-end") {
      addParent("p1.goodbye");
      if (appMode && onLogout) later(onLogout, 2200);
      else later(() => setParentReplies([v0Reply("home-menu", "Menu")]), 900);
      return undefined;
    }
    return undefined;
  };

  const showParentHome = () => {
    setParentReplies([]);
    parentHome();
  };

  const parentInviteFromHome = () => {
    setParentReplies([]);
    home13Dash();
    parentInvite(null, "home");
  };

  const PARENT_V0_NODES = {
    "s1-0": parentWelcome,
    "s1-1": parentChildStep,
    "s1-2": parentFirstPlan,
    "s1-3": parentFollow,
    "s1-4": parentHome,
    "s13-0": parentHome,
    "s13-1": parentHome,
    "s10-0": () => parentPropose("home"),
    "s10-1": () => parentPropose("home"),
    "s10-2": () => parentPropose("home"),
  };

  const levelOneNotice = (command) => {
    const soon = N1_MENU.find(([item, , , tag]) => item === `/${command}` && tag === "soon");
    if (soon) return `« ${soon[1]} » arrive avec les engagements. Écris /menu.`;
    if (Object.prototype.hasOwnProperty.call(ARCHIVED_COMMANDS, command)) {
      return "Cette fonction est rangée pour l’instant. Écris /menu pour voir ce qui est disponible.";
    }
    return "";
  };

  const showMenu = (title) => {
    if (levelOne) {
      setQuickReplies([]);
      later(() => agentNote(`${title}\n\n${n1MenuText()}\n\n/menu rouvre cette liste.`, role === "parent" ? "parent" : "eleve"), 250);
      if (role !== "parent") later(() => setQuickReplies(studentLot3Replies()), 450);
      return;
    }
    setQuickReplies([
      { id: "menu-objectifs", label: "Objectifs" },
      { id: "menu-dispos", label: "Dispos" },
      { id: "menu-liste", label: "Liste" },
      { id: "menu-plan", label: "Plan" },
    ]);
    later(() => {
      setMessages((current) => [
        ...current,
        {
          id: `menu-${Date.now()}`,
          sender: "agent",
          text: `${title}\n\n${LAB_MENU}\n\n/menu ou /fonctions rouvre cette liste.`,
          time: nowStamp(),
        },
      ]);
    }, 250);
  };

  const showDocument = (kind) => {
    if (kind === "plan") {
      if (!dash.planReady) {
        later(() => {
          setMessages((current) => [
            ...current,
            {
              id: `doc-plan-${Date.now()}`,
              sender: "agent",
              text: "Aucun plan de la semaine pour l’instant. Écris /plan pour le créer.",
              time: nowStamp(),
            },
          ]);
        }, 250);
        return;
      }
      later(() => pushKind("readyplan"), 250);
      return;
    }
    if (kind === "objectif") {
      const goal = dash.objective || emptyObjective();
      if (!goal.subject) {
        later(() => {
          setMessages((current) => [
            ...current,
            {
              id: `doc-goal-${Date.now()}`,
              sender: "agent",
              messageKey: "f3.rhythm",
              text: `${
                typeof revisionsRef.current.overrides?.["f3.rhythm"] === "string"
                  ? revisionsRef.current.overrides["f3.rhythm"]
                  : RHYTHM_MESSAGE
              }\n\nÉcris /objectifs pour la remplir.`,
              time: nowStamp(),
            },
          ]);
        }, 250);
        return;
      }
      later(() => pushKind("objective", { objective: goal }), 250);
      return;
    }
    later(() => pushKind("parentSummary"), 250);
  };

  const showAvailability = (fromMenu = false) => {
    availFromMenuRef.current = fromMenu;
    setDash((current) => ({
      ...current,
      flowId: 2,
      flowStep: 3,
      status: "Disponibilités",
      availability: current.availability || seedAvailability(),
    }));
    markSection(2, true);
    later(() => {
      addAgent("f2.avail", []);
      later(() => {
        pushKind("availability");
        setQuickReplies(fromMenu ? [{ id: "f2-avail-ok", label: "Enregistrer et revenir", primary: true }] : FLOW2_REPLIES.avail);
      }, 1200);
    }, 250);
  };

  const showObjectiveList = () => {
    const goal = dash.objective || emptyObjective();
    if (!goal.subject) {
      later(() => {
        setMessages((current) => [
          ...current,
          {
            id: `doc-list-${Date.now()}`,
            sender: "agent",
            text: "Aucun objectif en cours.\nÉcris /objectifs pour en définir un.",
            time: nowStamp(),
          },
        ]);
      }, 250);
      return;
    }
    later(() => {
      setMessages((current) => [
        ...current,
        {
          id: `doc-list-${Date.now()}`,
          sender: "agent",
          text: "Objectifs en cours",
          time: nowStamp(),
        },
      ]);
      pushKind("objective", { objective: goal });
    }, 250);
  };

  const studentMenuAliases = {
    plans: ["plans", "mes plans", "plans d'étude", "plans d’étude"],
    dispos: ["dispos", "disponibilite", "disponibilité", "disponibilites", "disponibilités"],
    horaire: ["horaire", "plan", "voir plan", "4"],
    objectifs: ["objectifs", "objectif", "3"],
    echeances: ["agenda", "echeance", "échéance", "echeances", "échéances", "9"],
  };
  const studentMenuCommand = (command) => Object.keys(studentMenuAliases).find((key) => studentMenuAliases[key].includes(command));

  const runCommand = (raw) => {
    const command = raw.trim().toLowerCase().replace(/^\//, "").replace(/\s+/g, " ");
    if (command === "menu") return showMenu("Menu");
    if (levelOne) {
      if (command === "fonctions" || command === "fonctons") return showMenu("Fonctions");
      if (command === "inviter" && role === "parent") return parentInviteFromHome();
      if (command === "proposer") return role === "parent" ? parentPropose("home") : studentSelfPlan();
      if (role === "parent" && (command === "plans" || command === "mes plans")) return parentFollowView();
      if (command === "engagement" || command === "realisation" || command === "réalisation" || command === "suivi" || command === "6") {
        return role === "parent" ? parentFollowView() : showStudentHome();
      }
      if (command === "bilan" || command === "7") return showReport(role === "parent" ? "parent" : "eleve");
      if (command === "aide") return role === "parent" ? parentHelpView() : studentHelp();
      if (role !== "parent") {
        // Student accueil menu (Flow 14): level-2 parts reopened from here only.
        const menu = studentMenuCommand(command);
        if (command === "accueil") return showStudentHome();
        if (["étude", "etude"].includes(command)) return studentSelfPlan();
        if (command === "commencer") return askAssistant("START_20_MIN_SESSION");
        if (menu === "echeances") return askAssistant("LIST_UPCOMING_DEADLINES");
        if (menu === "plans") return studentPlans();
        if (menu === "dispos") return showAvailability(true);
        if (menu === "horaire") return studentWeekPlan();
        if (menu === "objectifs") return studentSelfPlan();
        if (menu === "echeances") return beginFlow9(true);
      }
      const notice = levelOneNotice(command);
      if (notice) return later(() => agentNote(notice, role === "parent" ? "parent" : "eleve"), 250);
      if (role === "parent") {
        if (command === "accueil") return showParentHome();
        return later(() => agentNote("Je ne connais pas cette commande. Écrivez /menu.", "parent"), 250);
      }
    }
    if (command === "fonctions" || command === "fonctons") return showMenu("Fonctions");
    if (command === "accueil" || command === "2") return startFlow2();
    if (command === "objectifs" || command === "objectif" || command === "3") return beginFlow3(true);
    if (command === "dispos" || command === "disponibilite" || command === "disponibilité") return showAvailability();
    if (command === "liste") return showObjectiveList();
    if (command === "plan" || command === "4" || command === "voir plan") return beginFlow4(true);
    if (command === "debut" || command === "début" || command === "seance" || command === "séance" || command === "5") return beginFlow5(true);
    if (command === "suivi" || command === "6") return beginFlow6(true);
    if (command === "bilan" || command === "7") return beginFlow7(true);
    if (command === "photo" || command === "8") return beginFlow8(true);
    if (command === "agenda" || command === "9") return beginFlow9(true);
    if (command === "voir objectif") return showObjectiveList();
    if (command === "voir bilan") return showDocument("bilan");
    later(() => {
      setMessages((current) => [
        ...current,
        {
          id: `menu-unknown-${Date.now()}`,
          sender: "agent",
          text: "Je ne connais pas cette commande. Écris /menu.",
          time: nowStamp(),
        },
      ]);
    }, 250);
  };

  const startParentOnboarding = () => startParentFlow(1);

  const openParent = () => {
    setRole("parent");
    setDash((current) => ({ ...current, view: "parent" }));
    if (!parentStarted.current) startParentOnboarding();
  };

  const showParentMenu = () => {
    const flows = flowsByAudience(blueprintRef.current, "parent");
    setParentReplies(
      flows.map((item, index) => ({
        id: `parent-flow-${item.id}`,
        label: `${item.id}. ${item.title.replace(/^Flow \d+ — /, "")}`,
        primary: index === 0,
      }))
    );
    setParentMessages((current) => [
      ...current,
      {
        id: `pmenu-${Date.now()}`,
        sender: "agent",
        text: `Flows parent\n\n${flows
          .map((item) => `/${item.id} — ${item.title}`)
          .join("\n")}\n\n/menu ou /fonctions rouvre cette liste. L’arbre modifie le message, le prompt et la relance.`,
        time: nowStamp(),
      },
    ]);
  };

  const handleQuickReply = (id) => {
    if (typing) return;
    if (role === "parent") {
      if (id.startsWith("parent-flow-")) {
        startParentFlow(Number(id.slice("parent-flow-".length)));
        return;
      }
      const reply = parentReplies.find((item) => item.id === id);
      if (!reply) return;
      if (reply.id === "pv0-home-menu") {
        setSideMenuOpen(true);
        return;
      }
      setParentReplies([]);
      setParentMessages((current) => [
        ...current,
        { id: `pu-${Date.now()}`, sender: "user", text: reply.label, time: nowStamp() },
      ]);
      if (reply.v0) {
        handleParentV0(reply);
        return;
      }
      if (!reply.wireTo) {
        later(() => {
          setParentMessages((current) => [
            ...current,
            {
              id: `pend-${Date.now()}`,
              sender: "agent",
              text: "Ce chemin s’arrête ici. Relie la réponse à une étape dans l’arbre pour continuer.",
              time: nowStamp(),
            },
          ]);
        }, 350);
        return;
      }
      if (appMode && levelOne && reply.wireTo === "s7-0") {
        showParentHome();
        return;
      }
      later(() => playNode(reply.wireFlow || dash.flowId, reply.wireTo, "continue"), 350);
      return;
    }

    if (id.startsWith("menu-")) {
      const action = id.slice(5);
      const labels = {
        objectifs: "Objectifs",
        dispos: "Dispos",
        liste: "Liste",
        plan: "Plan",
      };
      addUser(labels[action] || action);
      return runCommand(action);
    }

    const wired = quickReplies.find(
      (item) => item.id === id && Object.prototype.hasOwnProperty.call(item, "wireTo")
    );
    if (wired) {
      addUser(wired.label);
      if (!wired.wireTo) {
        later(() => {
          setMessages((current) => [
            ...current,
            {
              id: `wire-end-${Date.now()}`,
              sender: "agent",
              text: "Ce chemin s’arrête ici. Relie la réponse à une étape dans l’arbre pour continuer.",
              time: nowStamp(),
            },
          ]);
          setQuickReplies([]);
        }, 350);
        return;
      }
      later(() => playNode(wired.wireFlow, wired.wireTo), 350);
      return;
    }

    if (id.startsWith("gpt:")) {
      const reply = quickReplies.find((item) => item.id === id);
      askGpt(reply?.label || "", true);
      return;
    }

    if (id === "f8-gpt-retry") {
      askGpt(gptPendingRef.current, false);
      return;
    }

    if (id === "f9-add" || id === "f9-another") {
      objectiveCaptureRef.current = false;
      agendaModeRef.current = true;
      photoRef.current?.click();
      return;
    }

    if (id === "f9-week") {
      acceptAgendaPhoto(weekAgendaUrl(), "week");
      return;
    }

    if (id === "f9-day") {
      acceptAgendaPhoto(dayAgendaUrl(), "day");
      return;
    }

    if (id === "f9-task") {
      acceptAgendaPhoto(taskAgendaUrl(), "task");
      return;
    }

    if (id === "f9-build") {
      buildAgendaPlans();
      return;
    }

    if (id === "f9-gap") {
      addUser("Une date manque");
      agendaModeRef.current = true;
      later(() => addAgent("f9.gap", FLOW9_REPLIES.ask), 400);
      return;
    }

    if (id === "f9-activate") {
      activateAgendaPlans();
      return;
    }

    if (id === "f9-done") {
      addUser("C'est complet");
      later(() => addAgent("f9.complete", []), 400);
      return;
    }

    if (id === "f8-start") {
      beginFlow8(true);
      return;
    }

    if (id === "f8-add") {
      objectiveCaptureRef.current = false;
      agendaModeRef.current = false;
      photoRef.current?.click();
      return;
    }

    if (id === "f8-pic-marge" || id === "f8-sample") {
      acceptHelpPhoto(sampleExerciseUrl(), "marge");
      return;
    }

    if (id === "f8-pic-pythagore") {
      acceptHelpPhoto(pythagoreExerciseUrl(), "pythagore");
      return;
    }

    if (id === "f8-pic-phrase") {
      acceptHelpPhoto(phraseExerciseUrl(), "phrase");
      return;
    }

    if (id === "f8-as-marge" || id === "f8-as-pythagore" || id === "f8-as-phrase") {
      const item = id.replace("f8-as-", "");
      const label =
        item === "marge" ? "C'est le sondage" : item === "pythagore" ? "C'est le triangle" : "C'est la phrase";
      addUser(label);
      helpItemRef.current = item;
      setDash((d) => ({ ...d, flowId: 8, flowStep: 1, subject: helpSubject(item), status: FLOW8_STEPS[1] }));
      later(() => {
        pushKind("extract", { rows: rowsFor(item) });
        later(() => addAgent("f8.prompt", repliesFor(item, "read"), { item }), 350);
      }, 400);
      return;
    }

    if (id === "f8-as-other") {
      addUser("Autre chose");
      helpItemRef.current = "unknown";
      later(() => addAgent("f8.idea", FLOW8_REPLIES.ask, { item: "unknown" }), 400);
      return;
    }

    if (id === "f8-text" || id === "f8-lost") {
      if (id === "f8-text" && dash.flowId !== 8) {
        beginFlow8(true);
        return;
      }
      if (dash.flowStep < 1) {
        addUser("Je ne comprends pas");
        later(() => addAgent("f8.nudge", FLOW8_REPLIES.ask), 400);
        return;
      }
      explainSampleSize();
      return;
    }

    if (id === "f8-formula") {
      explainFormula();
      return;
    }

    if (id === "f8-p-bad" || id === "f8-p-bad2") {
      addUser(id === "f8-p-bad" ? "7" : "12");
      later(() => addAgent("f8.choiceHint", repliesFor("pythagore", "choice"), { item: "pythagore" }), 400);
      return;
    }

    if (id === "f8-p-ok") {
      addUser("25");
      setDash((d) => ({ ...d, flowId: 8, flowStep: 3, status: FLOW8_STEPS[2], subject: "Maths" }));
      later(() => addAgent("f8.exact", repliesFor("pythagore", "next"), { item: "pythagore" }), 400);
      return;
    }

    if (id === "f8-p-sum-bad" || id === "f8-p-sum-bad2") {
      addUser(id === "f8-p-sum-bad" ? "14" : "48");
      later(() => addAgent("f8.divHint", repliesFor("pythagore", "divide"), { item: "pythagore" }), 400);
      return;
    }

    if (id === "f8-p-sum") {
      addUser("100");
      finishHelpWithImage();
      return;
    }

    if (id === "f8-s-verb" || id === "f8-s-place") {
      addUser(id === "f8-s-verb" ? "dort" : "le canapé");
      later(() => addAgent("f8.choiceHint", repliesFor("phrase", "choice"), { item: "phrase" }), 400);
      return;
    }

    if (id === "f8-s-ok") {
      addUser("Le petit chat noir");
      setDash((d) => ({ ...d, flowId: 8, flowStep: 3, status: FLOW8_STEPS[2], subject: "Français" }));
      later(() => addAgent("f8.exact", repliesFor("phrase", "next"), { item: "phrase" }), 400);
      return;
    }

    if (id === "f8-verb") {
      addUser("C'est le verbe");
      helpItemRef.current = "phrase";
      setDash((d) => ({ ...d, flowId: 8, flowStep: 6, status: FLOW8_STEPS[4], subject: "Français", planReady: true }));
      later(() => {
        addAgent("f8.formula", [], { item: "phrase" });
        later(() => addAgent("f8.fiche", FLOW8_REPLIES.after, { item: "phrase" }), 800);
      }, 400);
      return;
    }

    if (id === "f8-a" || id === "f8-c") {
      addUser(id === "f8-a" ? "A. Interroger moins de personnes" : "C. Toujours interroger 100 personnes");
      later(() => addAgent("f8.choiceHint", FLOW8_REPLIES.choice, { item: "marge" }), 400);
      return;
    }

    if (id === "f8-b") {
      addUser("B. Interroger plus de personnes");
      setDash((d) => ({ ...d, flowId: 8, flowStep: 3, status: FLOW8_STEPS[2], subject: "Maths" }));
      later(() => addAgent("f8.exact", FLOW8_REPLIES.next), 400);
      return;
    }

    if (id === "f8-div-mid" || id === "f8-div-hi") {
      addUser(id === "f8-div-mid" ? "0,00525" : "0,0525");
      later(() => addAgent("f8.divHint", FLOW8_REPLIES.divide), 400);
      return;
    }

    if (id === "f8-div") {
      addUser("0,000525");
      finishHelpWithImage();
      return;
    }

    if (id === "f8-blur") {
      addUser("La photo est floue");
      setDash((d) => ({ ...d, flowId: 8, flowStep: 90, status: "Photo à reprendre" }));
      later(() => addAgent("f8.blur", FLOW8_REPLIES.retry), 400);
      return;
    }

    if (id === "f8-visual") {
      addUser("Fais moi un résumé visuel");
      showHelpFiche();
      return;
    }

    if (id === "f8-done" || id === "f8-more" || id === "f8-tutor") {
      const choice =
        id === "f8-done"
          ? ["J'ai compris", "f8.understood", "Aide photo · comprise", 100]
          : id === "f8-more"
            ? ["Encore un exemple", "f8.again", "Aide photo · autre exemple", 60]
            : ["Je veux un tuteur", "f8.tutor", "Aide photo · tuteur", 60];
      addUser(choice[0]);
      setDash((d) => ({
        ...d,
        flowId: 8,
        flowStep: 8,
        status: choice[2],
        planReady: true,
        subject: "Maths",
        minutes: Math.max(d.minutes || 0, 8),
        sessionsDone: 1,
        sessionsTotal: Math.max(d.sessionsTotal, 1),
        regularity: choice[3],
      }));
      later(() => addAgent(choice[1], []), 400);
      return;
    }

    if (id === "f5-start") {
      beginFlow5(true);
      return;
    }

    if (id === "f5-start-now") {
      addUser("Oui je commence !");
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: 2,
        status: FLOW5_STEPS[2],
        subject: "Maths – Fonctions",
        session: {
          ...emptySession(),
          ...d.session,
          task: "Maths – Fonctions",
          checks: 0,
          hadPause: false,
        },
      }));
      later(() => {
        addAgent("f5.started", []);
        later(() => addAgent("f5.check", FLOW5_REPLIES.check), 350);
      }, 400);
      return;
    }

    if (id.startsWith("f5-task-")) {
      const label = FLOW5_REPLIES.task.find((reply) => reply.id === id)?.label;
      if (!label) return;
      addUser(label);
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: 1,
        status: FLOW5_STEPS[1],
        subject: label,
        session: { ...emptySession(), ...d.session, task: label, checks: 0, hadPause: false },
      }));
      later(() => addAgent("f5.begin", FLOW5_REPLIES.begin, { task: label }), 400);
      return;
    }

    if (id === "f5-begin") {
      addUser("Je commence");
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: 2,
        status: FLOW5_STEPS[2],
        session: { ...emptySession(), ...d.session, checks: 0 },
      }));
      later(() => {
        addAgent("f5.started", []);
        later(() => addAgent("f5.check", FLOW5_REPLIES.check), 350);
      }, 400);
      return;
    }

    if (id === "f5-ok") {
      addUser("Tout va bien");
      const checks = (dash.session?.checks || 0) + 1;
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: 2,
        status: FLOW5_STEPS[2],
        session: { ...emptySession(), ...d.session, checks },
      }));
      later(() => {
        if (checks >= 2) {
          addAgent("f5.okAgain", FLOW5_REPLIES.finish);
          return;
        }
        addAgent("f5.ok", []);
        later(() => addAgent("f5.again", FLOW5_REPLIES.check), 350);
      }, 400);
      return;
    }

    if (id === "f5-pause") {
      addUser("Pause");
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: 2,
        status: FLOW5_STEPS[2],
        session: { ...emptySession(), ...d.session, hadPause: true },
      }));
      later(() => addAgent("f5.pause", FLOW5_REPLIES.resume), 400);
      return;
    }

    if (id === "f5-resume") {
      addUser("Je reprends");
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: 2,
        status: FLOW5_STEPS[2],
        session: { ...emptySession(), ...d.session, hadPause: true },
      }));
      later(() => {
        addAgent("f5.resume", []);
        later(() => addAgent("f5.check", FLOW5_REPLIES.check), 350);
      }, 400);
      return;
    }

    if (id === "f5-diff") {
      addUser("Difficulté");
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: 2,
        status: FLOW5_STEPS[3],
        session: { ...emptySession(), ...d.session, asking: "difficulty" },
      }));
      later(() => addAgent("f5.rate", FLOW5_REPLIES.rate), 400);
      return;
    }

    if (id === "f5-collect") {
      addUser("Collecte de l'info");
      const inProgress = dash.flowStep < 3;
      const timing = timingFor(Boolean(dash.session?.hadPause), inProgress);
      const session = {
        ...emptySession(),
        ...dash.session,
        ...timing,
        difficulty: dash.session?.difficulty || "",
        blockage: dash.session?.blockage || "",
      };
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: 5,
        status: FLOW5_STEPS[4],
        minutes: sessionMinutes(timing.duration),
        session,
      }));
      later(() => {
        addAgent("f5.collect", []);
        later(() => {
          setMessages((current) => [
            ...current,
            {
              id: `tutor-${Date.now()}`,
              sender: "agent",
              kind: "tutorNote",
              rows: tutorRows({
                task: session.task,
                started: session.started,
                ended: session.ended,
                pause: session.pause,
                hadPause: session.hadPause,
                duration: session.duration,
                rating: session.difficulty,
                note: session.blockage,
              }),
              time: nowStamp(),
            },
          ]);
          setQuickReplies(FLOW5_REPLIES.send);
        }, 700);
      }, 400);
      return;
    }

    if (id === "f5-end") {
      addUser("J'ai terminé");
      const timing = timingFor(Boolean(dash.session?.hadPause));
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: 3,
        status: FLOW5_STEPS[3],
        minutes: sessionMinutes(timing.duration),
        session: { ...emptySession(), ...d.session, ...timing },
      }));
      later(() => {
        addAgent("f5.done", [], { paused: Boolean(dash.session?.hadPause) });
        later(() => addAgent("f5.rate", FLOW5_REPLIES.rate), 350);
      }, 400);
      return;
    }

    if (id.startsWith("f5-star-")) {
      const label = FLOW5_REPLIES.rate.find((reply) => reply.id === id)?.label;
      if (!label) return;
      addUser(label);
      if (dash.flowStep < 3) {
        setDash((d) => ({
          ...d,
          flowId: 5,
          flowStep: 2,
          status: FLOW5_STEPS[2],
          session: { ...emptySession(), ...d.session, difficulty: label, asking: "" },
        }));
        later(() => addAgent("f5.noted", FLOW5_REPLIES.check), 400);
        return;
      }
      prepareTutorSend({ difficulty: label, blockage: "" });
      return;
    }

    if (id === "f5-note") {
      addUser("Noter une difficulté");
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: d.flowStep < 3 ? 2 : 4,
        status: FLOW5_STEPS[3],
        session: { ...emptySession(), ...d.session, asking: "note" },
      }));
      later(() => addAgent("f5.note", FLOW5_REPLIES.note), 400);
      return;
    }

    if (id === "f5-note-sample") {
      acceptDifficulty("La question 3 était trop dure.");
      return;
    }

    if (id === "f5-send") {
      addUser("Continuer");
      setDash((d) => ({
        ...d,
        flowId: 5,
        flowStep: 6,
        status: "Infos envoyées",
        planReady: true,
        sessionsDone: 1,
        sessionsTotal: Math.max(d.sessionsTotal, 1),
        subject: d.session?.task || "Maths",
        minutes: sessionMinutes(d.session?.duration) || d.minutes,
        regularity: 100,
      }));
      later(() => addAgent("f5.sent", []), 400);
      pushLog("Infos de séance envoyées au tuteur (simulation, aucune BD).");
      return;
    }

    if (id === "f4-start") {
      beginFlow4(true);
      return;
    }

    if (id === "f4-go") {
      addUser("Oui, on y va !");
      setDash((d) => ({
        ...d,
        flowId: 4,
        flowStep: 1,
        status: FLOW4_STEPS[1],
        week: { ...emptyWeek(), ...d.week, days: [] },
      }));
      later(() => addAgent("f4.avail", dayReplies()), 400);
      return;
    }

    if (id.startsWith("f4-day-")) {
      const label = labelForDay(id);
      if (!label) return;
      setDash((d) => {
        const current = d.week?.days || [];
        const days = current.includes(label)
          ? current.filter((item) => item !== label)
          : [...current, label];
        return { ...d, week: { ...emptyWeek(), ...d.week, days } };
      });
      return;
    }

    if (id === "f4-days-ok") {
      const days = orderedDays(dash.week?.days || []);
      addUser(daysSentence(days));
      setDash((d) => ({
        ...d,
        flowStep: 2,
        status: FLOW4_STEPS[2],
        week: { ...emptyWeek(), ...d.week, days },
      }));
      later(() => {
        addAgent("f4.generate", []);
        later(() => {
          pushKind("build");
          later(() => addAgent("f4.preview", FLOW4_REPLIES.see), 350);
        }, 800);
      }, 400);
      return;
    }

    if (id === "f4-see") {
      addUser("Voir le plan");
      setDash((d) => ({
        ...d,
        flowStep: 3,
        status: FLOW4_STEPS[3],
        planReady: true,
        sessionsTotal: 6,
        minutes: 330,
        subject: "4 matières",
      }));
      later(() => {
        addAgent("f4.review", []);
        later(() => {
          pushKind("weekplan");
          later(() => addAgent("f4.choice", FLOW4_REPLIES.review), 350);
        }, 800);
      }, 400);
      return;
    }

    if (id === "f4-edit") {
      addUser("Modifier");
      setDash((d) => ({ ...d, flowStep: 4, status: FLOW4_STEPS[3] }));
      later(() => addAgent("f4.modify", FLOW4_REPLIES.shift), 400);
      return;
    }

    if (id === "f4-shift") {
      acceptPlanChange("Avancer le samedi à 9 h");
      return;
    }

    if (id === "f4-ok") {
      addUser("Valider et partager");
      setDash((d) => ({
        ...d,
        flowStep: 6,
        status: FLOW4_STEPS[4],
        planReady: true,
        sessionsTotal: 6,
        minutes: Math.max(d.minutes || 0, 330),
        subject: "4 matières",
        regularity: 100,
      }));
      later(() => {
        addAgent("f4.active", []);
        later(() => {
          pushKind("readyplan");
          later(() => {
            addAgent("f4.courage", []);
            later(() => beginFlow5(true), 1100);
          }, 350);
        }, 800);
      }, 400);
      return;
    }

    if (id === "f7-start") {
      beginFlow7(true);
      return;
    }

    if (id === "f7-subjects") {
      addUser("Voir le temps par matière");
      setDash((d) => ({ ...d, flowId: 7, flowStep: 1, status: FLOW7_STEPS[1] }));
      later(() => {
        addAgent("f7.subjects", []);
        later(() => {
          pushKind("subjectBars");
          later(() => addAgent("f7.balance", FLOW7_REPLIES.progress), 350);
        }, 800);
      }, 400);
      return;
    }

    if (id === "f7-progress") {
      addUser("Voir la progression");
      setDash((d) => ({ ...d, flowStep: 2, status: FLOW7_STEPS[2] }));
      later(() => {
        addAgent("f7.progress", []);
        later(() => {
          pushKind("taskList");
          later(() => addAgent("f7.regular", FLOW7_REPLIES.alert), 350);
        }, 800);
      }, 400);
      return;
    }

    if (id === "f7-alert") {
      sendWebReminder("alerte");
      addUser("Voir le point d'attention");
      setDash((d) => ({ ...d, flowStep: 3, status: FLOW7_STEPS[3] }));
      later(() => {
        addAgent("f7.alert", []);
        later(() => {
          addAgent("f7.encourage", []);
          later(() => addAgent("f7.advice", FLOW7_REPLIES.details), 800);
        }, 800);
      }, 400);
      return;
    }

    if (id === "f7-details") {
      addUser("Voir les détails");
      setDash((d) => ({ ...d, flowStep: 4, status: FLOW7_STEPS[4], planReady: true }));
      later(() => {
        addAgent("f7.next", []);
        later(() => {
          pushKind("nextGoals");
          later(() => {
            addAgent("f7.note", []);
            later(() => addAgent("f7.thanks", []), 800);
          }, 800);
        }, 800);
      }, 400);
      return;
    }

    if (id === "f6-start") {
      beginFlow6(true);
      return;
    }

    if (id === "f6-now") {
      addUser("Je fais un retour maintenant");
      setDash((d) => ({
        ...d,
        flowId: 6,
        flowStep: 1,
        status: FLOW6_STEPS[1],
        session: { ...emptySession(), ...d.session, activities: [] },
      }));
      later(() => addAgent("f6.activities", activityReplies()), 400);
      return;
    }

    if (id === "f6-later") {
      addUser("Un peu plus tard");
      setDash((d) => ({ ...d, flowId: 6, flowStep: 90, status: "Retour reporté" }));
      later(() => addAgent("f6.later", FLOW6_REPLIES.resume), 400);
      return;
    }

    if (id === "f6-done") {
      addUser("Je l'ai déjà fait");
      setDash((d) => ({ ...d, flowId: 6, flowStep: 91, status: "Retour déjà fait" }));
      later(() => addAgent("f6.already", []), 400);
      return;
    }

    if (id.startsWith("f6-act-")) {
      const label = labelForActivity(id);
      if (!label) return;
      setDash((d) => {
        const current = d.session?.activities || [];
        const activities = current.includes(label)
          ? current.filter((item) => item !== label)
          : [...current, label];
        return {
          ...d,
          session: { ...emptySession(), ...d.session, activities },
        };
      });
      return;
    }

    if (id === "f6-acts-ok") {
      const activities = dash.session?.activities?.length
        ? dash.session.activities
        : [...FLOW6_CANON.activities];
      addUser(activitiesSentence(activities));
      setDash((d) => ({
        ...d,
        flowStep: 2,
        status: FLOW6_STEPS[2],
        session: { ...emptySession(), ...d.session, activities },
      }));
      later(() => addAgent("f6.duration", flow6DurationReplies()), 400);
      return;
    }

    if (id.startsWith("f6-time-")) {
      const label = labelForTime(id);
      if (!label) return;
      addUser(label);
      setDash((d) => ({
        ...d,
        flowStep: 3,
        status: FLOW6_STEPS[3],
        minutes: sessionMinutes(label),
        session: { ...emptySession(), ...d.session, duration: label },
      }));
      later(() => addAgent("f6.difficulty", difficultyReplies()), 400);
      return;
    }

    if (id.startsWith("f6-diff-")) {
      const label = labelForDifficulty(id);
      if (!label) return;
      addUser(label);
      setDash((d) => ({
        ...d,
        flowStep: 4,
        status: FLOW6_STEPS[3],
        session: { ...emptySession(), ...d.session, difficulty: label },
      }));
      later(() => addAgent("f6.blockage", FLOW6_REPLIES.blockage), 400);
      return;
    }

    if (id === "f6-block") {
      acceptBlockage(FLOW6_CANON.blockage);
      return;
    }

    if (id === "f6-yes" || id === "f6-no") {
      const adapt = id === "f6-yes";
      addUser(adapt ? "Oui, adapte mon plan" : "Non, tout va bien");
      setDash((d) => ({
        ...d,
        flowStep: 6,
        status: adapt ? "Plan adapté" : "Séance enregistrée",
        planReady: true,
        sessionsDone: 1,
        sessionsTotal: adapt ? 2 : 1,
        subject: "Maths",
        minutes: d.minutes || sessionMinutes(d.session?.duration),
        regularity: 100,
      }));
      later(() => addAgent(adapt ? "f6.adapted" : "f6.keep", FLOW7_REPLIES.bridge), 400);
      return;
    }

    if (id === "go") {
      addUser("Oui, c'est parti !");
      setDash((d) => ({
        ...d,
        flowStep: 1,
        status: "Rôle du tuteur",
      }));
      later(() => {
        addAgent("f2.role", FLOW2_REPLIES.understood);
      }, 400);
      return;
    }

    if (id === "later") {
      addUser("Peut-être plus tard");
      setDash((d) => ({
        ...d,
        flowStep: 90,
        status: "Reporté",
      }));
      later(() => {
        addAgent(
          "f2.later",
          FLOW2_REPLIES.welcome.filter((reply) => reply.id === "go")
        );
      }, 400);
      return;
    }

    if (id === "understood") {
      addUser("Super, j'ai compris !");
      setDash((d) => ({
        ...d,
        flowStep: 2,
        status: "Élève prêt",
      }));
      later(() => {
        addAgent("f2.ready", FLOW2_REPLIES.ready);
      }, 400);
      return;
    }

    if (id === "free-continue") {
      addUser("Continuer");
      const resume = freeChatResumeRef.current;
      if (resume.length) setQuickReplies(resume);
      else showStudentHome();
      return;
    }
    if (STUDENT_MENU_COMMANDS[id]) {
      addUser(quickReplies.find((item) => item.id === id)?.label || id);
      runCommand(STUDENT_MENU_COMMANDS[id]);
      return;
    }
    if (id === "k-later") {
      addUser("Plus tard");
      sayStudent("k.later", {}, [studentMenuReply]);
      return;
    }
    if (id === "e-help") {
      addUser("Je bloque");
      studentAiHelp();
      return;
    }
    if (id === "e-report") {
      addUser("Mon bilan");
      showReport("eleve");
      return;
    }
    if (QUICK_INTENTS[id]) {
      addUser(quickAsk.find((item) => item.id === id)?.label || "Question");
      askAssistant(QUICK_INTENTS[id]);
      return;
    }
    if (id === "e-capture" || id === "e-ask") {
      addUser(id === "e-capture" ? "Capturer mes travaux" : "Questions rapides");
      if (id === "e-capture") startCapture(false);
      else {
        studentSection(21);
        mvpDash(21, 0);
        sayStudent("q.menu", {}, quickAsk);
      }
      return;
    }
    if (/^x-(open|go|now|snooze|move):/.test(id)) {
      const [kind, first, second] = id.split(":");
      const label = quickReplies.find((item) => item.id === id)?.label || { "x-open": "Ouvrir la séance", "x-go": "Je commence", "x-now": "Je commence", "x-snooze": "Dans 20 min", "x-move": "Reporter" }[kind];
      addUser(label);
      if (kind === "x-open") openSession(first, second);
      if (kind === "x-go") beginSession(first, second);
      if (kind === "x-now") beginSession("", first);
      if (kind === "x-snooze") snoozeSession(first);
      if (kind === "x-move") moveSession(first);
      return;
    }
    if (id === "a-human" || id === "a-back") {
      addUser(id === "a-human" ? "Besoin d’un tuteur" : "Retour à mon plan");
      onAiHelpAction(id === "a-human" ? "human_help_requested" : "needs_practice");
      return;
    }
    if (id.startsWith("cap-")) {
      addUser(quickReplies.find((item) => item.id === id)?.label || "");
      setQuickReplies([]);
      captureAction(id);
      return;
    }
    if (id === "e-self") {
      addUser("Créer mon propre plan d’étude");
      studentSelfPlan();
      return;
    }
    if (id === "e-menu") {
      setSideMenuOpen(true);
      return;
    }
    if (id === "e-end") {
      addUser("Terminer la session");
      sayStudent("e.goodbye", {}, []);
      if (appMode && onLogout) later(onLogout, 2200);
      else later(() => setQuickReplies([studentMenuReply]), 900);
      return;
    }
    if (id.startsWith("w-open:")) {
      const [, sessionId, planId] = id.split(":");
      addUser("J’ouvre le lien du texto");
      openSession(sessionId, planId);
      return;
    }

    if (id === "ready" && levelOne && !FEATURES.availability) {
      addUser("Oui, je suis prêt(e) !");
      if (cleanTutorName(tutorName) || cleanTutorName(dash.tutorName)) {
        showStudentHome();
        return;
      }
      offerTutorName();
      return;
    }

    if (id === "ready") {
      addUser("Oui, je suis prêt(e) !");
      setDash((d) => ({
        ...d,
        flowId: 2,
        flowStep: 3,
        status: FLOW2_STEPS[3],
        availability: seedAvailability(),
      }));
      later(() => addAgent("f2.avail", []), 400);
      later(() => {
        pushKind("availability");
        setQuickReplies(FLOW2_REPLIES.avail);
      }, 1300);
      return;
    }

    if (id.startsWith("f2-slot-")) {
      setDash((d) => {
        const current = d.availability || defaultAvailability();
        const availability = current.includes(id)
          ? current.filter((item) => item !== id)
          : [...current, id];
        return { ...d, availability };
      });
      return;
    }

    if (id === "f2-avail-ok") {
      addUser("Disponibilités notées");
      onAvailabilityCommit?.(dash.availability || seedAvailability());
      if (levelOne && availFromMenuRef.current) {
        availFromMenuRef.current = false;
        showStudentHome();
        return;
      }
      if (cleanTutorName(tutorName) || cleanTutorName(dash.tutorName)) {
        beginFlow3(true);
        return;
      }
      offerTutorName();
      return;
    }

    if (id === "f2-name-random") {
      acceptTutorName(pickTutorName());
      return;
    }

    if (id === "f2-name-other") {
      addUser("Autre choix");
      showTutorNameField();
      return;
    }

    if (id.startsWith("f2-name-") && id !== "f2-name-go") {
      const label = FLOW2_REPLIES.name.find((reply) => reply.id === id)?.label;
      if (label) acceptTutorName(label);
      return;
    }

    if (id === "f2-name-go") {
      if (levelOne) {
        addUser("Voir mon espace");
        showStudentHome();
        return;
      }
      addUser("Aller à mes objectifs");
      beginFlow3(true);
      return;
    }

    if (id === "question") {
      addUser("J'ai encore une question");
      setDash((d) => ({
        ...d,
        flowStep: 91,
        status: "Question libre",
      }));
      later(() => {
        addAgent("f2.question", []);
      }, 400);
      return;
    }

    if (id === "f3-start") {
      beginFlow3(true);
      return;
    }

    if (id === "f3-photo") {
      objectiveCaptureRef.current = true;
      photoRef.current?.click();
      return;
    }

    if (id === "f3-mention") {
      acceptMention(FLOW3_CANON.mention);
      return;
    }

    if (id.startsWith("f3-subject-")) {
      const label = labelForReply(id, SUBJECT_OPTIONS, "f3-subject-");
      if (!label) return;
      addUser(label);
      setDash((d) => ({
        ...d,
        flowId: 3,
        flowStep: 2,
        status: FLOW3_STEPS[2],
        subject: label,
        objective: { ...d.objective, subject: label },
      }));
      later(() => addAgent("f3.type", typeReplies(), { subject: label }), 400);
      return;
    }

    if (id.startsWith("f3-type-")) {
      const label = labelForReply(id, TYPE_OPTIONS, "f3-type-");
      if (!label) return;
      addUser(label);
      setDash((d) => ({
        ...d,
        flowStep: 3,
        status: FLOW3_STEPS[2],
        objective: { ...d.objective, type: label },
      }));
      later(() => {
        addAgent("f3.due", dueReply());
      }, 400);
      return;
    }

    if (id === "f3-due") {
      acceptDue(FLOW3_CANON.due);
      return;
    }

    if (id.startsWith("f3-duration-")) {
      const label = labelForReply(id, DURATION_OPTIONS, "f3-duration-");
      if (!label) return;
      addUser(label);
      setDash((d) => ({
        ...d,
        flowStep: 5,
        status: FLOW3_STEPS[3],
        minutes: durationMinutes(label),
        objective: { ...d.objective, duration: label },
      }));
      later(() => addAgent("f3.steps", stepsReplies()), 400);
      return;
    }

    if (id === "f3-steps") {
      acceptSteps(FLOW3_CANON.stepsText);
      return;
    }

    if (id === "f3-nosteps") {
      acceptSteps("");
    }
  };

  const acceptMention = (text) => {
    addUser(text);
    setDash((d) => ({
      ...d,
      flowId: 3,
      flowStep: 1,
      status: FLOW3_STEPS[1],
      objective: { ...emptyObjective(), ...d.objective, mention: text },
    }));
    later(() => {
      addAgent("f3.after", []);
      later(() => addAgent("f3.subject", subjectReplies()), 700);
    }, 400);
  };

  const acceptDue = (text) => {
    addUser(text);
    setDash((d) => ({
      ...d,
      flowStep: 4,
      status: FLOW3_STEPS[2],
      objective: { ...d.objective, due: text },
    }));
    later(() => addAgent("f3.duration", durationReplies()), 400);
  };

  const acceptSteps = (text) => {
    const steps = parseSteps(text);
    const objective = { ...dash.objective, steps };
    if (text) addUser(text);
    else addUser("Pas d'étapes précises");
    setDash((d) => ({
      ...d,
      flowStep: 6,
      status: FLOW3_STEPS[4],
      planReady: true,
      sessionsTotal: Math.max(d.sessionsTotal, 1),
      subject: objective.subject || d.subject,
      minutes: d.minutes || durationMinutes(objective.duration),
      objective,
    }));
    later(() => {
      addAgent("f3.confirmIntro", []);
      later(() => {
        setMessages((m) => [
          ...m,
          {
            id: `obj-${Date.now()}`,
            sender: "agent",
            kind: "objective",
            objective,
            time: nowStamp(),
          },
        ]);
        pushLog("Objectif créé (simulation locale, aucune BD).");
        later(() => addAgent("f3.confirmOutro", FLOW4_REPLIES.bridge), 350);
      }, 850);
    }, 400);
  };

  const acceptBlockage = (text) => {
    const session = {
      ...emptySession(),
      ...dash.session,
      blockage: text,
      activities: dash.session?.activities?.length
        ? dash.session.activities
        : [...FLOW6_CANON.activities],
      duration: dash.session?.duration || FLOW6_CANON.duration,
      difficulty: dash.session?.difficulty || FLOW6_CANON.difficulty,
    };
    addUser(text);
    setDash((d) => ({
      ...d,
      flowStep: 5,
      status: FLOW6_STEPS[4],
      planReady: true,
      sessionsDone: 1,
      sessionsTotal: Math.max(d.sessionsTotal, 1),
      subject: "Maths",
      minutes: sessionMinutes(session.duration),
      regularity: 100,
      session,
    }));
    later(() => {
      setMessages((current) => [
        ...current,
        {
          id: `ses-${Date.now()}`,
          sender: "agent",
          kind: "session",
          session,
          time: nowStamp(),
        },
      ]);
      pushLog("Séance enregistrée (simulation locale, aucune BD).");
      later(() => addAgent("f6.adapt", FLOW6_REPLIES.adapt), 350);
    }, 400);
  };

  const acceptPlanChange = (text) => {
    addUser(text);
    setDash((d) => ({
      ...d,
      flowId: 4,
      flowStep: 3,
      status: FLOW4_STEPS[3],
      week: { ...emptyWeek(), ...d.week, modified: true },
    }));
    later(() => {
      pushKind("weekplan", { modified: true });
      later(() => addAgent("f4.modified", FLOW4_REPLIES.review), 350);
    }, 400);
  };

  const acceptDifficulty = (text) => {
    const note = String(text || "").trim();
    if (!note) return;
    addUser(note);
    prepareTutorSend({ difficulty: "", blockage: note });
  };

  const shrinkPhoto = (src) =>
    new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, 1600 / Math.max(img.width, img.height));
        const canvas = document.createElement("canvas");
        canvas.width = Math.max(1, Math.round(img.width * scale));
        canvas.height = Math.max(1, Math.round(img.height * scale));
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          resolve(src);
          return;
        }
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = () => resolve(src);
      img.src = src;
    });

  const askGpt = async (studentText, showUser) => {
    const image = gptImageRef.current;
    if (!image) return;
    const gen = gptGenRef.current;
    const text = String(studentText || "").trim();
    gptPendingRef.current = text;
    if (showUser && text) addUser(text);
    setQuickReplies([]);
    setTyping(true);
    const history = gptHistoryRef.current.slice();
    try {
      const prepared = await shrinkPhoto(image);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/lab/photo-help`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          image: prepared,
          message: text || "Voici ma photo.",
          history,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (gen !== gptGenRef.current) return;
      if (!response.ok) {
        const detail = typeof data.detail === "string" ? data.detail : "La lecture de la photo a échoué. Réessaie.";
        setTyping(false);
        setMessages((current) => [
          ...current,
          { id: `gpt-err-${Date.now()}`, sender: "agent", text: detail, time: nowStamp() },
        ]);
        setQuickReplies([{ id: "f8-gpt-retry", label: "Réessayer", primary: true }, ...FLOW8_REPLIES.ask]);
        pushLog("Lecture photo : ChatGPT 5.2 indisponible.");
        return;
      }
      gptHistoryRef.current = [
        ...history,
        { role: "user", content: text || "Voici ma photo." },
        { role: "assistant", content: data.message },
      ];
      setTyping(false);
      setDash((d) => ({
        ...d,
        flowId: 8,
        flowStep: data.understood ? 8 : history.length ? 2 : 1,
        status: data.understood ? FLOW8_STEPS[4] : history.length ? FLOW8_STEPS[2] : FLOW8_STEPS[1],
        subject: data.subject || d.subject,
        planReady: data.understood ? true : d.planReady,
      }));
      setMessages((current) => {
        const next = [
          ...current,
          { id: `gpt-${Date.now()}`, sender: "agent", text: data.message, time: nowStamp() },
        ];
        if (!history.length && Array.isArray(data.rows)) {
          next.push({
            id: `extract-${Date.now()}`,
            sender: "agent",
            kind: "extract",
            rows: data.rows,
            time: nowStamp(),
          });
        }
        if (data.formula && !ficheShownRef.current) {
          ficheShownRef.current = true;
          next.push({
            id: `fiche-${Date.now()}`,
            sender: "agent",
            kind: "fiche",
            src: modelFormulaUrl(data.formula.title, data.formula.parts),
            time: nowStamp(),
          });
        }
        return next;
      });
      setQuickReplies(
        (data.replies || []).map((label, index) => ({
          id: `gpt:${index}:${label}`,
          label,
          primary: index === 0,
        }))
      );
      pushLog(`Photo lue par ${data.model || "gpt-5.2"} · ${data.subject || "exercice"}.`);
    } catch {
      if (gen !== gptGenRef.current) return;
      setTyping(false);
      setMessages((current) => [
        ...current,
        {
          id: `gpt-err-${Date.now()}`,
          sender: "agent",
          text: "La lecture de la photo a échoué. Réessaie.",
          time: nowStamp(),
        },
      ]);
      setQuickReplies([{ id: "f8-gpt-retry", label: "Réessayer", primary: true }, ...FLOW8_REPLIES.ask]);
      pushLog("Lecture photo : réseau indisponible.");
    }
  };

  const startGptPhoto = (src) => {
    gptGenRef.current += 1;
    gptImageRef.current = src;
    gptHistoryRef.current = [];
    ficheShownRef.current = false;
    helpItemRef.current = "gpt";
    setMessages((current) => [
      ...current,
      { id: `pic-${Date.now()}`, sender: "user", kind: "photo", src, time: nowStamp() },
    ]);
    setQuickReplies([]);
    setDash((d) => ({
      ...d,
      flowId: 8,
      flowStep: 1,
      status: FLOW8_STEPS[1],
      subject: "Photo",
    }));
    pushLog("Photo envoyée à ChatGPT 5.2.");
    askGpt("", false);
  };

  const acceptHelpPhoto = (src, forcedItem) => {
    if (!src) return;
    const item = forcedItem || matchHelpItem(src);
    if (!forcedItem && item === "unknown") {
      startGptPhoto(src);
      return;
    }
    gptGenRef.current += 1;
    helpItemRef.current = item;
    ficheShownRef.current = false;
    setMessages((current) => [
      ...current,
      { id: `pic-${Date.now()}`, sender: "user", kind: "photo", src, time: nowStamp() },
    ]);
    setQuickReplies([]);
    setDash((d) => ({
      ...d,
      flowId: 8,
      flowStep: 1,
      status: FLOW8_STEPS[1],
      subject: helpSubject(item),
    }));
    pushLog(`Photo reçue · ${helpSubject(item)} (simulation, aucune BD).`);
    later(() => {
      addAgent("f8.read", [], { item });
      later(() => {
        pushKind("extract", { rows: rowsFor(item) });
        later(() => addAgent("f8.prompt", repliesFor(item, "read"), { item }), 350);
      }, 800);
    }, 400);
  };

  const explainSampleSize = (label = "Je ne comprends pas") => {
    const item = helpItemRef.current || "marge";
    addUser(label);
    setDash((d) => ({
      ...d,
      flowId: 8,
      flowStep: 2,
      status: FLOW8_STEPS[2],
      subject: helpSubject(item),
    }));
    const table = tableFor(item);
    later(() => {
      addAgent("f8.idea", [], { item });
      later(() => {
        if (item !== "phrase") pushKind("margins", table);
        later(() => addAgent("f8.curve", repliesFor(item, "choice"), { item }), item === "phrase" ? 350 : 350);
      }, item === "phrase" ? 400 : 800);
    }, 400);
  };

  const explainFormula = (label = "C'est le calcul et la formule") => {
    const item = helpItemRef.current || "marge";
    if (item === "phrase") {
      addUser(label);
      setDash((d) => ({ ...d, flowId: 8, flowStep: 6, status: FLOW8_STEPS[4], subject: "Français", planReady: true }));
      later(() => addAgent("f8.formula", FLOW8_REPLIES.after, { item: "phrase" }), 400);
      return;
    }
    if (item === "unknown") {
      addUser(label);
      later(() => addAgent("f8.formula", FLOW8_REPLIES.ask, { item: "unknown" }), 400);
      return;
    }
    addUser(label);
    setDash((d) => ({ ...d, flowId: 8, flowStep: 4, status: FLOW8_STEPS[2], subject: helpSubject(item) }));
    later(() => {
      addAgent("f8.formula", [], { item });
      later(() => {
        pushKind("calc", { item });
        later(() => addAgent("f8.try", repliesFor(item, "divide"), { item }), 350);
      }, 800);
    }, 400);
  };

  const showAgenda = (src, source, note) => {
    const incoming = itemsFor(source);
    const { items, added } = mergeAgenda(agendaItemsRef.current, incoming);
    agendaItemsRef.current = items;
    agendaModeRef.current = true;
    setMessages((current) => [
      ...current,
      { id: `pic-${Date.now()}`, sender: "user", kind: "photo", src, alt: "Photo de l'agenda", time: nowStamp() },
    ]);
    setQuickReplies([]);
    setDash((d) => ({
      ...d,
      flowId: 9,
      flowStep: 1,
      status: FLOW9_STEPS[1],
      subject: planTotals(buildStudyPlans(items)).label,
      planReady: false,
    }));
    pushLog(`Agenda · ${source} · ${added.length} nouvelle(s) échéance(s).`);
    later(() => {
      addAgent("f9.read", [], { source, added: added.length, total: items.length });
      later(() => {
        pushKind("agenda", { rows: photoRows(source, added.length) });
        later(() => {
          addAgent("f9.deadlines", []);
          setDash((d) => ({ ...d, flowId: 9, flowStep: 2, status: FLOW9_STEPS[2] }));
          later(() => {
            pushKind("deadlines", { rows: deadlineRows(items) });
            later(() => addAgent(note || "f9.offer", FLOW9_REPLIES.offer), 350);
          }, 800);
        }, 350);
      }, 800);
    }, 400);
  };

  const showAgendaFromItems = (src, incoming, message) => {
    const { items, added } = mergeAgenda(agendaItemsRef.current, incoming);
    agendaItemsRef.current = items;
    setMessages((current) => [
      ...current,
      {
        id: `pic-${Date.now()}`,
        sender: "user",
        kind: "photo",
        src,
        alt: "Photo de l'agenda",
        time: nowStamp(),
      },
      {
        id: `gpt-agenda-${Date.now()}`,
        sender: "agent",
        text: message,
        time: nowStamp(),
      },
      {
        id: `agenda-${Date.now()}`,
        sender: "agent",
        kind: "agenda",
        rows: photoRows("import", added.length),
        time: nowStamp(),
      },
      {
        id: `deadlines-${Date.now()}`,
        sender: "agent",
        kind: "deadlines",
        rows: deadlineRows(items),
        time: nowStamp(),
      },
    ]);
    setDash((d) => ({
      ...d,
      flowId: 9,
      flowStep: 2,
      status: FLOW9_STEPS[2],
      subject: planTotals(buildStudyPlans(items)).label,
      planReady: false,
    }));
    setQuickReplies(FLOW9_REPLIES.offer);
    pushLog(`Agenda lu par ChatGPT 5.2 · ${added.length} échéance(s).`);
  };

  const acceptAgendaPhoto = async (src, source) => {
    if (!src) return;
    const known = source || matchAgenda(src);
    if (known) {
      showAgenda(src, known);
      return;
    }
    setMessages((current) => [
      ...current,
      { id: `pic-${Date.now()}`, sender: "user", kind: "photo", src, alt: "Photo de l'agenda", time: nowStamp() },
    ]);
    setQuickReplies([]);
    setTyping(true);
    setDash((d) => ({ ...d, flowId: 9, flowStep: 1, status: FLOW9_STEPS[1] }));
    try {
      const prepared = await shrinkPhoto(src);
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/api/lab/agenda`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: prepared, message: "Voici mon agenda.", history: [] }),
      });
      const data = await response.json().catch(() => ({}));
      setTyping(false);
      if (!response.ok || !data.readable || !data.items?.length) {
        const detail =
          typeof data.detail === "string"
            ? data.detail
            : "Je ne vois pas de date assez claire. Réessaie avec une autre photo.";
        setMessages((current) => [
          ...current,
          { id: `agenda-err-${Date.now()}`, sender: "agent", text: detail, time: nowStamp() },
        ]);
        setQuickReplies(FLOW9_REPLIES.ask);
        return;
      }
      setMessages((current) => current.slice(0, -1));
      showAgendaFromItems(src, data.items, data.message);
    } catch {
      setTyping(false);
      setMessages((current) => [
        ...current,
        {
          id: `agenda-err-${Date.now()}`,
          sender: "agent",
          text: "La lecture de l'agenda a échoué. Réessaie.",
          time: nowStamp(),
        },
      ]);
      setQuickReplies(FLOW9_REPLIES.ask);
    }
  };

  const buildAgendaPlans = (label = "Construire les plans") => {
    const plans = buildStudyPlans(agendaItemsRef.current);
    if (!plans.length) {
      addUser(label);
      later(() => addAgent("f9.blur", FLOW9_REPLIES.ask), 400);
      return;
    }
    const totals = planTotals(plans);
    addUser(label);
    setDash((d) => ({
      ...d,
      flowId: 9,
      flowStep: 3,
      status: FLOW9_STEPS[3],
      subject: totals.label,
      minutes: totals.minutes,
      sessionsTotal: totals.sessions,
      planReady: false,
    }));
    later(() => {
      addAgent("f9.plans", []);
      later(() => {
        plans.forEach((plan, index) => {
          later(() => pushKind("studyplan", plan), index * 350);
        });
        later(() => addAgent("f9.ready", FLOW9_REPLIES.ready), plans.length * 350 + 200);
      }, 700);
    }, 400);
  };

  const activateAgendaPlans = () => {
    const totals = planTotals(buildStudyPlans(agendaItemsRef.current));
    addUser("Activer les plans");
    setDash((d) => ({
      ...d,
      flowId: 9,
      flowStep: 5,
      status: FLOW9_STEPS[4],
      subject: totals.label,
      minutes: totals.minutes,
      sessionsTotal: totals.sessions,
      sessionsDone: 0,
      planReady: true,
    }));
    later(() => addAgent("f9.done", FLOW9_REPLIES.done), 400);
  };

  const onHelpPhotoFile = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    if (objectiveCaptureRef.current) {
      objectiveCaptureRef.current = false;
      acceptMention(file.type.startsWith("image/") ? "Photo d'une échéance" : file.name || "Document d'échéance");
      return;
    }
    if (!file.type.startsWith("image/")) {
      addUser(file.name || "Fichier");
      if (agendaModeRef.current) {
        setDash((d) => ({ ...d, flowId: 9, flowStep: 90, status: "Photo à reprendre" }));
        later(() => addAgent("f9.blur", FLOW9_REPLIES.ask), 400);
        return;
      }
      setDash((d) => ({ ...d, flowId: 8, flowStep: 90, status: "Photo à reprendre" }));
      later(() => addAgent("f8.blur", FLOW8_REPLIES.retry), 400);
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const src = String(reader.result || "");
      if (agendaModeRef.current) acceptAgendaPhoto(src);
      else acceptHelpPhoto(src);
    };
    reader.readAsDataURL(file);
  };

  const showHelpFiche = () => {
    const item = helpItemRef.current || "marge";
    if (item === "phrase" || item === "unknown") {
      later(() => addAgent("f8.fiche", FLOW8_REPLIES.after, { item }), 400);
      return;
    }
    if (ficheShownRef.current) {
      later(() => addAgent("f8.after", FLOW8_REPLIES.after), 400);
      return;
    }
    ficheShownRef.current = true;
    setDash((d) => ({
      ...d,
      flowId: 8,
      flowStep: 6,
      status: FLOW8_STEPS[3],
      subject: helpSubject(item),
      minutes: Math.max(d.minutes || 0, 8),
      planReady: true,
    }));
    later(() => {
      addAgent("f8.fiche", [], { item });
      later(() => {
        setMessages((current) => [
          ...current,
          { id: `fiche-${Date.now()}`, sender: "agent", kind: "fiche", item, time: nowStamp() },
        ]);
        later(() => addAgent("f8.after", FLOW8_REPLIES.after), 350);
      }, 800);
    }, 400);
  };

  const finishHelpWithImage = () => {
    const item = helpItemRef.current || "marge";
    setDash((d) => ({ ...d, flowId: 8, flowStep: 5, status: FLOW8_STEPS[2], subject: helpSubject(item) }));
    later(() => {
      addAgent("f8.divOk", [], { item });
      later(() => showHelpFiche(), 900);
    }, 400);
  };

  const sendFreeText = () => {
    const text = input.trim();
    if (!text || typing) return;
    setInput("");

    if (text.startsWith("/")) captureChatRef.current = null;
    const capturing = captureChatRef.current;
    if (capturing && levelOne && familyLevel >= 2 && !text.startsWith("/") && capturing.parentSide === (role === "parent")) {
      if (capturing.parentSide) setParentMessages((current) => [...current, { id: `pu-${Date.now()}`, sender: "user", text, time: nowStamp() }]);
      else addUser(text);
      sideSay(capturing.parentSide, "c.reading");
      captureRound(capturing, text).catch((problem) => sideSay(capturing.parentSide, capturing.parentSide ? "c.errorParent" : "c.error", { error: problem.message }));
      return;
    }

    if (role !== "parent" && levelOne && FEATURES.helpRequests && /^(je bloque|j'?ai besoin d'?aide|aide)\b/i.test(text.replace("’", "'"))) {
      addUser(text);
      studentAiHelp();
      return;
    }

    if (role !== "parent" && levelOne && !text.startsWith("/") && !isArchivedFlow(dash.flowId)) {
      const intent = matchIntent(text);
      if (intent || familyLevel >= 2) {
        addUser(text);
        askAssistant(intent, intent ? "" : text);
        return;
      }
    }

    const freeWord = text.trim().toLowerCase().replace(/^\//, "");
    const knownWords = ["proposer", "suivi", "engagement", "inviter", "accueil", "aide", "bilan", "menu", "fonctions", "realisation", "réalisation", "objectifs", "6", "7", "plans", "mes plans", "dispos", "disponibilités", "horaire", "agenda", "échéances", "echeances", "étude", "etude", "commencer"];
    if (levelOne && !text.startsWith("/") && !knownWords.includes(freeWord) && !(role !== "parent" && studentMenuCommand(freeWord)) && !isArchivedFlow(dash.flowId) && !(dash.flowId === 2 && dash.flowStep === 4)) {
      const parentSide = role === "parent";
      const lockedKey = (Number(accountForfait) || 1) >= 3 ? "x.freeChatSoon" : "x.freeChat";
      const saved = parentSide ? parentReplies : quickReplies;
      freeChatResumeRef.current = saved.filter((item) => item.id !== "free-continue");
      const bubble = { id: `fu-${Date.now()}`, sender: "user", text, time: nowStamp() };
      if (parentSide) {
        setParentMessages((current) => [...current, bubble]);
        addParent(lockedKey, [v0Reply("free-continue", "Continuer", true)], {});
      } else {
        addUser(text);
        sayStudent(lockedKey, {}, [{ id: "free-continue", label: "Continuer" }]);
      }
      return;
    }

    if (levelOne && role !== "parent" && !text.startsWith("/") && (knownWords.includes(freeWord) || studentMenuCommand(freeWord)) && !isArchivedFlow(dash.flowId)) {
      addUser(text);
      runCommand(freeWord);
      return;
    }

    if (role === "parent" && appMode && levelOne) {
      setParentMessages((current) => [
        ...current,
        { id: `pu-${Date.now()}`, sender: "user", text, time: nowStamp() },
      ]);
      if (text.startsWith("/")) {
        runCommand(text);
        return;
      }
      later(() => agentNote("Écrivez /menu pour voir les commandes.", "parent"), 250);
      return;
    }

    if (role === "parent") {
      const command = text.trim().toLowerCase().replace(/^\//, "");
      if (levelOne && ["proposer", "suivi", "plans", "mes plans", "engagement", "inviter", "accueil", "6", "aide", "bilan"].includes(command)) {
        setParentMessages((current) => [
          ...current,
          { id: `pu-${Date.now()}`, sender: "user", text, time: nowStamp() },
        ]);
        runCommand(text);
        return;
      }
      const flows = flowsByAudience(blueprintRef.current, "parent");
      const named =
        command === "onboarding" || command === "1"
          ? 1
          : command === "bilan" || command === "7"
            ? 7
            : flows.some((item) => String(item.id) === command)
              ? Number(command)
              : null;
      if (command === "menu" || command === "fonctions" || command === "fonctons") {
        showParentMenu();
        return;
      }
      if (named) {
        startParentFlow(named);
        return;
      }
      setParentMessages((current) => [
        ...current,
        { id: `pu-${Date.now()}`, sender: "user", text, time: nowStamp() },
      ]);
      later(() => {
        setParentMessages((current) => [
          ...current,
          {
            id: `phint-${Date.now()}`,
            sender: "agent",
            text: "Écris /menu pour la liste numérotée des flows parent.",
            time: nowStamp(),
          },
        ]);
      }, 250);
      return;
    }

    if (dash.flowId === 2 && dash.flowStep === 4 && dash.nameDraft) {
      acceptTutorName(text);
      return;
    }

    if (text.startsWith("/")) {
      addUser(text);
      runCommand(text);
      return;
    }

    if (dash.flowId === 9) {
      if (/semaine/i.test(text)) {
        acceptAgendaPhoto(weekAgendaUrl(), "week");
        return;
      }
      if (/journ/i.test(text)) {
        acceptAgendaPhoto(dayAgendaUrl(), "day");
        return;
      }
      if (/fiche|anglais|oral/i.test(text)) {
        acceptAgendaPhoto(taskAgendaUrl(), "task");
        return;
      }
      if (/plan/i.test(text) && agendaItemsRef.current.length) {
        buildAgendaPlans(text);
        return;
      }
      addUser(text);
      const restore =
        dash.flowStep >= 5 ? FLOW9_REPLIES.done : dash.flowStep === 3 ? FLOW9_REPLIES.ready : dash.flowStep === 2 ? FLOW9_REPLIES.offer : FLOW9_REPLIES.ask;
      later(() => addAgent("f9.nudge", restore), 400);
      return;
    }

    if (dash.flowId === 8) {
      const item = helpItemRef.current || "marge";
      if (item === "gpt") {
        askGpt(text, true);
        return;
      }
      if (item === "unknown" && dash.flowStep <= 1) {
        addUser(text);
        setDash((d) => ({ ...d, subject: text.slice(0, 32) }));
        later(() => addAgent("f8.idea", FLOW8_REPLIES.ask, { item: "unknown" }), 400);
        return;
      }
      if (item === "pythagore" && dash.flowStep === 2) {
        addUser(text);
        if (/\b25\b|vingt-cinq/.test(text)) {
          setDash((d) => ({ ...d, flowStep: 3, status: FLOW8_STEPS[2] }));
          later(() => addAgent("f8.exact", repliesFor("pythagore", "next"), { item }), 400);
        } else {
          later(() => addAgent("f8.choiceHint", repliesFor("pythagore", "choice"), { item }), 400);
        }
        return;
      }
      if (item === "pythagore" && dash.flowStep === 4) {
        addUser(text);
        if (/\b100\b|cent/.test(text)) finishHelpWithImage();
        else later(() => addAgent("f8.divHint", repliesFor("pythagore", "divide"), { item }), 400);
        return;
      }
      if (item === "phrase" && (dash.flowStep === 1 || dash.flowStep === 2)) {
        addUser(text);
        if (/chat/.test(text)) {
          setDash((d) => ({ ...d, flowStep: 3, subject: "Français", status: FLOW8_STEPS[2] }));
          later(() => addAgent("f8.exact", repliesFor("phrase", "next"), { item: "phrase" }), 400);
        } else if (/verbe|dort/.test(text)) {
          helpItemRef.current = "phrase";
          setDash((d) => ({ ...d, flowStep: 6, subject: "Français", planReady: true, status: FLOW8_STEPS[4] }));
          later(() => addAgent("f8.formula", FLOW8_REPLIES.after, { item: "phrase" }), 400);
        } else {
          later(() => addAgent("f8.choiceHint", repliesFor("phrase", "choice"), { item: "phrase" }), 400);
        }
        return;
      }
      if ((dash.flowStep === 1 || dash.flowStep === 3) && /formule|calcul/i.test(text)) {
        explainFormula(text);
        return;
      }
      if ((dash.flowStep === 1 || dash.flowStep === 3) && /comprend|idée|idee|échantillon|echantillon/i.test(text)) {
        explainSampleSize(text);
        return;
      }
      if (item === "marge" && dash.flowStep === 2) {
        addUser(text);
        if (/plus|davantage|\bb\b/i.test(text)) {
          setDash((d) => ({ ...d, flowStep: 3, status: FLOW8_STEPS[2] }));
          later(() => addAgent("f8.exact", FLOW8_REPLIES.next, { item: "marge" }), 400);
        } else {
          later(() => addAgent("f8.choiceHint", FLOW8_REPLIES.choice, { item: "marge" }), 400);
        }
        return;
      }
      if (item === "marge" && dash.flowStep === 4) {
        addUser(text);
        if (/0[,.]000525/.test(text)) {
          finishHelpWithImage();
        } else {
          later(() => addAgent("f8.divHint", FLOW8_REPLIES.divide, { item: "marge" }), 400);
        }
        return;
      }
      if (/résumé|resume|fiche|visuel/i.test(text) && dash.flowStep >= 4 && item !== "phrase" && item !== "unknown") {
        addUser(text);
        showHelpFiche();
        return;
      }
      addUser(text);
      if (dash.flowStep === 0 && /triangle|pythagore/i.test(text)) {
        acceptHelpPhoto(pythagoreExerciseUrl(), "pythagore");
        return;
      }
      if (dash.flowStep === 0 && /phrase|français|francais|chat/i.test(text)) {
        acceptHelpPhoto(phraseExerciseUrl(), "phrase");
        return;
      }
      if (dash.flowStep === 0 && /exemple|sondage/i.test(text)) {
        acceptHelpPhoto(sampleExerciseUrl(), "marge");
        return;
      }
      const restore =
        dash.flowStep === 0 || dash.flowStep === 90
          ? FLOW8_REPLIES.ask
          : dash.flowStep === 1
            ? repliesFor(item, "read")
            : dash.flowStep === 2
              ? repliesFor(item, "choice")
              : dash.flowStep === 3
                ? repliesFor(item, "next")
                : dash.flowStep === 4
                  ? repliesFor(item, "divide")
                  : dash.flowStep === 6
                    ? FLOW8_REPLIES.after
                    : [];
      later(() => addAgent(dash.flowStep >= 8 ? "f8.understood" : "f8.nudge", restore, { item }), 400);
      return;
    }

    if (dash.flowId === 5 && dash.flowStep === 4) {
      acceptDifficulty(text);
      return;
    }

    if (dash.flowId === 4 && dash.flowStep === 4) {
      acceptPlanChange(text);
      return;
    }

    if (dash.flowId === 6 && dash.flowStep === 4) {
      acceptBlockage(text);
      return;
    }

    if (dash.flowId === 3 && (dash.flowStep === 0 || dash.flowStep === 3 || dash.flowStep === 5)) {
      if (dash.flowStep === 0) acceptMention(text);
      else if (dash.flowStep === 3) acceptDue(text);
      else acceptSteps(text);
      return;
    }

    addUser(text);

    if (dash.flowId === 4) {
      const restore =
        dash.flowStep === 0
          ? FLOW4_REPLIES.go
          : dash.flowStep === 1
            ? dayReplies(dash.week?.days || [])
            : dash.flowStep === 2
              ? FLOW4_REPLIES.see
              : dash.flowStep === 3
                ? FLOW4_REPLIES.review
                : [];
      later(() => addAgent("f4.nudge", restore), 400);
      return;
    }

    if (dash.flowId === 7) {
      const restore =
        dash.flowStep === 0
          ? FLOW7_REPLIES.subjects
          : dash.flowStep === 1
            ? FLOW7_REPLIES.progress
            : dash.flowStep === 2
              ? FLOW7_REPLIES.alert
              : dash.flowStep === 3
                ? FLOW7_REPLIES.details
                : [];
      later(() => addAgent(dash.flowStep >= 4 ? "f7.thanks" : "f7.nudge", restore), 400);
      return;
    }

    if (dash.flowId === 6) {
      if (dash.flowStep === 90 && /retour|maintenant/i.test(text)) {
        setDash((d) => ({
          ...d,
          flowStep: 1,
          status: FLOW6_STEPS[1],
          session: { ...emptySession(), ...d.session, activities: [] },
        }));
        later(() => addAgent("f6.activities", activityReplies()), 400);
        return;
      }
      const restore =
        dash.flowStep === 0 || dash.flowStep === 90
          ? FLOW6_REPLIES.ask
          : dash.flowStep === 1
            ? activityReplies(dash.session?.activities || [])
            : dash.flowStep === 2
              ? flow6DurationReplies()
              : dash.flowStep === 3
                ? difficultyReplies()
                : dash.flowStep === 5
                  ? FLOW6_REPLIES.adapt
                  : [];
      later(() => {
        addAgent(
          dash.flowStep >= 6 || dash.flowStep === 91 ? "f6.keep" : "f6.nudge",
          dash.flowStep >= 6 ? FLOW7_REPLIES.bridge : restore
        );
      }, 400);
      return;
    }

    if (dash.flowId === 5) {
      const restore =
        dash.flowStep === 0
          ? FLOW5_REPLIES.startNow
          : dash.flowStep === 1
            ? FLOW5_REPLIES.begin
            : dash.flowStep === 2
              ? dash.session?.hadPause && (dash.session?.checks || 0) < 1
                ? FLOW5_REPLIES.check
                : (dash.session?.checks || 0) >= 2
                  ? FLOW5_REPLIES.finish
                  : FLOW5_REPLIES.check
              : dash.flowStep === 3
                ? FLOW5_REPLIES.rate
                : dash.flowStep === 5
                  ? FLOW5_REPLIES.send
                  : [];
      later(() => {
        addAgent(dash.flowStep >= 6 ? "f5.settled" : "f5.nudge", restore);
      }, 400);
      return;
    }

    // If postponed and user says go — advance without duplicating user bubble
    if (
      dash.flowStep === 90 &&
      /oui|parti|prêt|pret|commenc/i.test(text)
    ) {
      setDash((d) => ({ ...d, flowStep: 1, status: "Rôle du tuteur" }));
      later(() => {
        addAgent("f2.role", FLOW2_REPLIES.understood);
      }, 400);
      return;
    }

    // After question branch → resume ready
    if (dash.flowId === 2 && dash.flowStep === 91) {
      later(() => {
        addAgent("f2.resume", FLOW2_REPLIES.ready);
        setDash((d) => ({ ...d, flowStep: 2, status: "Élève prêt" }));
      }, 400);
      return;
    }

    if (dash.flowId === 3) {
      const restore =
        dash.flowStep === 1
          ? subjectReplies()
          : dash.flowStep === 2
            ? typeReplies()
            : dash.flowStep === 4
              ? durationReplies()
              : [];
      later(() => {
        addAgent(dash.flowStep >= 6 ? "f3.done" : "f3.nudge", restore);
      }, 400);
      return;
    }

    // Mid-flow free text: gentle nudge to use buttons
    const restore =
      dash.flowStep === 0
        ? FLOW2_REPLIES.welcome
        : dash.flowStep === 1
          ? FLOW2_REPLIES.understood
          : dash.flowStep === 2
            ? FLOW2_REPLIES.ready
            : [];
    later(() => {
      addAgent("f2.nudge", restore);
    }, 400);
  };

  const revertKey = (key) => {
    setPendingKeys((current) => current.filter((item) => item !== key));
    persist((prev) => {
      const overrides = { ...prev.overrides };
      delete overrides[key];
      return {
        ...prev,
        overrides,
        integrations: prev.integrations.filter(
          (item) => !(item.key === key && item.scope !== "prompt")
        ),
      };
    });
    markPending(key);
    pushLog(`Texte d’origine rétabli · ${getFlowMessage(key).label}`);
  };

  const saveComment = (key, text) => {
    if (!key) return;
    persist((prev) => ({
      ...prev,
      comments: { ...(prev.comments || {}), [key]: text },
    }));
  };

  const rememberIntegration = (key, fields) => {
    const meta = getFlowMessage(key);
    persist((prev) => ({
      ...prev,
      overrides: { ...prev.overrides, [key]: fields.after },
      integrations: [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          key,
          scope: "text",
          label: meta.label,
          flowId: meta.flowId,
          step: meta.step,
          at: new Date().toISOString(),
          ...fields,
        },
        ...prev.integrations,
      ].slice(0, 50),
    }));
    markPending(key);
    pushLog(`Enregistré · ${meta.label} · ${fields.summary} · cliquez sur Intégrer`);
  };

  const markPending = (key) =>
    setPendingKeys((current) => (current.includes(key) ? current : [...current, key]));

  const stepOf = (key) => {
    try {
      const meta = getFlowMessage(key);
      return `${meta.flowId}:${meta.step}`;
    } catch {
      return "";
    }
  };

  // Applies saved edits: replays the chat from the first edited step on screen.
  const integratePending = () => {
    const keys = pendingKeys;
    setPendingKeys([]);
    if (!keys.length) return;
    const parentSide = role === "parent";
    const chat = parentSide ? parentMessages : messages;
    const hit = chat.find((item) => keys.includes(item.messageKey));
    const meta = hit ? getFlowMessage(hit.messageKey) : null;
    const nodeId = meta ? `s${meta.flowId}-${meta.step}` : "";
    const node = meta ? findNode(blueprintRef.current, meta.flowId, nodeId) : null;
    const playable = meta && (PARENT_V0_NODES[nodeId] || node);
    // Messages shown outside a flow step (menu answers) are refreshed in place.
    const offStep = node?.messageKey && node.messageKey !== hit?.messageKey;
    if (!playable || offStep) {
      const refresh = (current) => current.map((item) => (keys.includes(item.messageKey) && !SPLIT_KEYS.has(item.messageKey) ? withRevision([item])[0] : item));
      setMessages(refresh);
      setParentMessages(refresh);
      pushLog("Modifications intégrées.");
      return;
    }
    const target = `${meta.flowId}:${meta.step}`;
    const start = chat.findIndex((item) => item.messageKey && stepOf(item.messageKey) === target);
    const trim = (current) => current.slice(0, start);
    if (parentSide) setParentMessages(trim);
    else setMessages(trim);
    pushLog(`Intégré · reprise à l’étape « ${meta.label} »`);
    playNode(meta.flowId, nodeId, "continue");
  };

  const playFromMap = (flowId, fresh = false) => {
    const audience = findFlow(blueprintRef.current, flowId)?.audience;
    if (audience === "eleve" || audience === "parent") setRole(audience);
    if (fresh) {
      clearTimers();
      setTyping(false);
      setWire(null);
      if (audience === "parent") {
        parentChildRef.current = null;
        setParentMessages([]);
        setParentReplies([]);
      } else {
        setMessages([]);
        setQuickReplies([]);
      }
    }
    if (flowId === 12) {
      setDash((current) => ({ ...current, view: "parent", flowId: 12 }));
      return showReport("parent");
    }
    return startParentFlow(flowId);
  };

  const replayFlow = () => {
    setWire(null);
    setPendingKeys([]);
    clearTimers();
    setTyping(false);
    if (role === "parent") {
      parentChildRef.current = null;
      setParentMessages([]);
      setParentReplies([]);
      if (dash.flowId === 11) return playNode(11, "s11-1");
      if (dash.flowId === 12) return showReport("parent");
      return startParentFlow([10, 13].includes(dash.flowId) ? dash.flowId : 1);
    }
    if ([11, 12, 14, 15, 16, 17, 18, 19, 20, 21, 22, 23, 24].includes(dash.flowId)) {
      setMessages([]);
      setQuickReplies([]);
      return startParentFlow(dash.flowId);
    }
    if (onFlow9) return beginFlow9(false);
    if (onFlow8) return beginFlow8(false);
    if (onFlow7) return beginFlow7(false);
    if (onFlow6) return beginFlow6(false);
    if (onFlow5) return beginFlow5(false);
    if (onFlow4) return beginFlow4(false);
    if (onFlow3) return beginFlow3(false);
    return startFlow2();
  };

  const rememberPrompt = (key, fields) => {
    const meta = getFlowMessage(key);
    persist((prev) => ({
      ...prev,
      promptOverrides: { ...(prev.promptOverrides || {}), [key]: fields.after },
      integrations: [
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
          key,
          scope: "prompt",
          label: meta.label,
          flowId: meta.flowId,
          step: meta.step,
          at: new Date().toISOString(),
          ...fields,
        },
        ...prev.integrations,
      ].slice(0, 50),
    }));
    pushLog(`Prompt intégré · ${meta.label} · ${fields.summary}`);
  };

  const revertPrompt = (key) => {
    persist((prev) => {
      const promptOverrides = { ...(prev.promptOverrides || {}) };
      delete promptOverrides[key];
      return {
        ...prev,
        promptOverrides,
        integrations: prev.integrations.filter(
          (item) => !(item.key === key && item.scope === "prompt")
        ),
      };
    });
    pushLog(`Prompt d’origine rétabli · ${getFlowMessage(key).label}`);
  };

  const applyMessageComment = ({ comment, draft, selection, target }) => {
    const result = applyLabComment(draft, comment, selection);
    if (!result.ok) return result;
    if (target === "prompt") {
      rememberPrompt(targetKey, {
        kind: "prompt-comment",
        comment: comment.trim(),
        summary: result.summary,
        before: draft,
        after: result.text,
      });
      return { ok: true, summary: `${result.summary}. Le prompt est mis à jour.` };
    }
    rememberIntegration(targetKey, {
      kind: "comment",
      comment: comment.trim(),
      summary: result.summary,
      before: draft,
      after: result.text,
    });
    return { ok: true, summary: `${result.summary}. Cliquez sur « Intégrer » pour le voir dans la conversation.` };
  };

  const saveMessageText = (draft) => {
    const next = draft.trim();
    if (!next) return { ok: false, reason: "Le message ne peut pas être vide." };
    if (next === targetResolved) {
      return { ok: false, reason: "Aucun changement à enregistrer." };
    }
    if (next === targetOriginal.trim()) {
      revertKey(targetKey);
      return { ok: true, summary: "Retour au texte d’origine." };
    }
    rememberIntegration(targetKey, {
      kind: "edit",
      comment: "",
      summary: "Texte modifié",
      before: targetResolved,
      after: next,
    });
    return { ok: true, summary: "Texte enregistré. Cliquez sur « Intégrer » pour le voir dans la conversation." };
  };

  const savePromptText = (draft) => {
    const next = draft.trim();
    if (!next) return { ok: false, reason: "Le prompt ne peut pas être vide." };
    if (next === targetPromptResolved) {
      return { ok: false, reason: "Aucun changement à enregistrer." };
    }
    if (next === targetPromptOriginal.trim()) {
      revertPrompt(targetKey);
      return { ok: true, summary: "Retour au prompt d’origine." };
    }
    rememberPrompt(targetKey, {
      kind: "prompt",
      comment: "",
      summary: "Prompt modifié",
      before: targetPromptResolved,
      after: next,
    });
    return { ok: true, summary: "Prompt enregistré. Il remplace la consigne de cette étape." };
  };

  const savePromptFor = (key, draft) => {
    const meta = getFlowMessage(key);
    const original = meta.prompt;
    const current =
      typeof revisionsRef.current.promptOverrides?.[key] === "string"
        ? revisionsRef.current.promptOverrides[key]
        : original;
    const next = draft.trim();
    if (!next) return { ok: false, reason: "Le prompt ne peut pas être vide." };
    if (next === current) return { ok: true, summary: "" };
    setFollowLive(false);
    setTargetKey(key);
    if (next === original.trim()) {
      revertPrompt(key);
      return { ok: true, summary: "Retour au prompt d’origine." };
    }
    rememberPrompt(key, {
      kind: "prompt",
      comment: "",
      summary: "Prompt modifié",
      before: current,
      after: next,
    });
    return { ok: true, summary: "Prompt enregistré." };
  };

  const saveMessageFor = (key, draft) => {
    const meta = getFlowMessage(key);
    const original = meta.text({
      ...FIELD_CTX,
      subject: dash.objective?.subject || (dash.subject !== "—" ? dash.subject : ""),
      minutes: dash.minutes || 25,
      item: "marge",
    });
    const current =
      typeof revisionsRef.current.overrides?.[key] === "string"
        ? revisionsRef.current.overrides[key]
        : original;
    const next = draft.trim();
    if (!next) return { ok: false, reason: "Le message ne peut pas être vide." };
    if (next === current) return { ok: true, summary: "" };
    setFollowLive(false);
    setTargetKey(key);
    if (next === original.trim()) {
      revertKey(key);
      return { ok: true, summary: "Retour au texte d’origine." };
    }
    rememberIntegration(key, {
      kind: "edit",
      comment: "",
      summary: "Texte modifié",
      before: current,
      after: next,
    });
    return { ok: true, summary: "Texte enregistré. Cliquez sur « Intégrer » pour le voir dans la conversation." };
  };

  const clearRevisions = () => {
    const empty = emptyRevisions();
    revisionsRef.current = empty;
    saveRevisions(empty);
    setRevisions(empty);
    setMessages((current) =>
      current.map((item) =>
        item.sourceText ? { ...item, text: item.sourceText, revised: false } : item
      )
    );
    pushLog("Messages et prompts d’origine rétablis.");
  };

  const targetMessage = getFlowMessage(targetKey);
  // The editor shows fields ({enfant}, {student}…) instead of names, so edits keep them.
  const targetCtx = {
    ...FIELD_CTX,
    subject: dash.objective?.subject || (dash.subject !== "—" ? dash.subject : ""),
    minutes: dash.minutes || 25,
  };
  const targetOriginal = targetMessage.text(targetCtx);
  const targetResolved =
    typeof revisions.overrides[targetKey] === "string"
      ? revisions.overrides[targetKey]
      : targetOriginal;
  const targetRevised = typeof revisions.overrides[targetKey] === "string";
  const targetPromptOriginal = targetMessage.prompt;
  const targetPromptResolved =
    typeof revisions.promptOverrides?.[targetKey] === "string"
      ? revisions.promptOverrides[targetKey]
      : targetPromptOriginal;
  const targetPromptRevised = typeof revisions.promptOverrides?.[targetKey] === "string";

  const onFlow1 = dash.flowId === 1;
  const onFlow10 = dash.flowId === 10;
  const onFlow11 = dash.flowId === 11;
  const onFlow12 = dash.flowId === 12;
  const onFlow13 = dash.flowId === 13;
  const onFlow14 = dash.flowId === 14;
  const liveStats = simStats();
  const onFlow9 = dash.flowId === 9;
  const onFlow8 = dash.flowId === 8;
  const onFlow7 = dash.flowId === 7;
  const onFlow6 = dash.flowId === 6;
  const onFlow5 = dash.flowId === 5;
  const onFlow4 = dash.flowId === 4;
  const onFlow3 = dash.flowId === 3;
  const parentSpine = onFlow1 ? spineNodes(blueprint.flows.find((item) => item.id === 1)) : [];
  const activeSteps = onFlow1
    ? parentSpine.map((node) => node.title)
    : onFlow10
    ? FLOW10_STEPS
    : onFlow11
    ? FLOW11_STEPS
    : onFlow12
    ? FLOW12_STEPS
    : onFlow13
    ? FLOW13_STEPS
    : onFlow14
    ? FLOW14_STEPS
    : STUDENT_FLOW_STEPS[dash.flowId]
    ? STUDENT_FLOW_STEPS[dash.flowId]
    : MVP_FLOWS[dash.flowId]
    ? MVP_FLOWS[dash.flowId].steps
    : onFlow9
    ? FLOW9_STEPS
    : onFlow8
    ? FLOW8_STEPS
    : onFlow7
    ? FLOW7_STEPS
    : onFlow6
      ? FLOW6_STEPS
      : onFlow5
        ? FLOW5_STEPS
        : onFlow4
          ? FLOW4_STEPS
          : onFlow3
            ? FLOW3_STEPS
            : FLOW2_STEPS;
  const activeIndex = onFlow1
    ? Math.max(0, parentSpine.findIndex((node) => (node.scriptIndex ?? 0) === dash.flowStep))
    : onFlow10
    ? Math.min(dash.flowStep, FLOW10_STEPS.length - 1)
    : onFlow11 || onFlow12
    ? Math.min(dash.flowStep, (onFlow11 ? FLOW11_STEPS : FLOW12_STEPS).length - 1)
    : onFlow13 || onFlow14
    ? Math.min(dash.flowStep, 1)
    : STUDENT_FLOW_STEPS[dash.flowId]
    ? Math.min(dash.flowStep, STUDENT_FLOW_STEPS[dash.flowId].length - 1)
    : MVP_FLOWS[dash.flowId]
    ? Math.min(dash.flowStep, MVP_FLOWS[dash.flowId].steps.length - 1)
    : onFlow9
    ? flow9DisplayIndex(dash.flowStep)
    : onFlow8
    ? flow8DisplayIndex(dash.flowStep)
    : onFlow7
    ? flow7DisplayIndex(dash.flowStep)
    : onFlow6
      ? flow6DisplayIndex(dash.flowStep)
      : onFlow5
        ? flow5DisplayIndex(dash.flowStep)
        : onFlow4
          ? flow4DisplayIndex(dash.flowStep)
          : onFlow3
            ? flow3DisplayIndex(dash.flowStep)
            : flow2DisplayIndex(dash.flowStep);
  const flowComplete = onFlow1
    ? dash.flowStep >= 3
    : onFlow13 || onFlow14
    ? dash.flowStep >= 1
    : onFlow10
    ? dash.flowStep >= 2
    : onFlow11
    ? dash.flowStep >= 3
    : onFlow12
    ? dash.flowStep >= 2
    : onFlow9
    ? dash.flowStep >= 5
    : onFlow8
    ? dash.flowStep >= 8
    : onFlow7
    ? dash.flowStep >= 4
    : onFlow6
      ? dash.flowStep >= 6 || dash.flowStep === 91
      : onFlow5
        ? dash.flowStep >= 6
        : onFlow4
          ? dash.flowStep >= 7
          : onFlow3
            ? dash.flowStep >= 6
            : dash.flowStep >= 5 && dash.flowStep < 90;
  const progress = flowComplete
    ? 100
    : Math.round((activeIndex / activeSteps.length) * 100);
  const currentTitle = wire
    ? findFlow(blueprint, wire.flowId).title
    : flowTitle(dash.flowId);
  const wiredFlow = blueprint.flows.find((item) => item.id === (wire?.flowId || dash.flowId));
  const wiredSpine = wiredFlow ? spineNodes(wiredFlow) : [];
  const wiredMatch = wire
    ? wiredSpine.findIndex((node) => node.id === wire.nodeId)
    : wiredSpine.findIndex((node) => node.scriptIndex === activeIndex);
  const shownIndex = wiredMatch >= 0 ? wiredMatch : activeIndex;
  const shownCount = wiredSpine.length || activeSteps.length;
  const shownLabel = wire
    ? findNode(blueprint, wire.flowId, wire.nodeId)?.title || activeSteps[activeIndex]
    : wiredSpine[shownIndex]?.title || activeSteps[activeIndex];
  const nativeScreen = appMode && immersive && narrow;
  const mobileType = appMode && narrow;
  // Phone drawn inside the page (not full screen): less room in the header.
  const framedMobile = mobileType && !immersive;

  return (
    <div
      className={
        appMode
          ? "flex w-full min-w-0 max-w-full justify-center overflow-x-hidden text-[#122118]"
          : "fixed inset-0 z-[60] bg-[#0E1A14]/55 backdrop-blur-sm flex items-stretch sm:items-center justify-center p-0 sm:p-4"
      }
      data-testid={appMode ? "study-app" : "dynamic-test-lab"}
      role={appMode ? undefined : "dialog"}
      aria-modal={appMode ? undefined : true}
      aria-labelledby="lab-title"
    >
      <input
        ref={photoRef}
        type="file"
        accept="image/*"
        className="sr-only"
        data-testid="lab-help-file"
        onChange={onHelpPhotoFile}
      />
      <div
        className={
          appMode
            ? "flex w-full min-w-0 max-w-full justify-center"
            : "w-full max-w-6xl bg-[#F7FAF8] sm:rounded-3xl shadow-2xl border border-white/40 overflow-hidden flex flex-col max-h-[100vh] sm:max-h-[92vh]"
        }
      >
        {!appMode && (
        <div className="relative z-30 flex flex-wrap sm:flex-nowrap items-center justify-between gap-x-3 gap-y-1 px-3 sm:px-5 py-2 sm:py-3 bg-white border-b border-gray-100">
          <div className="min-w-0 basis-full sm:basis-auto sm:flex-1">
            <div className="flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-[#25D366] animate-pulse" />
              <h2
                id="lab-title"
                className="font-display font-bold text-[#122118] text-sm sm:text-base truncate"
              >
                {currentTitle}
              </h2>
            </div>
            <p className="text-xs text-[#4a5f53] mt-0.5 truncate">
              Étape {Math.min(shownIndex, shownCount - 1) + 1}/{shownCount} · {shownLabel} ·
              simulation locale
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-1 sm:ml-auto sm:shrink-0">
            <div className="relative">
              <button
                type="button"
                data-testid="lab-toc"
                aria-expanded={tocOpen}
                aria-controls="lab-toc-panel"
                onClick={() => {
                  setTreeOpen(false);
                  setPromptsOpen(false);
                  setTocOpen((openToc) => !openToc);
                }}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold text-[#122118] hover:bg-gray-100"
              >
                <List className="w-3.5 h-3.5" />
                Sommaire
              </button>
              {tocOpen && (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-10 cursor-default"
                    aria-label="Fermer le sommaire"
                    onClick={() => setTocOpen(false)}
                  />
                  <div
                    id="lab-toc-panel"
                    data-testid="lab-toc-panel"
                    className="absolute left-0 sm:left-auto sm:right-0 top-full z-20 mt-2 w-[min(20rem,calc(100vw-2rem))] max-h-[70vh] overflow-y-auto rounded-2xl border border-gray-100 bg-white p-3 shadow-xl"
                  >
                    <p className="px-2 pb-2 text-[10px] font-bold uppercase tracking-wider text-[#829C8D]">
                      {role === "parent" ? "Flows parent" : "Sommaire"}
                    </p>
                    {!appMode && (
                      <label className="mb-2 flex items-center gap-2 rounded-xl bg-[#F7FAF8] px-2 py-1.5 text-[11px] text-[#4a5f53]">
                        <input
                          type="checkbox"
                          data-testid="lab-show-archived"
                          checked={showArchived}
                          onChange={(event) => setShowArchived(event.target.checked)}
                        />
                        Flows archivés (niveaux 2 et 3)
                      </label>
                    )}
                    {[
                      ["parent", "Parent", [1, 13, 10, 12, 7]],
                      ["eleve", "Élève", [2, 14, 11, 3, 4, 5, 6, 8, 9]],
                    ].flatMap(([audience, groupLabel, order]) => [
                      <p key={`group-${audience}`} className="mt-2 px-2 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#829C8D] first:mt-0" data-testid={`lab-toc-group-${audience}`}>
                        {groupLabel}
                      </p>,
                      ...flowsByAudience(blueprint, audience)
                        .filter(visibleFlow)
                        .sort((a, b) => (order.indexOf(a.id) + 1 || 99) - (order.indexOf(b.id) + 1 || 99))
                        .map((flow) => {
                      const currentFlow = dash.flowId === flow.id;
                      const steps = spineNodes(flow).filter(
                        (node) => !(levelOne && !FEATURES.availability && node.id === "s2-3")
                      );
                      return (
                        <div key={flow.id} className="mb-2 last:mb-0">
                          <p
                            className={`px-2 py-1 text-xs font-semibold ${
                              currentFlow ? "text-[#189346]" : "text-[#122118]"
                            }`}
                          >
                            {flow.title}
                            {isArchivedFlow(flow.id) && (
                              <span className="ml-1.5 rounded-full bg-[#F1ECFF] px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#5b3fb0]" data-testid={`lab-toc-hidden-${flow.id}`}>
                                caché
                              </span>
                            )}
                          </p>
                          <ol>
                            {steps.map((node, index) => {
                              const current =
                                wire?.nodeId === node.id ||
                                (!wire && currentFlow && node.scriptIndex === activeIndex);
                              return (
                                <li key={node.id}>
                                  <button
                                    type="button"
                                    data-testid={`lab-toc-${flow.id}-${node.scriptIndex ?? index}`}
                                    onClick={() => {
                                      if (flow.audience === "parent") {
                                        playNode(flow.id, node.id);
                                        return;
                                      }
                                      node.scriptIndex == null || flow.id >= 10
                                        ? playNode(flow.id, node.id)
                                        : openFromToc(flow.id, node.scriptIndex);
                                    }}
                                    className={`flex w-full items-start gap-2 rounded-xl px-2 py-1.5 text-left text-xs ${
                                      current
                                        ? "bg-[#E9FBF0] font-semibold text-[#0B8F6A]"
                                        : "text-[#4a5f53] hover:bg-[#F7FAF8]"
                                    }`}
                                  >
                                    <span className="mt-0.5 w-4 shrink-0 text-[10px] font-bold">
                                      {index + 1}
                                    </span>
                                    <span>{node.title}</span>
                                  </button>
                                </li>
                              );
                            })}
                          </ol>
                        </div>
                      );
                    }),
                    ])}
                  </div>
                </>
              )}
            </div>
            <div className="relative">
              <button
                type="button"
                data-testid="lab-tree"
                aria-expanded={treeOpen}
                aria-controls="lab-tree-panel"
                onClick={() => {
                  setTocOpen(false);
                  setPromptsOpen(false);
                  setTreeOpen((openTree) => !openTree);
                }}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold text-[#122118] hover:bg-gray-100"
              >
                <GitBranch className="w-3.5 h-3.5" />
                Arbre
              </button>
              {treeOpen && (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-10 cursor-default"
                    aria-label="Fermer l’arbre"
                    onClick={() => setTreeOpen(false)}
                  />
                  <LabFlowTree
                    blueprint={blueprint}
                    flowId={
                      role === "parent" && flowsByAudience(blueprint, "parent").some((item) => item.id === dash.flowId)
                        ? dash.flowId
                        : role === "parent"
                          ? 1
                          : dash.flowId
                    }
                    audience={role === "parent" ? "parent" : "eleve"}
                    revisions={revisions}
                    studentName={studentName}
                    activeNodeId={wire?.nodeId || ""}
                    onChange={(next) => {
                      blueprintRef.current = next;
                      saveFlowBlueprint(next);
                      setBlueprint(next);
                    }}
                    onPlay={playNode}
                    onOpenSimulation={openFromToc}
                    onSaveScriptPrompt={savePromptFor}
                    onSaveScriptMessage={saveMessageFor}
                    onSaveComment={saveComment}
                    onClose={() => setTreeOpen(false)}
                  />
                </>
              )}
            </div>
            <div className="relative">
              <button
                type="button"
                data-testid="lab-prompts"
                aria-expanded={promptsOpen}
                aria-controls="lab-prompts-panel"
                onClick={() => {
                  setTocOpen(false);
                  setTreeOpen(false);
                  setPromptsOpen((openPrompts) => !openPrompts);
                }}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold text-[#122118] hover:bg-gray-100"
              >
                <ScrollText className="w-3.5 h-3.5" />
                Prompts
              </button>
              {promptsOpen && (
                <>
                  <button
                    type="button"
                    className="fixed inset-0 z-10 cursor-default"
                    aria-label="Fermer les prompts"
                    onClick={() => setPromptsOpen(false)}
                  />
                  <LabPromptsPanel
                    revisions={revisions}
                    studentName={studentName}
                    onClose={() => setPromptsOpen(false)}
                  />
                </>
              )}
            </div>
            <button
              type="button"
              onClick={() => {
                setRole("eleve");
                setDash((current) => ({ ...current, view: "eleve" }));
              }}
              aria-pressed={role === "eleve"}
              data-testid="lab-role-eleve"
              className={`rounded-full px-3 py-2 text-xs font-semibold ${
                role === "eleve" ? "bg-[#122118] text-white" : "text-[#122118] hover:bg-gray-100"
              }`}
            >
              Élève
            </button>
            <button
              type="button"
              onClick={openParent}
              aria-pressed={role === "parent"}
              data-testid="lab-role-parent"
              className={`rounded-full px-3 py-2 text-xs font-semibold ${
                role === "parent" ? "bg-[#122118] text-white" : "text-[#122118] hover:bg-gray-100"
              }`}
            >
              Parent
            </button>
            {!appMode && (
              <select
                value={simForfait}
                onChange={(event) => {
                  const level = Number(event.target.value);
                  setSimForfait(level);
                  setSimForfaitState(level);
                  pushLog(`Forfait simulé : ${["", "Essentiel", "Famille", "Famille Plus"][level]}`);
                }}
                data-testid="lab-forfait"
                className="rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-[#2d5a3d]"
                aria-label="Forfait simulé"
              >
                <option value={1}>Essentiel</option>
                <option value={2}>Famille</option>
                <option value={3}>Famille Plus</option>
              </select>
            )}
            {!appMode && <DemoControls play={(flowId) => playFromMap(flowId, true)} />}
            <button
              type="button"
              onClick={() => setLoopMapOpen(true)}
              data-testid="lab-loop-map"
              className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-white px-3 py-2 text-xs font-semibold text-[#2d5a3d] hover:border-[#25D366]"
            >
              Carte des boucles
            </button>
            {loopMapOpen && (
              <LoopMap
                titleOf={flowTitle}
                onClose={() => setLoopMapOpen(false)}
                onPlay={(flowId) => {
                  setLoopMapOpen(false);
                  playFromMap(flowId);
                }}
              />
            )}
            {pendingKeys.length > 0 && (
              <button
                type="button"
                onClick={integratePending}
                data-testid="lab-integrate"
                className="inline-flex items-center gap-1.5 rounded-full bg-[#189346] px-3 py-2 text-xs font-semibold text-white hover:bg-[#127a39]"
              >
                <Check className="w-3.5 h-3.5" />
                Intégrer ({pendingKeys.length})
              </button>
            )}
            <button
              type="button"
              onClick={replayFlow}
              data-testid="lab-replay"
              className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold text-[#122118] hover:bg-gray-100"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Rejouer
            </button>
            <button
              type="button"
              onClick={onClose}
              data-testid="lab-close"
              className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        )}

        <div className={appMode ? "flex w-full min-w-0 max-w-full justify-center overflow-x-hidden" : "flex-1 overflow-y-auto p-4 sm:p-5"}>
          <div className={appMode ? "flex w-full min-w-0 max-w-full justify-center" : "grid lg:grid-cols-2 gap-4 lg:gap-5"}>
            {/* Phone */}
            <div className="flex w-full min-w-0 max-w-full flex-col items-center">
              <div
                className={
                  nativeScreen
                    ? "fixed z-50 flex flex-col overflow-hidden bg-[#EFEAE2] text-[#122118]"
                    : appMode
                      ? `relative mx-auto flex shrink-0 flex-col overflow-hidden border border-black/40 bg-[#0e1311] ${narrow ? "w-[calc(100vw-1rem)] max-w-[420px] rounded-[30px] p-1.5" : "w-[340px] max-w-[calc(100vw-2rem)] rounded-[44px] p-3"}`
                      : "relative flex h-[580px] w-[340px] shrink-0 flex-col overflow-hidden rounded-[32px] border-[8px] border-[#1a1a1a] bg-[#EFEAE2] text-[#122118] shadow-xl"
                }
                style={
                  nativeScreen
                    ? {
                        top: frameBox.top,
                        left: frameBox.left,
                        width: frameBox.width,
                        height: frameBox.height,
                      }
                    : appMode
                      ? { height: narrow ? "calc(100dvh - 5.5rem)" : "min(718px, calc(100dvh - 7rem))" }
                      : undefined
                }
                data-testid="lab-phone"
                data-immersive={appMode && immersive ? "true" : "false"}
              >
                {appMode && !nativeScreen && (
                  <div className="absolute top-3 left-1/2 z-20 hidden h-6 w-28 -translate-x-1/2 rounded-full bg-black sm:block" />
                )}
                <div
                  className={
                    appMode && !nativeScreen
                      ? `relative flex min-h-0 w-full flex-1 flex-col overflow-hidden bg-white ${narrow ? "rounded-[25px]" : "rounded-[34px]"}`
                      : nativeScreen
                        ? "flex min-h-0 w-full flex-1 flex-col overflow-hidden"
                        : "contents"
                  }
                >
                {feedbackOpen && (
                  <FeedbackDialog token={accountToken || readSession()?.token || ""} context={`${appMode ? "app" : "simulateur"} · ${role}`} onClose={() => setFeedbackOpen(false)} />
                )}
                <SideMenu open={sideMenuOpen} parent={role === "parent"} canSettings={Boolean(onOpenSettings)} mobileType={mobileType} onPick={pickSideMenu} onClose={() => setSideMenuOpen(false)} />
                <div className={`shrink-0 bg-[#075E54] text-white flex items-center ${
                  mobileType ? "gap-2 px-2 pb-3 pt-[max(0.7rem,env(safe-area-inset-top))]" : nativeScreen ? "gap-2 px-3 pb-2.5 pt-[max(0.6rem,env(safe-area-inset-top))]" : appMode ? "gap-2 px-4 pt-8 pb-3" : "gap-2 px-3 py-2.5"
                }`}>
                  <button
                    type="button"
                    aria-label="Ouvrir le menu"
                    data-testid="phone-menu"
                    onClick={() => {
                      setPhonePanel("");
                      setSideMenuOpen(true);
                    }}
                    className="inline-flex shrink-0 items-center justify-center rounded-full p-0.5 hover:bg-white/10"
                  >
                    <MenuIcon className={mobileType ? "h-6 w-6 opacity-90" : "h-4 w-4 opacity-80"} />
                  </button>
                  <div className={`shrink-0 rounded-full bg-[#25D366] text-[#0A2914] font-bold flex items-center justify-center ${mobileType ? (framedMobile ? "h-10 w-10 text-sm" : "h-11 w-11 text-base") : "h-8 w-8 text-xs"}`}>
                    PÉ
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`flex min-w-0 items-center gap-1 whitespace-nowrap font-bold ${mobileType ? (framedMobile ? "text-[17px] leading-tight" : "text-[19px] leading-tight") : "text-sm"}`}>
                      <span className="min-w-0 overflow-hidden">Plan Étude</span>
                      {!framedMobile && <span className={`text-[#25D366] ${mobileType ? "text-base" : "text-[10px]"}`}>✓</span>}
                    </div>
                    <div className={`truncate ${mobileType ? (framedMobile ? "text-[13px] leading-tight text-white/85" : "text-[15px] leading-tight text-white/85") : "text-[10px] text-white/80"}`}>
                      {typing ? "écrit…" : role === "parent" ? `Parent · ${parentName}` : `Élève · ${studentName}`}
                    </div>
                  </div>
                  <div className={`flex shrink-0 items-center ${mobileType ? (framedMobile ? "gap-1" : "gap-3") : "gap-1"}`}>
                  <button
                    type="button"
                    aria-label="Caméra"
                    aria-expanded={phonePanel === "camera"}
                    data-testid="phone-camera"
                    onClick={() => setPhonePanel((current) => (current === "camera" ? "" : "camera"))}
                    className={`${mobileType ? "hidden" : "inline-flex"} shrink-0 items-center justify-center rounded-full hover:bg-white/10 ${mobileType ? (framedMobile ? "h-10 w-9" : "h-11 w-11") : "h-7 w-7"}`}
                  >
                    <Video className={mobileType ? "h-6 w-6" : "h-4 w-4 opacity-80"} />
                  </button>
                  <button
                    type="button"
                    aria-label="Téléphone"
                    aria-expanded={phonePanel === "phone"}
                    data-testid="phone-call"
                    onClick={() => setPhonePanel((current) => (current === "phone" ? "" : "phone"))}
                    className={`${mobileType ? "hidden" : "inline-flex"} shrink-0 items-center justify-center rounded-full hover:bg-white/10 ${mobileType ? (framedMobile ? "h-10 w-9" : "h-11 w-11") : "h-7 w-7"}`}
                  >
                    <Phone className={mobileType ? "h-6 w-6" : "h-4 w-4 opacity-80"} />
                  </button>
                  <button
                    type="button"
                    aria-label="Plein écran"
                    aria-pressed={appMode && immersive}
                    data-testid="phone-overflow"
                    onClick={() => {
                      setPhonePanel("");
                      if (!appMode) return;
                      setImmersive((current) => {
                        const next = !current;
                        onImmersive?.(next);
                        return next;
                      });
                    }}
                    className={`inline-flex shrink-0 items-center justify-center rounded-full hover:bg-white/10 ${mobileType ? (framedMobile ? "h-10 w-9" : "h-11 w-11") : "h-7 w-7"}`}
                  >
                    <MoreVertical className={mobileType ? "h-6 w-6" : "h-4 w-4 opacity-80"} />
                  </button>
                  </div>
                </div>
                {phonePanel && (
                  <div
                    className={`mx-3 mt-3 rounded-2xl bg-white px-3 py-2.5 leading-snug text-[#122118] shadow-sm ${mobileType ? "text-[15px]" : "text-[13px]"}`}
                    data-testid={phonePanel === "camera" ? "phone-camera-panel" : "phone-ai-panel"}
                  >
                    {phonePanel === "camera"
                      ? "Pour se connecter avec un tuteur de succès scolaire, demandez à votre parent."
                      : "Pour discuter avec une IA, demandez à votre parent de passer au format IA."}
                  </div>
                )}

                <div
                  ref={chatRef}
                  className={`relative min-h-0 flex-1 px-3 py-3 flex flex-col gap-1 [&>*]:shrink-0 ${appMode ? "wa-bg" : ""} ${
                    nativeScreen
                      ? "overflow-x-hidden overflow-y-auto overscroll-y-contain touch-pan-y text-[15px] leading-[1.38]"
                      : appMode
                        ? "chat-scroll overflow-x-hidden"
                        : "overflow-y-auto"
                  }`}
                >
                  <div className="self-center my-2 text-center">
                    <div className={`inline-block bg-[#E1F2FB] text-[#54656F] rounded-md px-2.5 py-1 font-medium ${mobileType ? "text-[13px]" : "text-[10px]"}`}>
                      Aujourd’hui
                    </div>
                  </div>

                  <AnimatePresence initial={false}>
                    { (role === "parent" ? parentMessages : messages).map((msg, index, list) => {
                      if (msg.kind === "section") {
                        if (appMode && msg.text !== "Accueil") return null;
                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 4 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="self-center my-2"
                            data-testid="lab-section-title"
                          >
                            <div className={`inline-block rounded-md bg-white/95 px-3 py-1 text-[#667781] shadow-sm ${mobileType ? "text-[13px]" : "text-[11px]"}`}>
                              {msg.text}
                            </div>
                          </motion.div>
                        );
                      }
                      if (msg.kind === "photo") {
                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="self-end max-w-[78%] overflow-hidden rounded-2xl rounded-tr-sm bg-[#DCF8C6] shadow-sm"
                            data-testid="lab-help-photo"
                          >
                            <img
                              src={msg.src || sampleExerciseUrl()}
                              alt={msg.alt || "Photo de l'exercice"}
                              className="block w-full"
                            />
                            <div className="px-2 pb-1 text-right text-[10px] text-gray-500">{msg.time}</div>
                          </motion.div>
                        );
                      }
                      if (msg.kind === "extract") {
                        return (
                          <InfoCard
                            key={msg.id}
                            testId="lab-photo-extract"
                            title="Lecture de la photo"
                            subtitle="Matière et thème reconnus"
                            rows={msg.rows || EXTRACT_ROWS}
                            time={msg.time}
                          />
                        );
                      }
                      if (msg.kind === "margins") {
                        return (
                          <InfoCard
                            key={msg.id}
                            testId="lab-margin-table"
                            title={msg.title || "Selon le nombre d'élèves"}
                            subtitle={msg.subtitle || "Proportion observée : 30 %"}
                            rows={msg.rows || MARGIN_ROWS}
                            time={msg.time}
                          />
                        );
                      }
                      if (msg.kind === "calc") {
                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="self-start max-w-[92%] overflow-hidden rounded-2xl rounded-tl-sm border border-[#C6EBD7] bg-white shadow-sm"
                            data-testid="lab-formula-steps"
                          >
                            <div className="bg-[#E9FBF0] px-3 py-2 text-sm font-semibold text-[#122118]">
                              Calcul écrit en notation réelle
                            </div>
                            <img
                              src={msg.item === "pythagore" ? pythagoreFormulaUrl() : formulaStepsUrl()}
                              alt={msg.item === "pythagore" ? "Calcul de Pythagore" : "Calcul de la marge d'erreur pour 100 élèves"}
                              className="block w-full"
                            />
                            <div className="px-2 pb-1 text-right text-[10px] text-gray-500">{msg.time}</div>
                          </motion.div>
                        );
                      }
                      if (msg.kind === "fiche") {
                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="self-start max-w-[92%] overflow-hidden rounded-2xl rounded-tl-sm border border-[#C6EBD7] bg-white shadow-sm"
                            data-testid="lab-visual-summary"
                          >
                            <div className="bg-[#E9FBF0] px-3 py-2 text-sm font-semibold text-[#122118]">
                              Image · formules visuelles
                            </div>
                            <img
                              src={
                                msg.src ||
                                (msg.item === "pythagore" ? pythagoreSummaryUrl() : visualSummaryUrl())
                              }
                              alt={
                                msg.src
                                  ? "Résumé visuel"
                                  : msg.item === "pythagore"
                                    ? "Formule de Pythagore"
                                    : "Résumé visuel de la marge d'erreur"
                              }
                              className="block w-full"
                            />
                            <div className="px-2 pb-1 text-right text-[10px] text-gray-500">{msg.time}</div>
                          </motion.div>
                        );
                      }
                      if (msg.kind === "agenda") {
                        return (
                          <InfoCard
                            key={msg.id}
                            testId="lab-agenda-extract"
                            title="Lecture de la photo"
                            subtitle="Une photo à la fois"
                            rows={msg.rows}
                            time={msg.time}
                          />
                        );
                      }
                      if (msg.kind === "deadlines") {
                        return (
                          <InfoCard
                            key={msg.id}
                            testId="lab-deadlines"
                            title="Échéances"
                            subtitle="Devoirs et examens"
                            rows={msg.rows}
                            time={msg.time}
                          />
                        );
                      }
                      if (msg.kind === "studyplan") {
                        return (
                          <InfoCard
                            key={msg.id}
                            testId="lab-study-plan"
                            title={msg.title}
                            subtitle={msg.subtitle}
                            rows={msg.rows}
                            time={msg.time}
                          />
                        );
                      }
                      if (msg.kind === "goals") return <GoalsCard key={msg.id} time={msg.time} />;
                      if (msg.kind === "build") return <BuildCard key={msg.id} time={msg.time} />;
                      if (msg.kind === "planForm") {
                        return (
                          <PlanFormCard
                            key={msg.id}
                            time={msg.time}
                            mobileType={mobileType}
                            self={Boolean(msg.self)}
                            childName={msg.childName || ""}
                            initial={msg.initial || null}
                            onSubmit={submitPlanForm(msg)}
                            onCancel={() => (msg.self
                              ? sayStudent("e.formCancelled", {}, studentLot3Replies())
                              : addParent("p1.formCancelled", parentHomeReplies(), {}))}
                            onStep={msg.self ? (step) => weekDash(15, step) : flow10Dash}
                          />
                        );
                      }
                      if (msg.kind === "capture") {
                        return (
                          <CaptureCard
                            key={msg.id}
                            time={msg.time}
                            mobileType={mobileType}
                            title={msg.parentSide ? `Travaux de ${msg.childName || "votre enfant"} à préparer` : undefined}
                            placeholder="Ex. examen de maths vendredi, devoir de français jeudi…"
                            onSubmit={submitCapture(msg)}
                          />
                        );
                      }
                      if (msg.kind === "captureReview") {
                        return <CaptureReviewCard key={msg.id} draft={msg.draft} time={msg.time} mobileType={mobileType} onConfirm={confirmCaptureItems(msg)} onCancel={cancelCapture(msg)} />;
                      }
                      if (msg.kind === "schedule") {
                        return (
                          <ScheduleProposalCard key={msg.id} api={engagementApi} studentId={selfStudentId} remembered={msg.remembered || null} time={msg.time} mobileType={mobileType}
                            onProposal={onProposal} onConfirmed={onScheduleConfirmed} onEdit={manualWeekPlan}
                            onLater={(why) => { mvpDash(20, why === "skip" ? 0 : 1); sayStudent("s.later", {}, studentLot3Replies()); }} />
                        );
                      }
                      if (msg.kind === "moveSession") {
                        return (
                          <MoveSessionCard key={msg.id} api={engagementApi} studentId={selfStudentId} session={msg.session} time={msg.time} mobileType={mobileType}
                            onMoved={(pick) => sayStudent("x.moved", { when: `${fmtDay(pick.date)} à ${pick.time}` }, quickAsk)} />
                        );
                      }
                      if (msg.kind === "aiHelpAsk") {
                        return <CaptureCard key={msg.id} testId="ai-help-card" title={`Je bloque · ${msg.plan.title}`} button="Envoyer" placeholder="Ex. je ne comprends pas comment isoler x…" time={msg.time} mobileType={mobileType} onSubmit={submitAiHelp(msg)} />;
                      }
                      if (msg.kind === "infoList") {
                        return <InfoListCard key={msg.id} kind={msg.list} title={msg.title} subtitle={msg.subtitle} rows={msg.rows} time={msg.time} mobileType={mobileType} />;
                      }
                      if (msg.kind === "aiHelpTurn") {
                        return <AiHelpTurnCard key={msg.id} turn={msg.turn} triesLeft={msg.triesLeft} time={msg.time} mobileType={mobileType} onAction={onAiHelpAction} />;
                      }
                      if (msg.kind === "weekPlan") {
                        return (
                          <WeekPlannerCard
                            key={msg.id}
                            api={engagementApi}
                            studentId={selfStudentId}
                            time={msg.time}
                            mobileType={mobileType}
                            onDone={onWeekDone}
                          />
                        );
                      }
                      if (msg.kind === "sessionTimer") {
                        return (
                          <SessionTimerCard
                            key={msg.id}
                            timerKey={msg.timerKey}
                            title={msg.plan?.title || "Séance d’étude"}
                            plannedMinutes={msg.minutes}
                            doneMinutes={msg.doneMinutes || 0}
                            mobileType={mobileType}
                            onFinish={(minutes) => {
                              setMessages((current) => current.map((item) => (item.id === msg.id ? { ...item, doneMinutes: minutes } : item)));
                              pushStudentCard({ kind: "planCard", plan: msg.plan, timedMinutes: minutes }, 300);
                            }}
                          />
                        );
                      }
                      if (msg.kind === "planCard") {
                        return (
                          <PlanCard
                            key={msg.id}
                            timedMinutes={msg.timedMinutes || 0}
                            plan={msg.plan}
                            api={engagementApi}
                            viewer={role === "parent" ? "parent" : "student"}
                            viewerId={viewerId}
                            time={msg.time}
                            mobileType={mobileType}
                            onChange={(next, ...rest) => {
                              // A timed check-in is used once: the card keeps the new plan, not the stale timer.
                              if (msg.timedMinutes) setMessages((current) => current.map((item) => (item.id === msg.id ? { ...item, plan: next, timedMinutes: 0 } : item)));
                              return (role === "parent" ? onParentPlanChange : onStudentPlanChange)(next, ...rest);
                            }}
                            onEdit={editPlanFromCard}
                          />
                        );
                      }
                      if (msg.kind === "helpAsk") {
                        return (
                          <HelpAskCard
                            key={msg.id}
                            api={engagementApi}
                            plans={msg.plans}
                            mobileType={mobileType}
                            onSent={(item) => {
                              flowDash(11, 1, "eleve");
                              sayStudent("h.sent", {}, studentLot3Replies());
                              if (!appMode) pushParentCard({ kind: "helpCard", request: item }, 400);
                            }}
                          />
                        );
                      }
                      if (msg.kind === "helpCard") {
                        return (
                          <HelpCard
                            key={msg.id}
                            request={msg.request}
                            api={engagementApi}
                            viewer={role === "parent" ? "parent" : "student"}
                            mobileType={mobileType}
                            onChange={onHelpChange(role === "parent" ? "parent" : "student")}
                          />
                        );
                      }
                      if (msg.kind === "reportCard") {
                        return <WeeklyReportCard key={msg.id} report={msg.report} mobileType={mobileType} />;
                      }
                      if (msg.kind === "simNotice") {
                        return (
                          <div key={msg.id} className="max-w-[85%] self-start rounded-2xl rounded-tl-sm border border-dashed border-[#9aa7a0] bg-[#F3F5F4] p-3 shadow-sm" data-testid="sim-notice">
                            <span className="mb-1 inline-block rounded-full bg-[#122118] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white" data-testid="sim-notice-badge">
                              {appMode ? msg.channel : `${msg.channel} simulé`}
                            </span>
                            <p className={`text-[#3b4a42] ${mobileType ? "text-[15px]" : "text-[12px]"}`}>{msg.text}</p>
                            <p className="mt-1 text-right text-[10px] text-[#829C8D]">{msg.time}</p>
                          </div>
                        );
                      }
                      if (msg.kind === "localLink") {
                        return (
                          <div
                            key={msg.id}
                            className="max-w-[85%] self-start break-all rounded-2xl rounded-tl-sm bg-[#FFF8E1] p-3 text-xs text-[#6b5400] shadow-sm"
                            data-testid="local-link"
                          >
                            Mode local, aucun courriel envoyé. Lien :{" "}
                            <a className="underline" href={msg.href}>
                              {msg.href}
                            </a>
                          </div>
                        );
                      }
                      if (msg.kind === "planExample") {
                        return <PlanExampleCard key={msg.id} time={msg.time} mobileType={mobileType} />;
                      }
                      if (msg.kind === "inviteChild") {
                        return (
                          <InviteChildCard
                            key={msg.id}
                            time={msg.time}
                            mobileType={mobileType}
                            childName={msg.childName || ""}
                            childEmail={msg.childEmail || ""}
                            memberId={msg.memberId || ""}
                            accountToken={appMode ? accountToken : ""}
                            onInvited={(member) => {
                              if (!member.simulated) onStudentInvited?.(member);
                              parentChildRef.current = { ...member, status: "pending" };
                              addParent(msg.memberId ? "p1.emailFixed" : "p1.invited", [], { child: member.name, email: member.email });
                              later(() => (msg.next === "home" ? parentHome() : parentFirstPlan()), 1800);
                            }}
                          />
                        );
                      }
                      if (msg.kind === "tutorName") {
                        return (
                          <TutorNameField
                            key={msg.id}
                            time={msg.time}
                            active={
                              dash.nameDraft &&
                              dash.flowStep === 4 &&
                              !list.slice(index + 1).some((item) => item.kind === "tutorName")
                            }
                            onSubmit={acceptTutorName}
                          />
                        );
                      }
                      if (msg.kind === "availability") {
                        return (
                          <AvailabilityCard
                            key={msg.id}
                            time={msg.time}
                            selected={dash.availability || seedAvailability()}
                            note={scheduleNote}
                            onToggle={(slot) => handleQuickReply(slot)}
                          />
                        );
                      }
                      if (msg.kind === "weekplan") {
                        return <WeekPlanCard key={msg.id} time={msg.time} modified={msg.modified} />;
                      }
                      if (msg.kind === "readyplan") return <ReadyPlanCard key={msg.id} time={msg.time} />;
                      if (msg.kind === "parentSummary") {
                        return <ParentSummaryCard key={msg.id} time={msg.time} />;
                      }
                      if (msg.kind === "subjectBars") return <SubjectBarsCard key={msg.id} time={msg.time} />;
                      if (msg.kind === "taskList") return <TaskListCard key={msg.id} time={msg.time} />;
                      if (msg.kind === "nextGoals") return <NextGoalsCard key={msg.id} time={msg.time} />;
                      if (msg.kind === "tutorNote") {
                        return (
                          <InfoCard
                            key={msg.id}
                            testId="lab-tutor-note"
                            title="Pour le tuteur"
                            subtitle="Tâche, temps calculé, séance"
                            rows={msg.rows}
                            time={msg.time}
                          />
                        );
                      }
                      if (msg.kind === "objective") {
                        const goal = msg.objective || emptyObjective();
                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="self-start max-w-[92%] overflow-hidden rounded-2xl rounded-tl-sm border border-[#C6EBD7] bg-white shadow-sm"
                            data-testid="lab-objective-card"
                          >
                            <div className="flex items-center gap-2 bg-[#E9FBF0] px-3 py-2">
                              <CheckCircle2 className="w-4 h-4 text-[#189346]" />
                              <span className="text-sm font-semibold text-[#122118]">
                                Objectif ajouté !
                              </span>
                            </div>
                            <dl className="space-y-1.5 px-3 py-2.5 text-[12px] text-[#122118]">
                              {[
                                ["Matière", goal.subject || "—"],
                                ["Type", goal.type || "—"],
                                ["Échéance", goal.due || "—"],
                                ["Durée estimée", durationLabel(goal.duration)],
                              ].map(([label, value]) => (
                                <div key={label} className="flex gap-2">
                                  <dt className="w-[6.5rem] shrink-0 text-[#829C8D]">
                                    {label}
                                  </dt>
                                  <dd className="font-semibold">{value}</dd>
                                </div>
                              ))}
                              <div>
                                <dt className="text-[#829C8D]">Étapes</dt>
                                <dd className="mt-1 space-y-0.5 font-semibold">
                                  {goal.steps?.length ? (
                                    goal.steps.map((step) => (
                                      <div key={step}>– {step}</div>
                                    ))
                                  ) : (
                                    <div>Aucune étape pour l’instant</div>
                                  )}
                                </dd>
                              </div>
                            </dl>
                            <div className="px-3 pb-2 text-right text-[10px] text-gray-500">
                              {msg.time}
                            </div>
                          </motion.div>
                        );
                      }
                      if (msg.kind === "session") {
                        const record = msg.session || emptySession();
                        const rows = [
                          ["Activités", activitiesSummary(record.activities)],
                          ["Temps", record.duration || "—"],
                          ["Difficulté", record.difficulty || "—"],
                          ["Blocage", blockageShort(record.blockage)],
                        ];
                        return (
                          <motion.div
                            key={msg.id}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            className="self-start max-w-[92%] overflow-hidden rounded-2xl rounded-tl-sm border border-[#C6EBD7] bg-white shadow-sm"
                            data-testid="lab-session-card"
                          >
                            <div className="bg-[#E9FBF0] px-3 py-2">
                              <div className="text-sm font-semibold text-[#122118]">
                                Merci pour ton retour !
                              </div>
                              <div className="text-[11px] text-[#4a5f53]">Séance enregistrée</div>
                            </div>
                            <dl className="space-y-1.5 px-3 py-2.5 text-[12px] text-[#122118]">
                              {rows.map(([label, value]) => (
                                <div key={label} className="flex gap-2">
                                  <dt className="w-[5.5rem] shrink-0 text-[#829C8D]">{label}</dt>
                                  <dd className="font-semibold">{value}</dd>
                                </div>
                              ))}
                            </dl>
                            <div className="px-3 pb-2 text-right text-[10px] text-gray-500">
                              {msg.time}
                            </div>
                          </motion.div>
                        );
                      }
                      const mine = msg.sender === "user";
                      const editable = Boolean(msg.messageKey) && !appMode;
                      return (
                        <motion.div
                          key={msg.id}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          role={editable ? "button" : undefined}
                          tabIndex={editable ? 0 : undefined}
                          onClick={() => {
                            if (!editable) return;
                            setFollowLive(false);
                            setTargetKey(msg.messageKey);
                          }}
                          onKeyDown={(event) => {
                            if (!editable) return;
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setFollowLive(false);
                              setTargetKey(msg.messageKey);
                            }
                          }}
                          className={`max-w-[85%] px-3 py-2 rounded-2xl shadow-sm whitespace-pre-wrap break-words text-left [overflow-wrap:anywhere] ${
                            mobileType ? "text-[15px] leading-[1.38]" : "text-[13px] leading-snug"
                          } ${
                            mine
                              ? "self-end bg-[#DCF8C6] rounded-tr-sm"
                              : "self-start bg-white rounded-tl-sm"
                          } ${editable ? "cursor-pointer" : ""} ${
                            editable && msg.messageKey === targetKey ? "ring-2 ring-[#25D366]" : ""
                          }`}
                          data-testid={`lab-bubble-${msg.sender}`}
                        >
                          {msg.text}
                          <div className={`text-right mt-1 flex items-center justify-end gap-1 ${mobileType ? "text-[11px]" : "text-[10px]"} text-gray-500`}>
                            {msg.revised && !appMode && (
                              <span className="font-semibold text-[#189346]">Intégré</span>
                            )}
                            <span>
                              {msg.time} {mine ? "✓✓" : ""}
                            </span>
                          </div>
                        </motion.div>
                      );
                    })}
                  </AnimatePresence>

                  {typing && (
                    <div data-chat-extra="" className="self-start bg-white rounded-2xl rounded-tl-sm px-3 py-2 shadow-sm">
                      <span className="inline-flex gap-1">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" />
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
                          style={{ animationDelay: "120ms" }}
                        />
                        <span
                          className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce"
                          style={{ animationDelay: "240ms" }}
                        />
                      </span>
                    </div>
                  )}

                  {(role === "parent" ? parentReplies : quickReplies).length > 0 && !typing && (
                    <div
                      data-chat-extra=""
                      className="flex flex-col gap-2 mt-2"
                      data-testid="lab-quick-replies"
                    >
                      {(role === "parent"
                        ? parentReplies
                        : onFlow4 && dash.flowStep === 1
                        ? dayReplies(dash.week?.days || [])
                        : onFlow6 && dash.flowStep === 1
                          ? activityReplies(dash.session?.activities || [])
                          : quickReplies
                      ).map((btn) => (
                        <button
                          key={btn.id}
                          type="button"
                          data-testid={`lab-qr-${btn.id}`}
                          onClick={() => handleQuickReply(btn.id)}
                          className={`w-full max-w-full px-3 py-2.5 font-semibold border transition text-left leading-snug break-words [overflow-wrap:anywhere] ${
                            mobileType ? "text-[15px]" : "text-sm"
                          } ${
                            btn.label.length > 42 ? "rounded-2xl" : "rounded-full"
                          } ${
                            btn.selected
                              ? "bg-[#E9FBF0] text-[#075E54] border-[#25D366]"
                              : btn.primary
                                ? "bg-[#25D366] text-[#0A2914] border-[#25D366]"
                                : "bg-white text-[#075E54] border-[#25D366]/50"
                          }`}
                        >
                          {btn.selected ? "✓ " : ""}
                          {btn.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                {appMode && (
                  <form
                    className={`flex w-full min-w-0 max-w-full shrink-0 items-center gap-2 overflow-hidden bg-[#F0F2F5] px-2 py-2 ${nativeScreen ? "pb-[max(0.5rem,env(safe-area-inset-bottom))]" : ""}`}
                    onSubmit={(event) => {
                      event.preventDefault();
                      sendFreeText();
                    }}
                  >
                    <button
                      type="button"
                      aria-label="Ajouter un fichier"
                      data-testid="app-attach"
                      onClick={() => {
                        objectiveCaptureRef.current = dash.flowId === 3 && dash.flowStep === 0;
                        photoRef.current?.click();
                      }}
                      className={`inline-flex shrink-0 items-center justify-center rounded-full text-[#54656F] ${mobileType ? "h-11 w-11" : "h-10 w-10"}`}
                    >
                      <Plus className={mobileType ? "h-7 w-7" : "h-6 w-6"} strokeWidth={2.25} />
                    </button>
                    <input
                      value={input}
                      onChange={(event) => setInput(event.target.value)}
                      onFocus={() => {
                        const pin = () => {
                          scrollToAnchor();
                          document.documentElement.scrollLeft = 0;
                          document.body.scrollLeft = 0;
                        };
                        pin();
                        requestAnimationFrame(pin);
                        window.setTimeout(pin, 300);
                      }}
                      placeholder={
                        dash.flowId === 2 && dash.flowStep === 4 && dash.nameDraft
                          ? "Écris un prénom"
                          : "Écrivez un message…"
                      }
                      data-testid="app-composer"
                      size={1}
                      className="w-0 min-w-0 max-w-full flex-1 rounded-full border border-gray-200 bg-white px-4 py-2.5 text-[#122118] outline-none"
                      style={{ fontSize: 16 }}
                    />
                    <button
                      type="submit"
                      disabled={!input.trim() || typing}
                      data-testid="app-send"
                      aria-label="Envoyer"
                      className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full disabled:opacity-50"
                      style={{ background: "var(--sp-primary)", color: "var(--sp-text-on-primary)" }}
                    >
                      <Send className="w-4 h-4" />
                    </button>
                  </form>
                )}
                </div>
              </div>
            </div>

            {!appMode && (
            <div className="space-y-3">
              {/* Message dans l'application */}
              <div className="rounded-2xl border border-gray-100 bg-white p-4" data-testid="lab-wa-panel">
                <label
                  htmlFor="lab-wa-input"
                  className="text-xs font-bold uppercase tracking-wider text-[#189346]"
                >
                  Message dans l’application
                </label>
                <div className="mt-2 flex gap-2">
                  <input
                    id="lab-wa-input"
                    data-testid="lab-wa-input"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        sendFreeText();
                      }
                    }}
                    placeholder={
                      appMode
                        ? "Écrivez un message…"
                        : role === "parent"
                        ? "Ou écris /menu, /onboarding, /bilan…"
                        : onFlow9
                        ? "Ex. : Voici mon agenda de jeudi…"
                        : onFlow8
                        ? "Ou ajoute une photo, ou écris 0,21…"
                        : onFlow4 && dash.flowStep === 4
                        ? "Ex. : Avance le samedi à 9 h…"
                        : onFlow4
                          ? "Ou utilise les boutons du chat Flow 4…"
                          : onFlow7
                            ? "Ou utilise les boutons du chat Flow 7…"
                            : onFlow5 && dash.flowStep === 4
                              ? "Ex. : J'ai du mal avec la question 3…"
                              : onFlow5
                                ? "Ou utilise les boutons du chat Flow 5…"
                                : onFlow6 && dash.flowStep === 4
                                  ? "Ex. : Un peu difficile sur les fractions…"
                                  : onFlow6
                                    ? "Ou utilise les boutons du chat Flow 6…"
                                    : onFlow3 && dash.flowStep === 0
                                      ? "Ex. : J’ai un contrôle de maths…"
                                      : onFlow3 && dash.flowStep === 3
                                        ? "Ex. : Lundi 20 octobre"
                                        : onFlow3 && dash.flowStep === 5
                                          ? "Décris les étapes, ou utilise les boutons…"
                                          : onFlow3
                                            ? "Ou utilise les boutons du chat Flow 3…"
                                            : dash.flowStep === 91
                                              ? "Pose ta question…"
                                              : dash.flowStep === 4 && dash.nameDraft
                                                ? "Écris un prénom"
                                                : dash.flowStep >= 5
                                                  ? "Photo / fichier / « j’ai terminé 25 min »…"
                                                  : `Ou utilise les boutons du chat Flow ${dash.flowId}…`
                    }
                    className="flex-1 rounded-full border border-gray-200 bg-[#FBFCFC] px-4 py-2.5 text-sm outline-none focus:border-[#25D366] focus:ring-2 focus:ring-[#25D366]/20"
                  />
                  <button
                    type="button"
                    data-testid="lab-wa-send"
                    onClick={sendFreeText}
                    disabled={!input.trim() || typing}
                    className="inline-flex items-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold disabled:opacity-50"
                    style={{
                      background: "var(--sp-primary)",
                      color: "var(--sp-text-on-primary)",
                    }}
                  >
                    <Send className="w-4 h-4" />
                    Envoyer
                  </button>
                </div>
                {!appMode && (
                <p className="mt-1.5 text-[11px] text-[#829C8D]">
                  {role === "parent"
                    ? "Vue parent · /menu liste les flows numérotés. L’arbre les modifie."
                    : "/menu ou /fonctions rouvre la liste des commandes."}
                </p>
                )}
                {!appMode && (
                <p className="text-[11px] text-[#829C8D]">
                  {onFlow10
                    ? "Flow 10 · proposer une activité · les messages modifiés restent dans ce navigateur."
                    : onFlow1
                    ? "Flow 1 · accueil parent · les messages modifiés restent dans ce navigateur."
                    : onFlow9
                    ? "Flow 9 · agenda et remises · une photo à la fois."
                    : onFlow8
                    ? "Flow 8 · aide photo · les messages modifiés restent dans ce navigateur."
                    : onFlow7
                    ? "Flow 7 · bilan pour le parent · les messages modifiés restent dans ce navigateur."
                    : onFlow6
                      ? "Flow 6 · suivi après la séance · les messages modifiés restent dans ce navigateur."
                      : onFlow5
                        ? "Flow 5 · accompagnement à la tâche · les messages modifiés restent dans ce navigateur."
                        : onFlow4
                          ? "Flow 4 · plan de la semaine · les messages modifiés restent dans ce navigateur."
                          : onFlow3
                            ? "Flow 3 · collecte d’un objectif · les messages modifiés restent dans ce navigateur."
                            : `Flow ${dash.flowId} guidé par boutons · les messages modifiés restent dans ce navigateur.`}
                </p>
                )}
              </div>

              <div className="rounded-2xl border border-[#C6EBD7] bg-[#F3FCF7] p-4">
                <div className="flex items-center gap-2">
                  <Brain className="w-5 h-5 text-[#189346]" />
                  <span className="font-semibold">
                    {onFlow14
                      ? "Flow 14 · Accueil élève"
                      : dash.flowId === 15
                      ? "Flow 15 · Mon plan d’étude (élève)"
                      : dash.flowId === 16
                      ? "Flow 16 · Planifier ma semaine"
                      : dash.flowId === 17
                      ? "Flow 17 · Rappels et séance du jour"
                      : MVP_FLOWS[dash.flowId]
                      ? MVP_FLOWS[dash.flowId].title.replace(" — ", " · ")
                      : onFlow13
                      ? "Flow 13 · Accueil parent"
                      : onFlow12
                      ? "Flow 12 · Bilan de la semaine"
                      : onFlow11
                      ? "Flow 11 · Aide et escalade"
                      : onFlow10
                      ? "Flow 10 · Proposer une activité"
                      : onFlow1
                      ? "Flow 1 · Accueil parent"
                      : onFlow9
                      ? "Flow 9 · Agenda et remises"
                      : onFlow8
                      ? "Flow 8 · Aide photo"
                      : onFlow7
                      ? "Flow 7 · Bilan parent"
                      : onFlow6
                        ? "Flow 6 · Suivi des activités"
                        : onFlow5
                          ? "Flow 5 · Rappels et séances"
                          : onFlow4
                            ? "Flow 4 · Planification hebdomadaire"
                            : onFlow3
                              ? "Flow 3 · Collecte des objectifs"
                              : "Flow 2 · Accueil"}
                  </span>
                  <span className="text-[10px] font-bold uppercase px-2 py-0.5 rounded-full bg-[#25D366] text-[#0A2914]">
                    Actif
                  </span>
                </div>
                <p className="mt-1 mb-2 text-xs text-[#4a5f53]" data-testid="lab-flow-step-line">
                  Étape {activeIndex + 1}/{activeSteps.length} · {activeSteps[activeIndex]}
                </p>
                <ol className="space-y-1.5 text-sm">
                  {activeSteps.map((label, i) => {
                    const done = i < activeIndex || (flowComplete && i === activeIndex);
                    const current = i === activeIndex && !flowComplete;
                    return (
                      <li
                        key={label}
                        className={`flex items-center gap-2 ${
                          current
                            ? "text-[#0B8F6A] font-semibold"
                            : done
                              ? "text-[#122118]"
                              : "text-[#829C8D]"
                        }`}
                      >
                        <span
                          className={`w-5 h-5 rounded-full text-[10px] flex items-center justify-center border ${
                            done || current
                              ? "bg-[#E9FBF0] border-[#25D366] text-[#189346]"
                              : "border-gray-200"
                          }`}
                        >
                          {done && !current ? "✓" : i + 1}
                        </span>
                        {label}
                      </li>
                    );
                  })}
                </ol>
              </div>

              <div className="rounded-2xl border border-gray-100 bg-white p-4">
                <div className="flex items-center gap-2 font-semibold text-[#122118] mb-3">
                  <Calendar className="w-4 h-4 text-[#189346]" />
                  Plan d’étude — {role === "parent" ? parentName : studentName}
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                  <div>
                    <div className="text-[10px] uppercase text-[#829C8D]">
                      Parent
                    </div>
                    <div className="font-semibold">{parentName}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[#829C8D]">
                      Statut
                    </div>
                    <div className="font-semibold text-[#189346]">
                      {dash.status}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-[#829C8D]">
                      Plan
                    </div>
                    <div className="font-semibold">
                      {dash.planReady ? "Prêt (simulé)" : "En attente"}
                    </div>
                  </div>
                </div>
              </div>

              {!appMode && (
                <SimClockPanel now={clockNow} smsToday={simSmsToday()} onAdvance={advanceClock} onReset={resetClock} />
              )}
              {!appMode && <PublishPanel revisions={revisions} status={labStatus} onStatus={setLabStatus} />}
              <div className="rounded-2xl border border-gray-100 bg-white p-4">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#189346]">
                  Tableau de bord parent
                </div>
                <div className="font-display font-bold text-lg mb-3">
                  Suivi (simulation)
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {[
                    {
                      label: "Séances",
                      value: `${liveStats.done}/${liveStats.target}`,
                      icon: CheckCircle2,
                    },
                    {
                      label: "Minutes",
                      value: `${liveStats.minutes} min`,
                      icon: Clock,
                    },
                    {
                      label: "Régularité",
                      value: `${liveStats.regularity}%`,
                      icon: TrendingUp,
                    },
                    {
                      label: "Activité",
                      value: liveStats.title,
                      icon: FileText,
                    },
                  ].map(({ label, value, icon: Icon }) => (
                    <div
                      key={label}
                      className="rounded-xl border border-gray-100 bg-[#F8FAF9] p-3"
                    >
                      <Icon className="w-4 h-4 text-[#189346] mb-1" />
                      <div className="text-[10px] uppercase text-[#829C8D]">
                        {label}
                      </div>
                      <div className="font-bold text-sm">{value}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-4">
                  <div className="flex justify-between text-xs text-[#4a5f53] mb-1">
                    <span>Progression {onFlow14 ? "Flow 14" : onFlow13 ? "Flow 13" : onFlow12 ? "Flow 12" : onFlow11 ? "Flow 11" : onFlow10 ? "Flow 10" : onFlow1 ? "Flow 1" : onFlow9 ? "Flow 9" : onFlow8 ? "Flow 8" : onFlow7 ? "Flow 7" : onFlow6 ? "Flow 6" : onFlow5 ? "Flow 5" : onFlow4 ? "Flow 4" : onFlow3 ? "Flow 3" : "Flow 2"}</span>
                    <span>{progress}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-gray-100 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-[#25D366] transition-all duration-500"
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                </div>
              </div>

              <LabCopyStudio
                targetKey={targetKey}
                followLive={followLive}
                resolvedText={targetResolved}
                originalText={targetOriginal}
                revised={targetRevised}
                resolvedPrompt={targetPromptResolved}
                originalPrompt={targetPromptOriginal}
                promptRevised={targetPromptRevised}
                integrations={revisions.integrations}
                savedComment={revisions.comments?.[targetKey] || ""}
                onCommentChange={(text) => saveComment(targetKey, text)}
                onSelectTarget={(key) => {
                  setFollowLive(false);
                  setTargetKey(key);
                }}
                onFollowLive={setFollowLive}
                onApply={applyMessageComment}
                onSave={saveMessageText}
                onSavePrompt={savePromptText}
                onRevert={revertKey}
                onRevertPrompt={revertPrompt}
                onClearAll={clearRevisions}
                onIntegrated={() => {
                  const next = nextFlowMessageKey(targetKey);
                  if (!next) return;
                  setFollowLive(false);
                  setTargetKey(next);
                }}
              />

              <div className="rounded-2xl border border-dashed border-gray-200 bg-white/70 p-3 max-h-32 overflow-y-auto">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#829C8D] mb-2">
                  Journal
                </div>
                <ul className="space-y-1.5">
                  {log.map((item) => (
                    <li key={item.id} className="text-xs text-[#4a5f53]">
                      • {item.text}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicTestLab;
